import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, Output, EventEmitter, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { DatabaseService } from '../../../services/database.service';
import { environment } from '../../../services/global-environment';
import { I18nService } from '../../../services/i18n.service';
import * as QRCode from 'qrcode';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { TimelineViewComponent } from '../timeline-view/timeline-view.component';
import { DynamicFormComponent, FormFieldConfig } from '../../dynamic-form/dynamic-form/dynamic-form.component';
import { FormConfigService } from '../../../services/form-config.service';
import { ApprovalFlowDisplayComponent, ApprovalStep } from '../../shared/approval-flow-display/approval-flow-display.component';
import { FileUploadService } from '../../../services/file-upload.service';
import { AuthService } from '../../../services/auth.service';
import { FormDisplayComponent } from '../../form-display/form-display.component';
import { EmailService } from '../../../services/email.service';
import { EmailConfirmationModalComponent, EmailPreviewData, EmailConfirmationResult } from '../../email-confirmation-modal/email-confirmation-modal.component';

// 時間軸數據介面定義
interface TimelineGroup {
  groupField: string;
  groupValue: string;
  periods: TimelinePeriod[];
}

interface TimelinePeriod {
  periodKey: string;  // 例如 "2023-01", "2023-W01", "2023-Q1" 等
  periodStart: Date;
  periodEnd: Date;
  count: number;
}

export interface Photo {
  fileName: string;
  fileUrl: string;
}

// 搜尋條件介面
interface SearchCondition {
  fieldName: string;
  fieldType: string;
  operator: string;
  value: any;
  startValue?: any; // 用於日期範圍的起始值
  endValue?: any;   // 用於日期範圍的結束值
}

// 自定義操作按鈕介面
export interface CustomActionButton {
  label: string;                    // 按鈕文字
  icon?: string;                    // FontAwesome icon class (例如: 'fas fa-envelope')
  class?: string;                   // 額外的 CSS class
  title?: string;                   // tooltip 文字
  show?: boolean;                   // 是否顯示按鈕（預設 true）
  disabled?: boolean;               // 是否禁用（預設 false）
  onClick: (item: any) => void;     // 點擊事件處理函數
}

// 搜尋操作符
interface SearchOperator {
  value: string;
  label: string;
}

// 欄位選項
interface FieldOption {
  value: string;
  label: string;
}

// 🆕 待辦事項相關 interface
/**
 * 待辦事項資訊
 */
export interface TodoInfo {
  count: number;           // 待辦事項總數
  todos: TodoItem[];       // 待辦事項清單
  hasOutstanding: boolean; // 是否有未完成項目
}

/**
 * 單一待辦事項
 */
export interface TodoItem {
  type: 'approval-flow' | 'permit-form';  // 待辦事項類型
  description: string;                     // 待辦事項說明
  icon: string;                            // 顯示圖示
  formId?: string;                         // 表單 ID（如果是表單類型）
}

@Component({
  selector: 'app-showlist',
  standalone: true,
  imports: [CommonModule, FormsModule, DragDropModule, TranslatePipe, DynamicFormComponent, ApprovalFlowDisplayComponent, FormDisplayComponent, EmailConfirmationModalComponent],
  templateUrl: './showlist.component.html',
  styleUrls: ['./showlist.component.scss']
})
export class ShowlistComponent implements OnInit, OnDestroy, OnChanges {


  //TODO:未來可以拿掉的input參數
    @Input() onlyShowDataCreatedBy: string = ''; // 新增：只顯示特定使用者建立的資料
  @Input() userRoles: string[] = []; // 新增：使用者角色陣列，用於權限檢查
  @Input() hideAddButton: boolean = false; // 新增：是否隱藏新增按鈕
  @Input() hideDeleteButton: boolean = false; // 新增：是否隱藏刪除按鈕
  //////////////////////////////////////////////////////


  @Input() formName: string = '';
  @Input() formId: string = '';
  @Input() parentFormId: string = ''; // 新增：父表單ID，用於子表單的新增記錄

  // 🆕 權限資料 Input（可選，如果提供則根據 permissions 陣列判斷權限）
  @Input() componentPermissions?: string[];  // 權限列表，例如：['view', 'edit', 'delete']
  // 🆕 資料範圍 Input（可選，如果提供則根據 dataScope 控制資料顯示範圍）
  @Input() dataScope?: 'all' | 'own' | 'department-self' | 'department-below';  // 資料範圍：all=所有資料, own=僅自己的資料, department-self=僅本部門, department-below=本部門及下屬部門

  // 🆕 通用自訂過濾條件
  @Input() customFilters?: Array<{
    field: string;  // 欄位名稱，例如 'parent_formid'
    operator: 'equals' | 'notEquals' | 'contains' | 'notContains' | 'isEmpty' | 'isNotEmpty' | 'greaterThan' | 'lessThan' | 'in' | 'notIn';
    value?: any;  // 條件值（isEmpty/isNotEmpty 不需要）
  }>
  @Input() programmaticColumns?: {
    columns: string[];            // 要顯示的欄位，陣列順序就是顯示順序
    hideColumnSettings?: boolean; // 是否隱藏自定義欄位功能
    restrictSearch?: boolean;     // 是否限制搜尋範圍
  } = undefined;

  // 新增：控制編輯按鈕disabled狀態的回調函數
  @Input() customEditDisabledCheck?: (item: any) => boolean;

  // 新增：條件式子表單樣式回調函數
  @Input() getSubformCssClass?: (item: any, column: string) => string;

  // 新增：主表單日期範圍限制（用於子表單的日期欄位限制）
  @Input() parentDateRange?: {
    startDate: string;  // 主表單作業開始日期
    endDate: string;    // 主表單作業結束日期
  };

  @Output() dataUpdated = new EventEmitter<void>(); // 資料更新事件發射器
  @Output() newDataCreated = new EventEmitter<any>(); // 新增：新資料建立事件發射器
  @Output() itemAbolished = new EventEmitter<any>(); // 新增：項目廢止事件發射器

  // 新增：施工通知配置相關 @Input 和 @Output
  @Input() enableConstructionNotification: boolean = false; // 是否啟用施工通知功能
  @Input() enableEmailConfirmation: boolean = true; // 是否啟用寄信確認彈窗（預設啟用）

  // 新增：結案按鈕控制
  @Input() enableCloseButton: boolean = false; // 是否啟用結案按鈕

  // 新增：自定義操作按鈕配置
  @Input() customActionButtons?: (item: any) => CustomActionButton[];

  // 新增：施工通知相關事件
  @Output() constructionNotificationSent = new EventEmitter<any>();
  @Output() beforeConstructionNotification = new EventEmitter<any>();

  // 新增：結案按鈕點擊事件
  @Output() closeButtonClicked = new EventEmitter<any>();

  // 新增：通用簽核完成事件（取代業務相關的施工通知邏輯）
  @Output() approvalCompleted = new EventEmitter<any>();

  // 🆕 待辦事項提示功能相關屬性
  @Input() showTodoIndicator: boolean = false;  // 是否顯示待辦事項徽章
  @Input() getTodoInfoCallback?: (item: any) => TodoInfo;  // 取得待辦事項資訊的回調函數

  showTodoListModal: boolean = false;  // 是否顯示待辦清單彈窗
  currentTodoInfo?: TodoInfo;          // 目前選中的待辦事項資訊

  // 🆕 表單資訊彈窗
  showFormInfoModal: boolean = false;  // 是否顯示表單資訊彈窗

  // 🆕 外部篩選條件（由父元件傳入）
  @Input() externalFilters?: any;  // 外部篩選條件物件

  // 🆕 自訂狀態文字對應表（由父元件傳入，用於客製化狀態顯示文字）
  @Input() customStatusLabels?: {
    [key: string]: string;  // 狀態鍵 -> 顯示文字的對應表
  };

  // 🆕 自訂狀態取得函數（由父元件傳入，用於元件特定的狀態計算邏輯）
  @Input() getCustomItemStatus?: (item: any) => string | null;  // 如果返回 null，則使用預設邏輯

  // ViewChildren：取得所有 approval-flow-display 元件實例
  @ViewChildren(ApprovalFlowDisplayComponent) approvalFlowComponents!: QueryList<ApprovalFlowDisplayComponent>;





  // 寄信確認相關屬性
  showEmailConfirmationModal: boolean = false;
  emailPreviewData: EmailPreviewData[] = [];
  pendingConstructionInfo: any = null;

  displayedColumns: string[] = [];
  data: any[] = [];
  companyid = '';
  currentUserId = ''; // 當前使用者ID，用於權限檢查
  assistantNo = ''; // Assistant ID, obtained from the route parameter
  showAdminPermissionBy = ''; // Administrator Permissions Display Control, Save User ID

  // 欄位設定相關變數
  showColumnSettings: boolean = false;
  availableColumns: string[] = [];
  defaultColumns: string[] = [];
  columnSettings: {
    visibleColumns: string[];
    columnOrder: string[];
  } = {
    visibleColumns: [],
    columnOrder: []
  };

  // 編輯功能相關變數
  showEditDialog: boolean = false;
  editingItem: any = null;

  // 刪除功能相關變數
  showDeleteConfirm: boolean = false;
  deletingItem: any = null;

  // 廢止功能相關變數
  showDisableConfirm: boolean = false;
  disablingItem: any = null;
  disableComment: string = ''; // 🆕 無効理由（commentフィールドに保存）

  // 取り下げ機能相關變數
  showWithdrawConfirm: boolean = false;
  withdrawingItem: any = null;

  // Abolish 廢止功能相關變數
  showAbolishConfirm: boolean = false;
  abolishingItem: any = null;
  abolishReason: string = ''; // 廢止原因

  // 新增記錄對話框相關變數
  showAddRecordDialog: boolean = false;

  // 編輯表單配置相關變數
  showEditFormDialog: boolean = false;
  editingFormConfig: any = null;
  editFormBuilderMode: 'ai' | 'manual' = 'ai';
  editFormAiMessage: string = '';
  editFormAiMessages: {role: 'user' | 'assistant', content: string}[] = [];
  isEditFormSending: boolean = false;
  editResponseId: string = '';

  // 載入狀態
  isLoading: boolean = false;
  // 追蹤是否曾經載入過資料（用來區分初始載入和真正無資料）
  hasLoadedOnce: boolean = false;
  // 重新整理狀態
  isRefreshing: boolean = false;

  // 分頁相關屬性
  currentPage: number = 1;           // 當前頁碼
  pageSize: number = 10;             // 每頁資料筆數
  totalItems: number = 0;            // 總資料筆數
  totalPages: number = 0;            // 總頁數
  pageSizeOptions: number[] = [10, 30, 50, 100]; // 分頁大小選項

  // 排序相關屬性
  sortBy: 'createdAt' | '_ts' = '_ts';  // 預設使用異動時間排序
  sortOrder: 'asc' | 'desc' = 'desc';   // 預設降序（最新在前）
  
  // 搜尋字串
  searchQuery: string = '';
  // 多選搜尋欄位，預設空陣列（代表沒特定選擇，就搜尋全部欄位）
  searchColumns: string[] = [];

  // 動態搜尋功能相關屬性
  showSearchPanel: boolean = false;
  searchConditions: SearchCondition[] = [];
  searchableFields: FormFieldConfig[] = [];
  dynamicFilteredData: any[] = [];
  originalData: any[] = [];

  // 下拉選項資料
  availableUsers: { id: string; name: string }[] = [];
  availableDepartments: { id: string; name: string }[] = [];

  // 視圖模式
  viewMode: 'table' | 'timeline' = 'table';

  // Dynamic Form 相關屬性
  originalFormConfig: FormFieldConfig[] = [];
  editFormConfig: FormFieldConfig[] = [];
  editingItemData: any = {};

  // Files Modal 相關變數
  showFilesModal: boolean = false;
  modalFiles: any[] = [];
  modalItem: any = null;
  modalColumn: string = '';

  // Radiobutton Detail Modal 相關變數
  showRadiobuttonDetailModal: boolean = false;
  radiobuttonDetailData: any = null;

  // 清理計時器
  private cacheCleanupInterval: any;

  // 子表單顯示相關屬性
  showSubform: boolean = false;
  subformId: string = '';
  subformName: string = '';
  subformDisplayTitle: string = ''; // 子表單顯示標題（使用欄位的 label）
  activeSubformData: any = null; // 儲存觸發子表單顯示的資料項目
  currentParentFormId: string = ''; // 新增：當前父表單ID，用於新增子記錄時使用

  // Dynamic API Table 相關屬性
  showDynamicApiTableDialog: boolean = false;
  dynamicApiTableDialogTitle: string = '';
  dynamicApiTableData: any[] = [];
  dynamicApiTableColumns: string[] = [];

  // Meeting Signature Collection 相關屬性
  showMeetingSignatureDialog: boolean = false;
  meetingSignatureDialogTitle: string = '';
  meetingSignatureData: any = {
    participants: [],
    signedCount: 0,
    totalCount: 0
  };

  // 表單查看相關屬性
  showViewForm: boolean = false;
  viewFormConfig: FormFieldConfig[] = [];
  viewFormData: any = {};
  viewFormName: string = '';

  // 用 ViewChild 取得 form-display 元件的參考
  @ViewChild(FormDisplayComponent) viewFormDisplayComponent!: FormDisplayComponent;

  // 參考資料對話框相關屬性
  showReferenceDataDialog: boolean = false;
  referenceDataDialogTitle: string = '';
  referenceDataContent: any = null;

  constructor(
    private databaseService: DatabaseService,
    private http: HttpClient,
    private i18nService: I18nService,
    private formConfigService: FormConfigService,
    private fileUploadService: FileUploadService,
    private authService: AuthService,
    private emailService: EmailService,
    private router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // 監聽 externalFilters 的變化
    if (changes['externalFilters'] && !changes['externalFilters'].firstChange) {
      console.log('🔄 [showlist] externalFilters 已變更:', this.externalFilters);
      // 套用外部篩選條件
      this.applyExternalFilters();
    }
  }

  ngOnInit(): void {
    console.log('ShowlistComponent ngOnInit:', {
      formName: this.formName,
      formId: this.formId,
      componentPermissions: this.componentPermissions,
      dataScope: this.dataScope,
      parentFormId: this.parentFormId,
      isSubform: !!this.parentFormId
    });

    this.companyid = environment.companyid;
    const userInfo = this.authService.getUserInfo(); // Get current user info
    this.currentUserId = userInfo ? userInfo.userid : ''; // Set current user ID
    this.assistantNo = this.router.routerState.snapshot.root.queryParams['id'] || '';

    console.log('🔍 [showlist] companyid:', this.companyid, 'formId:', this.formId);

    if (!this.companyid) {
      console.warn('⚠️ [showlist] companyid 為空，提前返回');
      return;
    }

    if (!this.formId) {
      console.warn('⚠️ [showlist] formId 為空，提前返回');
      return;
    }

    // Obtain the user's role permissions within the assistant
    const userRoles = this.authService.getUserRolesForModule(this.assistantNo);
    // console.log('User Role Permissions:', userRoles);

    // 🆕 資料範圍控制邏輯（優先級：dataScope > onlyShowDataCreatedBy > 角色預設）
    // Priority 1: 如果外部傳入 dataScope，根據 dataScope 設定資料範圍
    if (this.dataScope) {
      console.log('使用外部傳入的 dataScope:', this.dataScope);
      switch (this.dataScope) {
        case 'all':
          // 顯示所有資料，不設定任何過濾
          this.onlyShowDataCreatedBy = '';
          this.showAdminPermissionBy = '';
          break;
        case 'own':
          // 只顯示自己建立的資料
          this.onlyShowDataCreatedBy = userInfo.userid;
          this.showAdminPermissionBy = '';
          break;
        case 'department-self':
        case 'department-below':
          // 顯示部門資料（目前後端使用 showAdminPermissionBy 處理）
          // TODO: 未來可能需要區分 department-self 和 department-below
          // 現在執行的邏輯都是 department-below
          this.onlyShowDataCreatedBy = '';
          this.showAdminPermissionBy = userInfo.userid;
          break;
      }
    }
    // Priority 2: 如果沒有 dataScope 但有 onlyShowDataCreatedBy，使用已設定的值
    else if (!this.onlyShowDataCreatedBy) {
      // Priority 3: 如果都沒有，根據角色設定預設資料範圍
      if (userRoles.includes('super_admin')) {
        // super_admin Permissions: No restrictions set; can view all data
      } else if (userRoles.includes('admin')) {
        this.showAdminPermissionBy = userInfo.userid;
      } else if (userRoles.includes('user')) {
        this.onlyShowDataCreatedBy = userInfo.userid;
      }
    }

    // 定期清理過期緩存（每5分鐘）
    this.cacheCleanupInterval = setInterval(() => {
      this.cleanExpiredCache();
    }, this.CACHE_DURATION);

    // 使用新的 API 取得表單容器資料
    const apiUrl = `${environment.apiUrl}/api/form-manager/get_form_container`;
    const params = new URLSearchParams({
      componentname: this.formName,
      formid: this.formId
    });

    // 如果有 parentFormId，表示這是子表單，需要過濾資料
    if (this.parentFormId) {
      params.append('parent_formid', this.parentFormId);
      // console.log(this.i18nService.translate('showlist.console.load_subform_data_parent_record_id'), this.parentFormId);
    }

    // 🆕 處理通用自訂過濾條件
    if (this.customFilters && this.customFilters.length > 0) {
      params.append('customFilters', JSON.stringify(this.customFilters));
    }

    // Permission Control Parameter Settings (Mutual Exclusion)
    if (this.onlyShowDataCreatedBy) {
      params.append('onlyShowDataCreatedBy', this.onlyShowDataCreatedBy);
      // console.log(this.i18nService.translate('showlist.console.only_show_data_created_by_specific_user'), this.onlyShowDataCreatedBy);
    } else if (this.showAdminPermissionBy) {
      params.append('showAdminPermissionBy', this.showAdminPermissionBy);
      // console.log('管理員權限標識:', this.showAdminPermissionBy);
    }

    // console.log("params",params);

    // 開始載入資料
    this.isLoading = true;

    this.http.get(`${apiUrl}?${params.toString()}`)
      .subscribe({
        next: (response: any) => {
          if (response.success) {

            this.data = response.data;

            // console.log(this.i18nService.translate('showlist.console.data_load_complete'), this.data);


            this.originalData = [...response.data]; // 儲存原始資料

            // 初始化過濾資料時，無効化されたレコードをスーパーユーザーのみ表示
            let initialFilteredData = [...response.data];
            const userRoles = this.authService.getUserRolesForModule(this.assistantNo);
            const isSuperAdmin = userRoles.includes('super_admin');

            if (!isSuperAdmin) {
              // スーパーユーザーでない場合、deleted: true のレコードを除外
              initialFilteredData = initialFilteredData.filter(item => !item.deleted);
            }

            this.dynamicFilteredData = initialFilteredData;

            // 取得所有欄位（排除系統欄位）
            this.availableColumns = this.getColumns();
            this.defaultColumns = [...this.availableColumns];

            // 載入欄位設定
            this.loadColumnSettings();

            // 確保新欄位被加入到設定中
            this.syncNewColumnsToSettings();

            // 移除重複的 loadData() 呼叫，避免重複請求 API
            // this.loadData();

            // 載入原始表單配置
            this.loadOriginalFormConfig();

            // 預載入子表單資料筆數
            this.preloadSubformRecordCounts();

            // 初始化搜尋功能
            this.initializeSearch();

            // 確保所有處理完成後才停止載入狀態
            setTimeout(() => {
              this.isLoading = false;
              this.hasLoadedOnce = true;
            }, 0);
          } else {
            console.error('API response failed:', response);
            this.isLoading = false;
            this.hasLoadedOnce = true;
          }
        },
        error: (error) => {
          console.error('Error fetching data:', error);
          this.isLoading = false; // 載入失敗時也要停止載入狀態
          this.hasLoadedOnce = true; // 標記已嘗試載入
        },
        complete: () => {
          // console.log('Data fetching complete');
          // 載入狀態在 next 回調中處理，這裡不需要重複設定
        }
      });
  }

  loadData(): void {
    // 重新載入資料
    this.reloadFormData();
  }

  /**
   * 手動重新整理資料
   * 供使用者點擊重新整理按鈕使用，會保持當前頁碼和篩選條件
   */
  refreshData(): void {
    console.log('使用者手動觸發重新整理，當前頁碼:', this.currentPage);
    this.isRefreshing = true;

    // 儲存當前的頁碼和篩選狀態
    const savedPage = this.currentPage;
    const savedSearchQuery = this.searchQuery;
    const savedSearchConditions = [...this.searchConditions];

    // 重新載入資料
    this.reloadFormData();

    // 由於 reloadFormData 是非同步的，我們需要在 subscribe 完成後恢復狀態
    // 這裡先設定一個短暫的延遲來確保資料載入完成
    setTimeout(() => {
      this.currentPage = savedPage;
      this.searchQuery = savedSearchQuery;
      this.searchConditions = savedSearchConditions;
      this.isRefreshing = false;
      console.log('重新整理完成，已恢復頁碼:', this.currentPage);
    }, 500);
  }

  /**
   * 重新載入表單資料
   */
  public reloadFormData(): void {
    if (!this.companyid || !this.formId) return;

    this.isLoading = true; // 開始載入
    const apiUrl = `${environment.apiUrl}/api/form-manager/get_form_container`;
    const params = new URLSearchParams({
      componentname: this.formName,
      formid: this.formId
    });

    // 如果有 parentFormId，表示這是子表單，需要過濾資料
    if (this.parentFormId) {
      params.append('parent_formid', this.parentFormId);
      // console.log(this.i18nService.translate('showlist.console.reload_subform_data_parent_record_id'), this.parentFormId);
    }

    // 🆕 處理通用自訂過濾條件
    if (this.customFilters && this.customFilters.length > 0) {
      params.append('customFilters', JSON.stringify(this.customFilters));
    }

    // Permission Control Parameter Settings (Mutual Exclusion)
    if (this.onlyShowDataCreatedBy) {
      params.append('onlyShowDataCreatedBy', this.onlyShowDataCreatedBy);
      // console.log(this.i18nService.translate('showlist.console.reload_only_show_data_created_by_specific_user'), this.onlyShowDataCreatedBy);
    } else if (this.showAdminPermissionBy) {
      params.append('showAdminPermissionBy', this.showAdminPermissionBy);
      // console.log('管理員權限標識 (reload):', this.showAdminPermissionBy);
    }

    // console.log("params.toString()",params.toString());

    this.http.get(`${apiUrl}?${params.toString()}`)
      .subscribe({
        next: (response: any) => {
          if (response.success) {

            this.data = response.data;
            this.originalData = [...response.data]; // 更新原始資料
            // console.log(this.i18nService.translate('showlist.console.reload_data_complete'), this.data);

            // 檢查是否有廢止的項目及其廢止原因
            const abolishedItems = this.data.filter((item: any) => item.deleted);
            if (abolishedItems.length > 0) {
              // console.log('🔍 [showlist] 廢止的項目:', abolishedItems.map((item: any) => ({
              //   id: item.id,
              //   abolishReason: item.abolishReason
              // })));
            }

            // 重新載入後需要更新欄位設定
            this.availableColumns = this.getColumns();
            this.syncNewColumnsToSettings();
            this.applyColumnSettings();

            // 預載入子表單資料筆數
            this.preloadSubformRecordCounts();

            // 重新應用搜尋條件
            this.performSearch();

            // 確保所有處理完成後才停止載入狀態
            setTimeout(() => {
              this.isLoading = false;
              this.hasLoadedOnce = true;
            }, 0);
          } else {
            console.error(this.i18nService.translate('showlist.console.reload_data_api_response_failed'), response);
            this.isLoading = false;
            this.hasLoadedOnce = true;
          }
        },
        error: (error) => {
          console.error(this.i18nService.translate('showlist.console.reload_data_failed'), error);
          this.isLoading = false; // 載入失敗也要停止載入狀態
          this.hasLoadedOnce = true; // 即使載入失敗也標記為已嘗試載入
        }
      });
  }

  // 切換視圖模式
  switchViewMode(mode: 'table' | 'timeline'): void {
    this.viewMode = mode;
  }

  getUniqueGroupValues(groupField: string): string[] {
    const values = new Set<string>();

    this.data.forEach(item => {
      let value: string;

      if (item[groupField] && item[groupField].value !== undefined) {
        value = String(item[groupField].value);
      } else {
        value = String(item[groupField] || '');
      }

      values.add(value);
    });

    return Array.from(values).sort();
  }




  safeConvertToDate(value: any): Date | null {
    if (!value) return null;

    try {
      // 如果是數字或可以轉換為數字的字串
      if (typeof value === 'number' || !isNaN(Number(value))) {
        // 檢查位數判斷是秒還是毫秒格式的時間戳
        const timestamp = Number(value);
        if (timestamp < 10000000000) { // 秒級時間戳通常小於這個數
          return new Date(timestamp * 1000);
        } else {
          return new Date(timestamp);
        }
      } else if (typeof value === 'string') {
        // 如果是日期字串
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    } catch (error) {
      console.error('Date conversion error:', error, value);
    }

    return null;
  }


  getItemPeriodKey(date: Date, dimension: 'day' | 'week' | 'month' | 'quarter' | 'year'): string {
    switch (dimension) {
      case 'day':
        return this.formatDateForKey(date);

      case 'week':
        return `${date.getFullYear()}-W${this.getWeekNumber(date)}`;

      case 'month':
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      case 'quarter':
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        return `${date.getFullYear()}-Q${quarter}`;

      case 'year':
        return `${date.getFullYear()}`;
    }
  }

  getWeekNumber(date: Date): number {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  }

  getPeriodStartDate(periodKey: string, dimension: 'day' | 'week' | 'month' | 'quarter' | 'year'): Date {
    switch (dimension) {
      case 'day':
        return new Date(periodKey);

      case 'week':
        const [yearStr, weekStr] = periodKey.split('-W');
        const year = parseInt(yearStr);
        const week = parseInt(weekStr);

        const firstDayOfYear = new Date(year, 0, 1);
        const dayOfWeek = firstDayOfYear.getDay();
        const daysToAdd = dayOfWeek === 0 ? 1 : (8 - dayOfWeek);

        const firstWeekStart = new Date(firstDayOfYear);
        firstWeekStart.setDate(firstDayOfYear.getDate() + daysToAdd);

        const startDate = new Date(firstWeekStart);
        startDate.setDate(firstWeekStart.getDate() + (week - 1) * 7);

        return startDate;

      case 'month':
        const [yearM, monthM] = periodKey.split('-');
        return new Date(parseInt(yearM), parseInt(monthM) - 1, 1);

      case 'quarter':
        const [yearQ, quarterStr] = periodKey.split('-Q');
        const quarter = parseInt(quarterStr);
        const startMonth = (quarter - 1) * 3;
        return new Date(parseInt(yearQ), startMonth, 1);

      case 'year':
        return new Date(parseInt(periodKey), 0, 1);
    }
  }

  getPeriodEndDate(periodKey: string, dimension: 'day' | 'week' | 'month' | 'quarter' | 'year'): Date {
    const startDate = this.getPeriodStartDate(periodKey, dimension);
    const endDate = new Date(startDate);

    switch (dimension) {
      case 'day':
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'week':
        endDate.setDate(endDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'month':
        endDate.setMonth(endDate.getMonth() + 1);
        endDate.setDate(0);
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'quarter':
        endDate.setMonth(endDate.getMonth() + 3);
        endDate.setDate(0);
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'year':
        endDate.setFullYear(endDate.getFullYear() + 1);
        endDate.setDate(0);
        endDate.setMonth(0);
        endDate.setHours(23, 59, 59, 999);
        break;
    }

    return endDate;
  }

  formatDateForKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getHeatmapColor(count: number, maxCount: number): string {
    if (maxCount <= 0 || count <= 0) return 'rgba(65, 105, 225, 0.1)';

    const intensity = Math.min(0.1 + (count / maxCount) * 0.8, 0.9);
    return `rgba(65, 105, 225, ${intensity})`;
  }

  // 需要過濾掉的欄位清單（全部使用小寫，因為比對時會轉成小寫）
  private readonly excludedColumns = [
    '_rid', '_self', '_etag', 'formid','id', 'parent_formid','_attachments',
    'updated_date', 'updatedat', 'updated_at',  // 隱藏更新時間，只顯示 _ts (異動時間)
    'companyid', 'assistantid', 'assistantname', 'componentname','created_by','deleted','updated_by',
    'isdraft', 'draftsavedat'
  ];

  // 需要過濾掉的欄位前綴清單
  private readonly excludedPrefixes = [
    'rich_text-display',
    'paragraph'
  ];

  // 取得資料中所有的欄位(在這邊直接設定那些欄位不顯示)
  getColumns(): string[] {
    if (this.data && this.data.length > 0) {
      const allKeys = new Set<string>();
      this.data.forEach(item => {
        Object.keys(item).forEach(key => {
          // 檢查是否在排除清單中
          const keyLower = key.toLowerCase();
          const isExcludedColumn = this.excludedColumns.includes(keyLower);

          // 檢查是否以排除前綴開頭
          const isExcludedPrefix = this.excludedPrefixes.some(prefix =>
            key.startsWith(prefix)
          );

          // 檢查欄位類型，過濾掉 file-display 和 video-player 類型
          const fieldData = item[key];
          const isExcludedFieldType = fieldData &&
                                     typeof fieldData === 'object' &&
                                     fieldData.type &&
                                     (fieldData.type === 'file-display' || fieldData.type === 'video-player');

          // 特殊處理 referencedata：展開其內容為獨立欄位
          if (key === 'referencedata' && fieldData) {
            try {
              // 如果是字串，嘗試解析為 JSON
              const parsedData = typeof fieldData === 'string' ? JSON.parse(fieldData) : fieldData;

              if (typeof parsedData === 'object' && parsedData !== null) {
                // 為 referencedata 中的每個項目創建獨立欄位
                Object.keys(parsedData).forEach(refKey => {
                  const expandedKey = `referencedata_${refKey}`;
                  allKeys.add(expandedKey);
                });
              }
            } catch (e) {
              console.error('Error parsing referencedata:', e);
              // 如果解析失敗，還是加入原始欄位
              allKeys.add(key);
            }
          } else if (!isExcludedColumn && !isExcludedPrefix && !isExcludedFieldType) {
            // 隱藏原始的 referencedata 欄位，只顯示展開的欄位
            if (key !== 'referencedata') {
              allKeys.add(key);
            }
          }
        });
      });
      return Array.from(allKeys);
    }
    return [];
  }

  // 取得欄位的顯示名稱，優先使用 label，如果沒有則使用翻譯或欄位名稱
  getColumnDisplayName(column: string): string {
    // 特殊處理 _status 仮想フィールド
    if (column === '_status') {
      return this.i18nService.translate('form.list.status');
    }
    
    // 特殊處理 referencedata 欄位
    if (column === 'referencedata') {
      return '參考資料';
    }

    // 特殊處理展開的 referencedata 欄位
    if (column.startsWith('referencedata_')) {
      const refKey = column.replace('referencedata_', '');
      return this.getReferenceDataLabel(refKey);
    }

    if (!this.data || this.data.length === 0) {
      return this.getTranslatedColumnName(column);
    }

    // 找到第一個包含此欄位的資料項目
    for (const item of this.data) {
      if (item[column] && typeof item[column] === 'object' && item[column].label) {
        // 確保 label 是字串類型，如果不是則轉換成字串
        const labelValue = item[column].label;
        if (typeof labelValue === 'string') {
          return labelValue;
        } else if (labelValue && typeof labelValue.toString === 'function') {
          return labelValue.toString();
        }
        // 如果 label 不是有效字串，繼續尋找下一個項目
        continue;
      }
    }

    // 如果沒有找到 label，返回翻譯後的欄位名稱或原欄位名稱
    return this.getTranslatedColumnName(column);
  }

  /**
   * 取得翻譯後的欄位名稱
   */
  private getTranslatedColumnName(column: string): string {
    // 定義需要翻譯的系統欄位（key 都用小寫）
    const systemFields: { [key: string]: string } = {
      'username': 'common.username',
      'createat': 'common.createAt',
      'created': 'common.createAt',
      'createdat': 'common.createAt',
      'created_at': 'common.createAt',
      'updated_date': 'form.list.updated_date',
      'updatedat': 'form.list.updated_date',
      'updated_at': 'form.list.updated_date',
      '_ts': 'form.list.modified_time',
      'isdraft': 'form.draft.is_draft',
      'draftsavedat': 'form.draft.draft_saved_at'
    };

    // 檢查是否為系統欄位（不區分大小寫）
    const translationKey = systemFields[column.toLowerCase()];
    if (translationKey) {
      return this.i18nService.translate(translationKey);
    }

    // 如果不是系統欄位，返回原欄位名稱
    return column;
  }

  // 根據 searchQuery 與 searchColumns 或動態搜尋條件過濾資料
  get filteredData(): any[] {
    let results: any[] = [];

    // 如果有動態搜尋條件，優先使用動態搜尋結果
    if (this.searchConditions.length > 0) {
      results = this.dynamicFilteredData;
    }
    // 否則使用原有的簡單搜尋邏輯
    else if (!this.searchQuery.trim()) {
      results = this.data;
    }
    else {
      const lowerQuery = this.searchQuery.toLowerCase();

      if (this.searchColumns.length > 0) {
        results = this.data.filter(item =>
          this.searchColumns.some(col =>
            String(item[col] || '').toLowerCase().includes(lowerQuery)
          )
        );
      } else {
        results = this.data.filter(item =>
          Object.values(item).some(val =>
            String(val).toLowerCase().includes(lowerQuery)
          )
        );
      }
    }

    // 🆕 套用外部篩選條件
    if (this.externalFilters && Object.keys(this.externalFilters).length > 0) {
      results = this.applyExternalFiltersToData(results);
    }

    // 無効化されたレコードはスーパーユーザーのみ表示
    const userRoles = this.authService.getUserRolesForModule(this.assistantNo);
    const isSuperAdmin = userRoles.includes('super_admin');

    if (!isSuperAdmin) {
      // スーパーユーザーでない場合、deleted: true のレコードを除外
      results = results.filter(item => !item.deleted);
    }

    // 排序資料
    return this.sortData(results);
  }

  /**
   * 排序資料
   */
  private sortData(data: any[]): any[] {
    if (!data || data.length === 0) {
      return data;
    }

    return [...data].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // 取得比較值
      if (this.sortBy === 'createdAt') {
        aValue = a.createdAt || a.created_at || a.createdat || '';
        bValue = b.createdAt || b.created_at || b.createdat || '';
      } else { // _ts
        aValue = a._ts || 0;
        bValue = b._ts || 0;
      }

      // 轉換為可比較的值
      let compareResult = 0;
      if (this.sortBy === 'createdAt') {
        // ISO 字串可以直接比較
        compareResult = aValue.localeCompare(bValue);
      } else {
        // 數字比較
        compareResult = aValue - bValue;
      }

      // 根據排序方向返回結果
      return this.sortOrder === 'asc' ? compareResult : -compareResult;
    });
  }

  /**
   * 切換排序方式
   */
  changeSortBy(sortBy: 'createdAt' | '_ts'): void {
    this.sortBy = sortBy;
    console.log('切換排序方式:', sortBy);
  }

  /**
   * 切換排序方向
   */
  toggleSortOrder(): void {
    this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    console.log('切換排序方向:', this.sortOrder);
  }

  // 取得分頁後的資料
  get paginatedData(): any[] {
    const filteredResults = this.filteredData;
    this.totalItems = filteredResults.length;
    this.totalPages = Math.ceil(this.totalItems / this.pageSize);

    // 確保當前頁碼在有效範圍內
    if (this.currentPage > this.totalPages && this.totalPages > 0) {
      this.currentPage = this.totalPages;
    }

    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;

    return filteredResults.slice(startIndex, endIndex);
  }

  // === 動態搜尋功能相關方法 ===

  // 初始化搜尋功能
  initializeSearch(): void {
    // 從表單配置中提取可搜尋的欄位
    this.searchableFields = this.originalFormConfig.filter(field =>
      this.isFieldSearchable(field.type)
    );

    // 如果沒有原始表單配置，使用當前可用欄位作為備選
    if (this.searchableFields.length === 0) {
      this.searchableFields = this.availableColumns.map(column => ({
        type: this.inferFieldType(column),
        name: column,
        label: this.getColumnDisplayName(column),
        id: column
      } as FormFieldConfig));
    }

    // 如果啟用搜尋限制，只保留當前顯示的欄位
    if (this.programmaticColumns?.restrictSearch === true) {
      this.searchableFields = this.searchableFields.filter(field =>
        this.displayedColumns.includes(field.name)
      );
    }

    // 添加狀態欄位作為可搜尋欄位
    const statusField: FormFieldConfig = {
      type: 'status',
      name: '_status',
      label: this.i18nService.translate('form.list.status'),
      id: '_status'
    };
    this.searchableFields.push(statusField);

    // 載入下拉選項資料
    this.loadSearchOptions();
  }

  // 判斷欄位是否可搜尋
  isFieldSearchable(fieldType: string): boolean {
    const searchableTypes = [
      'text-input', 'number-input', 'date', 'datetime',
      'select', 'multi-checkbox', 'personnel-selector',
      'department-selector', 'approval-flow', 'approval-personnel-selector', 'signature', 'status'
    ];
    return searchableTypes.includes(fieldType);
  }

  // 推斷欄位類型（當沒有原始配置時的備選方案）
  inferFieldType(column: string): string {
    // 根據欄位名稱或資料內容推斷類型
    const lowerColumn = column.toLowerCase();

    if (lowerColumn.includes('date') || lowerColumn.includes('時間')) {
      return 'datetime';
    }
    if (lowerColumn.includes('number') || lowerColumn.includes('數量')) {
      return 'number-input';
    }
    if (lowerColumn.includes('select') || lowerColumn.includes('選擇')) {
      return 'select';
    }
    if (lowerColumn.includes('signature') || lowerColumn.includes('簽名') || lowerColumn.includes('簽章')) {
      return 'signature';
    }

    // 檢查實際資料類型
    if (this.data.length > 0) {
      const sampleValue = this.data[0][column];
      if (sampleValue && typeof sampleValue === 'object' && sampleValue.type) {
        return sampleValue.type;
      }
    }

    return 'text-input'; // 預設為文字輸入
  }

  // 載入搜尋選項資料
  loadSearchOptions(): void {
    // 載入使用者列表（簡化版）
    this.availableUsers = [
      { id: 'user1', name: '使用者1' },
      { id: 'user2', name: '使用者2' }
    ];

    // 載入部門列表（簡化版）
    this.availableDepartments = [
      { id: 'dept1', name: '部門1' },
      { id: 'dept2', name: '部門2' }
    ];
  }

  // 切換搜尋面板顯示狀態
  toggleSearchPanel(): void {
    this.showSearchPanel = !this.showSearchPanel;
  }

  // 新增搜尋條件
  addSearchCondition(): void {
    this.searchConditions.push({
      fieldName: '',
      fieldType: '',
      operator: 'contains',
      value: ''
    });
  }

  // 移除搜尋條件
  removeSearchCondition(index: number): void {
    this.searchConditions.splice(index, 1);
    // 移除條件後重新執行搜尋
    this.performSearch();
  }

  // 欄位選擇變更時更新欄位類型
  onFieldSelectionChange(index: number): void {
    const condition = this.searchConditions[index];
    const field = this.searchableFields.find(f => f.name === condition.fieldName);

    if (field) {
      condition.fieldType = field.type;
      condition.operator = this.getDefaultOperator(field.type);
      condition.value = this.getDefaultValue(field.type);

      // 清空日期範圍值
      condition.startValue = '';
      condition.endValue = '';

      // 對於簽名欄位，直接設定為 equals 操作符，這樣 UI 就不會顯示操作符選擇
      if (field.type === 'signature') {
        condition.operator = 'equals';
        condition.value = ''; // 重置值讓使用者選擇
      }

      // 對於大頭照上傳欄位，直接設定為 equals 操作符，這樣 UI 就不會顯示操作符選擇
      if (field.type === 'avatar-upload') {
        condition.operator = 'equals';
        condition.value = ''; // 重置值讓使用者選擇
      }

      // 對於檔案上傳管理器欄位，直接設定為 equals 操作符，這樣 UI 就不會顯示操作符選擇
      if (field.type === 'file-upload-manager') {
        condition.operator = 'equals';
        condition.value = ''; // 重置值讓使用者選擇
      }
    }
  }

  // 取得預設操作符
  getDefaultOperator(fieldType: string): string {
    switch (fieldType) {
      case 'text-input':
        return 'contains';
      case 'number-input':
      case 'date':
      case 'datetime':
        return 'equals';
      case 'select':
      case 'personnel-selector':
      case 'department-selector':
      case 'approval-flow':
      case 'approval-personnel-selector':
        return 'equals';
      case 'multi-checkbox':
        return 'contains_any';
      case 'signature':
        return 'equals';
      case 'status':
        return 'equals';
      default:
        return 'contains';
    }
  }

  // 取得預設值
  getDefaultValue(fieldType: string): any {
    switch (fieldType) {
      case 'multi-checkbox':
        return [];
      case 'number-input':
        return '';
      default:
        return '';
    }
  }

  // 根據欄位類型取得可用操作符
  getAvailableOperators(fieldType: string): SearchOperator[] {
    switch (fieldType) {
      case 'text-input':
        return [
          { value: 'contains', label: 'search.operator.contains' },
          { value: 'equals', label: 'search.operator.equals' },
          { value: 'starts_with', label: 'search.operator.starts_with' },
          { value: 'ends_with', label: 'search.operator.ends_with' }
        ];

      case 'number-input':
        return [
          { value: 'equals', label: 'search.operator.equals' },
          { value: 'greater_than', label: 'search.operator.greater_than' },
          { value: 'less_than', label: 'search.operator.less_than' },
          { value: 'between', label: 'search.operator.between' }
        ];

      case 'date':
      case 'datetime':
        return [
          { value: 'equals', label: 'search.operator.equals' },
          { value: 'after', label: 'search.operator.after' },
          { value: 'before', label: 'search.operator.before' },
          { value: 'between', label: 'search.operator.between' }
        ];

      case 'select':
      case 'personnel-selector':
      case 'department-selector':
      case 'approval-flow':
      case 'approval-personnel-selector':
        return [
          { value: 'equals', label: 'search.operator.equals' },
          { value: 'not_equals', label: 'search.operator.not_equals' }
        ];

      case 'multi-checkbox':
        return [
          { value: 'contains_any', label: 'search.operator.contains_any' },
          { value: 'contains_all', label: 'search.operator.contains_all' }
        ];

      case 'signature':
        return [
          { value: 'equals', label: 'search.operator.equals' }
        ];

      case 'avatar-upload':
        return [
          { value: 'equals', label: 'search.operator.equals' }
        ];

      case 'file-upload-manager':
        return [
          { value: 'equals', label: 'search.operator.equals' }
        ];

      case 'status':
        return [
          { value: 'equals', label: 'search.operator.equals' }
        ];

      default:
        return [{ value: 'contains', label: 'search.operator.contains' }];
    }
  }

  // 取得欄位選項（用於下拉選單和多選框）
  getFieldOptions(fieldName: string): FieldOption[] {
    const field = this.searchableFields.find(f => f.name === fieldName);

    if (!field) return [];

    // 根據欄位類型返回選項
    switch (field.type) {
      case 'personnel-selector':
        return this.availableUsers.map(user => ({ value: user.id, label: user.name }));

      case 'department-selector':
        return this.availableDepartments.map(dept => ({ value: dept.id, label: dept.name }));

      case 'select':
      case 'multi-checkbox':
        // 從原始配置中取得選項
        if (field.options && Array.isArray(field.options)) {
          return field.options.map(opt => ({
            value: typeof opt === 'string' ? opt : opt.value || opt.name || opt,
            label: typeof opt === 'string' ? opt : opt.label || opt.name || opt.value || opt
          }));
        }
        return [];

      default:
        return [];
    }
  }

  // 檢查多選框選項是否被選中
  isOptionSelected(selectedValues: any, optionValue: string): boolean {
    if (!selectedValues || !Array.isArray(selectedValues)) return false;
    return selectedValues.includes(optionValue);
  }

  // 切換多選框選項
  toggleCheckboxOption(condition: SearchCondition, optionValue: string): void {
    if (!condition.value || !Array.isArray(condition.value)) {
      condition.value = [];
    }

    const index = condition.value.indexOf(optionValue);
    if (index > -1) {
      condition.value.splice(index, 1);
    } else {
      condition.value.push(optionValue);
    }
  }

  // 執行搜尋
  performSearch(): void {
    let results: any[] = [];

    if (this.searchConditions.length === 0) {
      results = [...this.originalData];
    } else {
      results = this.originalData.filter(item =>
        this.searchConditions.every(condition =>
          this.evaluateCondition(item, condition)
        )
      );
    }

    // 無効化されたレコードはスーパーユーザーのみ表示
    const userRoles = this.authService.getUserRolesForModule(this.assistantNo);
    const isSuperAdmin = userRoles.includes('super_admin');

    if (!isSuperAdmin) {
      // スーパーユーザーでない場合、deleted: true のレコードを除外
      results = results.filter(item => !item.deleted);
    }

    this.dynamicFilteredData = results;
  }

  /**
   * 🆕 套用外部篩選條件（由父元件觸發）
   */
  applyExternalFilters(): void {
    // 觸發 Angular 的變更檢測，重新計算 filteredData
    // 由於 filteredData 是 getter，它會自動使用最新的 externalFilters
  }

  /**
   * 🆕 對資料套用外部篩選邏輯
   */
  private applyExternalFiltersToData(data: any[]): any[] {
    if (!this.externalFilters || Object.keys(this.externalFilters).length === 0) {
      return data;
    }

    return data.filter(item => {
      // 檢查每個篩選條件（所有條件都必須符合）
      for (const [key, value] of Object.entries(this.externalFilters)) {
        // 注意：即使是 false，也要進行篩選（例如 isDraft: false）
        if (value === null || value === undefined || value === '') {
          continue; // 只跳過 null、undefined 或空字串
        }

        // 根據欄位名稱進行不同的比對邏輯
        if (key === 'isDraft') {
          // 草稿狀態篩選
          const itemIsDraft = item.isDraft === true || item.isDraft === 'true' || item.isDraft === 1;
          const filterIsDraft = value === true || value === 'true' || value === 1;
          if (itemIsDraft !== filterIsDraft) {
            return false;
          }
        } else if (key === 'deleted') {
          // 廢止狀態篩選
          const itemDeleted = item.deleted === true || item.deleted === 'true' || item.deleted === 1;
          const filterDeleted = value === true || value === 'true' || value === 1;
          if (itemDeleted !== filterDeleted) {
            return false;
          }
        } else if (key === 'approval_status') {
          // 簽核狀態篩選
          const itemStatus = this.getItemApprovalStatus(item);
          if (itemStatus !== value) {
            return false;
          }
        } else {
          // 一般欄位篩選
          if (item[key] !== value) {
            return false;
          }
        }
      }

      return true; // 通過所有篩選條件
    });
  }

  /**
   * 🆕 取得項目的簽核狀態（用於外部篩選）
   */
  private getItemApprovalStatus(item: any): string {
    // 如果有明確的 approval_status 欄位，直接返回
    if (item.approval_status) {
      return item.approval_status;
    }

    // 否則根據項目狀態推斷（使用與 getItemStatus 相同的邏輯）
    const itemStatus = this.getItemStatus(item);

    // 將 item status 對應到 approval_status
    switch (itemStatus) {
      case 'draft':
        return 'draft';
      case 'abolished':
        return 'abolished';
      case 'disabled':
        return 'disabled';
      case 'in_progress':
        return 'in_progress';  // 審核中/作業中
      case 'approved':
        return 'completed';     // 已結案
      case 'active':
        return 'active';        // 有效
      case 'overdue':
        return 'overdue';       // 🆕 已逾期（元件特定狀態）
      default:
        return 'in_review';     // 預設
    }
  }

  // 評估單一搜尋條件
  evaluateCondition(item: any, condition: SearchCondition): boolean {



    if (!condition.fieldName || !condition.value) {
      return true; // 忽略空條件
    }

    // 特殊處理狀態欄位
    if (condition.fieldName === '_status') {
      const itemStatus = this.getItemStatus(item);
      return itemStatus === condition.value;
    }

    const fieldData = item[condition.fieldName];
    const fieldValue = this.extractFieldValue(fieldData, condition.fieldType);


    switch (condition.operator) {
      case 'contains':
        return this.stringContains(fieldValue, condition.value);

      case 'equals':
        // 特殊處理簽名欄位的 equals 操作
        if (condition.fieldType === 'signature') {
          const hasSignature = this.checkHasSignature(fieldValue);
          return condition.value === 'true' ? hasSignature : !hasSignature;
        }
        // 特殊處理大頭照上傳欄位的 equals 操作
        if (condition.fieldType === 'avatar-upload') {
          const hasAvatar = this.checkHasAvatar(fieldValue);
          return condition.value === 'true' ? hasAvatar : !hasAvatar;
        }
        // 特殊處理檔案上傳管理器欄位的 equals 操作
        if (condition.fieldType === 'file-upload-manager') {
          const hasFiles = this.checkHasFiles(fieldValue);
          return condition.value === 'true' ? hasFiles : !hasFiles;
        }
        return this.valuesEqual(fieldValue, condition.value, condition.fieldType);

      case 'starts_with':
        return String(fieldValue).toLowerCase().startsWith(String(condition.value).toLowerCase());

      case 'ends_with':
        return String(fieldValue).toLowerCase().endsWith(String(condition.value).toLowerCase());

      case 'greater_than':
        return Number(fieldValue) > Number(condition.value);

      case 'less_than':
        return Number(fieldValue) < Number(condition.value);

      case 'after':
        return this.compareDates(fieldValue, condition.value) > 0;

      case 'before':
        return this.compareDates(fieldValue, condition.value) < 0;

      case 'between':
        return this.dateIsBetween(fieldValue, condition.startValue, condition.endValue, condition.fieldType);

      case 'not_equals':
        return !this.valuesEqual(fieldValue, condition.value, condition.fieldType);

      case 'contains_any':
        return this.checkMultiCheckboxAny(fieldValue, condition.value);

      case 'contains_all':
        return this.checkMultiCheckboxAll(fieldValue, condition.value);

      default:
        return true;
    }
  }

  // 從欄位資料中提取值
  extractFieldValue(fieldData: any, fieldType: string): any {
    if (!fieldData) return '';

    // 處理具有 type 和 value 結構的資料
    if (fieldData.type && fieldData.value !== undefined) {
      switch (fieldType) {
        case 'personnel-selector':
        case 'department-selector':
          return fieldData.value.id || fieldData.value;

        case 'datetime':
          return fieldData.value;

        case 'approval-flow':
        case 'approval-personnel-selector':
          const status = this.getApprovalStatus(fieldData.value);
          return status;

        case 'multi-checkbox':
          return fieldData.value;

        case 'signature':
          return fieldData.value;

        default:
          return fieldData.value;
      }
    }

    return fieldData;
  }

  // 字串包含檢查
  stringContains(fieldValue: any, searchValue: any): boolean {
    return String(fieldValue).toLowerCase().includes(String(searchValue).toLowerCase());
  }

  // 值相等檢查
  valuesEqual(fieldValue: any, searchValue: any, fieldType: string): boolean {
    if (fieldType === 'datetime') {
      // 對於日期時間，需要特殊處理
      let fieldDate: Date;
      let searchDate: Date;

      // 處理 Unix 時間戳（秒）
      if (typeof fieldValue === 'number') {
        fieldDate = new Date(fieldValue * 1000);
      } else if (typeof fieldValue === 'string' && !isNaN(Number(fieldValue))) {
        fieldDate = new Date(Number(fieldValue) * 1000);
      } else {
        fieldDate = new Date(fieldValue);
      }

      // 處理搜尋值
      if (typeof searchValue === 'string') {
        searchDate = new Date(searchValue);
      } else {
        searchDate = new Date(searchValue);
      }

      // 檢查日期是否有效
      if (isNaN(fieldDate.getTime()) || isNaN(searchDate.getTime())) {
        return false;
      }

      // 比較日期時間（允許一定的時間誤差，比如同一天內）
      const timeDiff = Math.abs(fieldDate.getTime() - searchDate.getTime());
      const oneDayInMs = 24 * 60 * 60 * 1000; // 一天的毫秒數

      // 如果時間差在一天內，則認為相等（可以根據需求調整）
      return timeDiff < oneDayInMs;
    }    return String(fieldValue).toLowerCase() === String(searchValue).toLowerCase();
  }

  // 日期比較
  compareDates(fieldValue: any, searchValue: any): number {
    let fieldDate: Date;
    let searchDate: Date;

    // 處理欄位值 - 可能是 Unix 時間戳（秒）
    if (typeof fieldValue === 'number') {
      fieldDate = new Date(fieldValue * 1000);
    } else if (typeof fieldValue === 'string' && !isNaN(Number(fieldValue))) {
      fieldDate = new Date(Number(fieldValue) * 1000);
    } else {
      fieldDate = new Date(fieldValue);
    }

    // 處理搜尋值
    if (typeof searchValue === 'string') {
      searchDate = new Date(searchValue);
    } else {
      searchDate = new Date(searchValue);
    }

    return fieldDate.getTime() - searchDate.getTime();
  }

  // 檢查日期是否在範圍內
  dateIsBetween(fieldValue: any, startValue: any, endValue: any, fieldType: string): boolean {
    // 檢查範圍值是否有效
    if (!startValue || !endValue) {
      return false;
    }

    let fieldDate: Date;
    let startDate: Date;
    let endDate: Date;

    // 處理欄位值 - 可能是 Unix 時間戳（秒）
    if (typeof fieldValue === 'number') {
      fieldDate = new Date(fieldValue * 1000);
    } else if (typeof fieldValue === 'string' && !isNaN(Number(fieldValue))) {
      fieldDate = new Date(Number(fieldValue) * 1000);
    } else {
      fieldDate = new Date(fieldValue);
    }

    // 處理起始日期
    startDate = new Date(startValue);
    // 處理結束日期
    endDate = new Date(endValue);

    // 檢查日期是否有效
    if (isNaN(fieldDate.getTime()) || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return false;
    }

    // 設定時間為當天的開始和結束
    const fieldDateOnly = new Date(fieldDate.getFullYear(), fieldDate.getMonth(), fieldDate.getDate());
    const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999);

    return fieldDateOnly >= startDateOnly && fieldDateOnly <= endDateOnly;
  }

  // 多選框 - 包含任一選項
  checkMultiCheckboxAny(fieldValue: any, searchValues: string[]): boolean {
    if (!fieldValue || !Array.isArray(searchValues) || searchValues.length === 0) return false;

    let selectedValues: string[] = [];

    if (Array.isArray(fieldValue)) {
      selectedValues = fieldValue
        .filter(item => item.selected)
        .map(item => item.value || item.option || item.name);
    }

    return searchValues.some(searchVal => selectedValues.includes(searchVal));
  }

  // 多選框 - 包含全部選項
  checkMultiCheckboxAll(fieldValue: any, searchValues: string[]): boolean {
    if (!fieldValue || !Array.isArray(searchValues) || searchValues.length === 0) return false;

    let selectedValues: string[] = [];

    if (Array.isArray(fieldValue)) {
      selectedValues = fieldValue
        .filter(item => item.selected)
        .map(item => item.value || item.option || item.name);
    }

    return searchValues.every(searchVal => selectedValues.includes(searchVal));
  }

  // 檢查項目是否可以編輯（根據簽核狀態和廢止狀態）
  checkApprovalStatus(item: any): { canEdit: boolean; reason: string } {

    // 優先檢查是否已廢止
    if (item.deleted) {
      return { canEdit: false, reason: 'showlist.edit_blocked_disabled' };
    }

    // 檢查是否為暫存狀態（isDraft: true）
    const isDraftCheck = item.isDraft === true || item.isDraft === 'true' || item.isDraft === 1;

    if (isDraftCheck) {
      return { canEdit: true, reason: '' }; // 暫存狀態可以編輯
    }

    // 檢查是否包含簽核元件
    const approvalField = this.findApprovalField(item);
    // console.log('🔍 [SHOWLIST] 簽核欄位:', approvalField);

    if (!approvalField) {
      // console.log('✅ [SHOWLIST] 沒有簽核元件，可以編輯');
      return { canEdit: true, reason: '' }; // 沒有簽核元件，可以編輯
    }

    const approvalStatus = this.getApprovalStatus(approvalField.value);
    // console.log('🔍 [SHOWLIST] 簽核狀態:', approvalStatus);

    // 根據簽核狀態判斷是否可編輯
    switch (approvalStatus) {
      case 'pending':
        // 待處理狀態：還沒開始簽核，通常可以編輯
        // 但需要檢查是否已有簽核人員設定且流程已初始化
        const isInitialized = this.isApprovalInitialized(approvalField.value);
        // console.log('🔍 [SHOWLIST] 簽核流程是否已初始化:', isInitialized);
        if (isInitialized) {
          // console.log('❌ [SHOWLIST] 簽核流程已初始化，不可編輯');
          return { canEdit: false, reason: 'showlist.edit_blocked_approval_pending' };
        }
        // console.log('✅ [SHOWLIST] 簽核流程未初始化，可以編輯');
        return { canEdit: true, reason: '' }; // 修復：未初始化的 pending 狀態可以編輯

      case 'in_progress':
        return { canEdit: false, reason: 'showlist.edit_blocked_approval_in_progress' };

      case 'approved':
      case 'completed':
        return { canEdit: false, reason: 'showlist.edit_blocked_approval_completed' };

      case 'rejected':
        // 差し戻された案件は修正して再提出可能
        return { canEdit: true, reason: '' };
      default:
        return { canEdit: true, reason: '' };
    }
  }

  // 尋找項目中的簽核欄位
  findApprovalField(item: any): any {
    for (const key in item) {
      const value = item[key];
      if (value && typeof value === 'object') {
        // 檢查是否為簽核相關欄位
        if (value.type === 'approval-flow' || value.type === 'approval-personnel-selector') {
          return { key, value };
        }
        // 也檢查雙層嵌狀結構
        if (value.value && typeof value.value === 'object' &&
            (value.value.type === 'approval-flow' || value.value.type === 'approval-personnel-selector')) {
          return { key, value };
        }
      }
    }
    return null;
  }

  // 尋找項目中編號最大的簽核欄位（approval_flow_1, approval_flow_2, ...）
  findLastApprovalFlowField(item: any): any {
    let maxNumber = 0;
    let lastField = null;

    for (const key in item) {
      // 檢查是否符合 approval_flow_N 的命名模式
      const match = key.match(/^approval_flow_(\d+)$/);
      if (match) {
        const number = parseInt(match[1], 10);
        const value = item[key];
        
        if (value && typeof value === 'object') {
          // 檢查是否為簽核相關欄位
          if (value.type === 'approval-flow' || value.type === 'approval-personnel-selector') {
            if (number > maxNumber) {
              maxNumber = number;
              lastField = { key, value };
            }
          }
          // 也檢查雙層嵌狀結構
          else if (value.value && typeof value.value === 'object' && 
              (value.value.type === 'approval-flow' || value.value.type === 'approval-personnel-selector')) {
            if (number > maxNumber) {
              maxNumber = number;
              lastField = { key, value };
            }
          }
        }
      }
    }
    
    return lastField;
  }

  // アイテムに承認フローがあるかチェック
  hasApprovalFlowInItem(item: any): boolean {
    return this.findApprovalField(item) !== null;
  }

  // 檢查簽核流程是否已初始化（有具體的簽核人員和步驟）
  isApprovalInitialized(approvalData: any): boolean {
    try {
      let actualData = approvalData;

      // 處理雙層嵌狀結構
      if (actualData.value) {
        actualData = actualData.value;
        if (actualData.value) {
          actualData = actualData.value;
        }
      }

      // 檢查是否有步驟且步驟有具體的簽核人員
      if (actualData.steps && Array.isArray(actualData.steps) && actualData.steps.length > 0) {
        return actualData.steps.some((step: any) =>
          step.approverList && Array.isArray(step.approverList) && step.approverList.length > 0
        );
      }

      return false;
    } catch (error) {
      console.warn(this.i18nService.translate('showlist.console.check_approval_initialization_error'), error);
      return false;
    }
  }

  // 檢查項目是否可以編輯（用於 UI 顯示）
  canEditItem(item: any): boolean {
    // 原本的簽核狀態檢查
    const approvalCheck = this.checkApprovalStatus(item).canEdit;

    // 如果提供了自定義檢查函數，則使用自定義邏輯
    if (this.customEditDisabledCheck) {
      const customCheck = !this.customEditDisabledCheck(item);
      return approvalCheck && customCheck;
    }

    return approvalCheck;
  }

  // 取得項目狀態 (用於模板顯示)
  getItemStatus(item: any): 'abolished' | 'disabled' | 'draft' | 'active' | 'approved' | 'in_progress' | 'overdue' {
    // 0. 最高優先：如果父元件提供了自訂狀態取得函數，先呼叫它
    if (this.getCustomItemStatus) {
      const customStatus = this.getCustomItemStatus(item);
      if (customStatus !== null) {
        return customStatus as any;  // 返回自訂狀態
      }
      // 如果返回 null，繼續使用預設邏輯
    }

    // 1. 最優先：無効化チェック
    if (item.deleted) {
      // 判斷是廢止還是停用的邏輯：
      // - 停用（disabled）：只能對已核准完成的記錄執行
      // - 廢止（abolished）：任何狀態都可以執行
      //
      // 因此，如果 deleted=true 時：
      // 1. 檢查簽核狀態是否為 completed（已完成）
      // 2. 如果是 completed → 停用（disabled）
      // 3. 如果不是 completed → 廢止（abolished）

      const approvalField = this.findApprovalField(item);
      if (approvalField) {
        const approvalStatus = this.getApprovalStatus(approvalField.value);

        if (approvalStatus === 'completed') {
          // console.log('[Status] ✅ deleted=true + 簽核完成 → 判定為停用（disabled）:', { id: item.id });
          return 'disabled';
        }
      }

      // 沒有簽核流程，或簽核未完成 → 廢止
      // console.log('[Status] ✅ deleted=true + 簽核未完成 → 判定為廢止（abolished）:', { id: item.id });
      return 'abolished';
    }

    // 2. 次に優先：下書きチェック（承認フローの有無に関わらず）
    const isDraft = item.isDraft === true || item.isDraft === 'true' || item.isDraft === 1;
    if (isDraft) {
      return 'draft';
    }

    // 3. 承認フローがある場合、そのステータスをチェック

    // 方法1: 編號付き承認フロー（approval_flow_1, approval_flow_2, ...）
    const lastApprovalField = this.findLastApprovalFlowField(item);
    if (lastApprovalField) {
      // 承認フローが初期化されているかチェック
      const isInitialized = this.isApprovalInitialized(lastApprovalField.value);
      if (isInitialized) {
        const approvalStatus = this.getApprovalStatus(lastApprovalField.value);
        if (approvalStatus === 'completed') {
          return 'approved';  // 承認済
        } else {
          return 'in_progress';  // 申請中
        }
      }
    }

    // 方法2: 任意名称の承認フロー（type: 'approval-flow' または 'approval-personnel-selector'）
    const anyApprovalField = this.findApprovalField(item);
    if (anyApprovalField) {
      // 承認フローが初期化されているかチェック
      const isInitialized = this.isApprovalInitialized(anyApprovalField.value);
      if (isInitialized) {
        const approvalStatus = this.getApprovalStatus(anyApprovalField.value);
        if (approvalStatus === 'completed') {
          return 'approved';  // 承認済
        } else {
          return 'in_progress';  // 申請中
        }
      }
    }

    // 4. 承認フローなし、または承認フローが初期化されていない場合
    return 'active';  // 有効
  }

  /**
   * 🆕 取得狀態顯示文字（優先使用自訂標籤，否則使用預設 i18n）
   * @param status 狀態鍵值
   * @returns 狀態顯示文字
   */
  getStatusLabel(status: string): string {
    // 1. 優先檢查是否有自訂狀態標籤
    if (this.customStatusLabels && this.customStatusLabels[status]) {
      return this.customStatusLabels[status];
    }

    // 2. 使用預設的 i18n 翻譯
    const i18nKey = `form.list.status_${status}`;
    return this.i18nService.translate(i18nKey);
  }

  // 取り下げ可能かチェック
  canWithdrawItem(item: any): boolean {
    // 無効化されている場合は取り下げ不可
    if (item.deleted) {
      return false;
    }

    // 下書き状態の場合は取り下げ不要（既に下書き）
    const isDraft = item.isDraft === true || item.isDraft === 'true' || item.isDraft === 1;
    if (isDraft) {
      return false;
    }

    // 承認フローを探す
    const approvalField = this.findApprovalField(item);
    if (!approvalField) {
      // 承認フローがない場合は取り下げ不要
      return false;
    }

    // 承認フローが初期化されているかチェック
    const isInitialized = this.isApprovalInitialized(approvalField.value);
    if (!isInitialized) {
      // 承認フローが初期化されていない場合は取り下げ不要
      return false;
    }

    // 承認ステータスをチェック
    const approvalStatus = this.getApprovalStatus(approvalField.value);

    // pending（承認待ち）状態のみ取り下げ可能
    // in_progress、approved、rejectedは取り下げ不可
    if (approvalStatus !== 'pending') {
      return false;
    }

    // 🔐 權限檢查：允許以下任一條件撤回
    // 1. created_by 是當前使用者
    // 2. 當前使用者是管理員 (Admin 或 SuperAdmin)
    const itemCreator = item.createdBy || item.created_by || item.createby;
    const isCreator = itemCreator === this.currentUserId;
    const isAdmin = this.hasAdminPermission();

    return isCreator || isAdmin;
  }

  /**
   * 檢查當前使用者是否有管理員權限
   * @returns true 如果使用者是 admin 或 super_admin
   */
  private hasAdminPermission(): boolean {
    try {
      // 從 authService 取得當前使用者的角色
      const userRoles = this.authService.getUserRolesForModule(this.assistantNo);

      // 檢查是否包含 admin 或 super_admin 角色
      return userRoles.includes('admin') || userRoles.includes('super_admin');
    } catch (error) {
      console.error('檢查管理員權限時發生錯誤:', error);
      return false;
    }
  }

  // 無効化可能かチェック（承認済みのレコードのみ）
  canDisableItem(item: any): boolean {
    // 已經被廢止的項目不能再次廢止
    if (item.deleted) {
      return false;
    }

    // 根據 group_id 判斷邏輯
    if (environment.group_id === 'IAD') {
      // IAD: 嚴格邏輯 - 必須有簽核流程且簽核完成
      const approvalField = this.findApprovalField(item);
      if (!approvalField) {
        // 承認フローがない場合は無効化不可
        return false;
      }

      // 承認ステータスをチェック
      const approvalStatus = this.getApprovalStatus(approvalField.value);

      // completed（承認済み）状態のみ無効化可能
      // getApprovalStatus は 'approved' を 'completed' に変換して返す
      return approvalStatus === 'completed';
    }

    // JSAI 或其他: 寬鬆邏輯 - 只要未被廢止就可以執行廢止操作
    return true;
  }

  /**
   * 刪除按鈕是否可用
   * 刪除按鈕的邏輯：任何狀態都可以刪除（包括簽核中、已廢止等）
   */
  canDeleteItem(item: any): boolean {
    // 刪除按鈕始終可用，不受簽核狀態或廢止狀態影響
    // 這是為了測試和管理方便
    return true;
  }

  /**
   * 取得刪除按鈕被阻止的原因
   * 由於 canDeleteItem 始終返回 true，這個方法主要用於未來擴展
   */
  getDeleteBlockedReason(item: any): string {
    return this.i18nService.translate('common.delete');
  }

  // 無効化できない理由を取得（ツールチップ用）
  getDisableBlockedReason(item: any): string {
    // 既に無効化されている場合
    if (item.deleted) {
      return this.i18nService.translate('form.list.already_disabled');
    }

    // 根據 group_id 判斷提示訊息
    if (environment.group_id === 'IAD') {
      // IAD: 嚴格邏輯的提示訊息（承認済みでない場合）
      return this.i18nService.translate('form.list.disable_not_allowed_not_approved');
    }

    // JSAI 或其他: 寬鬆邏輯的提示訊息
    return this.i18nService.translate('form.list.disable');
  }

  /**
   * 取得撤回按鈕的提示訊息
   * @param item 項目資料
   * @returns 提示訊息
   */
  getWithdrawTooltip(item: any): string {
    if (item.deleted) {
      return this.i18nService.translate('form.list.already_disabled');
    }

    const isDraft = item.isDraft === true || item.isDraft === 'true' || item.isDraft === 1;
    if (isDraft) {
      return this.i18nService.translate('form.list.already_withdrawn');
    }

    const approvalField = this.findApprovalField(item);
    if (!approvalField) {
      return this.i18nService.translate('form.list.no_approval_flow');
    }

    const isInitialized = this.isApprovalInitialized(approvalField.value);
    if (!isInitialized) {
      return this.i18nService.translate('form.list.approval_not_initialized');
    }

    const approvalStatus = this.getApprovalStatus(approvalField.value);
    if (approvalStatus !== 'pending') {
      return this.i18nService.translate('form.list.withdraw_not_allowed');
    }

    // 檢查權限
    const itemCreator = item.createdBy || item.created_by || item.createby;
    const isCreator = itemCreator === this.currentUserId;
    const isAdmin = this.hasAdminPermission();

    if (!isCreator && !isAdmin) {
      return this.i18nService.translate('form.list.only_creator_or_admin_can_withdraw');
    }

    // 一切正常，可以撤回
    return this.i18nService.translate('form.list.withdraw');
  }

  // 取得廢止原因
  getAbolishReason(item: any): string {
    if (!item.deleted) {
      return '';
    }

    // 檢查各種可能的位置
    // 優先檢查後端 API 儲存的 invalidationComment 欄位
    if (item.invalidationComment) {
      return item.invalidationComment;
    }

    // 向下相容：檢查舊的 abolishReason 欄位
    if (item.abolishReason) {
      return item.abolishReason;
    }

    // 如果資料中沒有廢止原因，返回「無」
    return '無';
  }

  // 取得編輯被阻止的原因（用於 tooltip 顯示）
  getEditBlockedReason(item: any): string {
    const check = this.checkApprovalStatus(item);

    // 如果簽核狀態不允許編輯，優先顯示簽核相關原因
    if (!check.canEdit) {
      return this.i18nService.translate(check.reason);
    }

    // 如果提供了自定義檢查函數且返回true（表示應該禁用），顯示自定義原因
    if (this.customEditDisabledCheck && this.customEditDisabledCheck(item)) {
      return this.i18nService.translate('showlist.edit_blocked_custom_rule');
    }

    return '';
  }

  // 取得簽核狀態
  getApprovalStatus(approvalData: any): string {

    if (!approvalData) {
      return 'pending';
    }

    // 處理嵌套結構：可能是 {type: 'approval-flow', value: {...}} 或 {type: 'approval-personnel-selector', value: {...}} 的格式
    let actualData = approvalData;

    // 第一層：檢查是否為 {type: 'approval-flow', value: ...} 或 {type: 'approval-personnel-selector', value: ...} 格式
    if ((actualData.type === 'approval-flow' || actualData.type === 'approval-personnel-selector') && actualData.value) {
      actualData = actualData.value;
    }

    // 第二層：進一步檢查是否還有嵌套的 value
    if (actualData.value && typeof actualData.value === 'object' && actualData.value.steps) {
      actualData = actualData.value;
    }

    // 優先檢查整體 status 欄位（如果有的話）
    if (actualData.status) {
      switch (actualData.status.toLowerCase()) {
        case 'approved':
          return 'completed';
        case 'rejected':
          return 'rejected';
        default:
          return 'pending';
      }
    }

    // 如果沒有整體 status，檢查 steps 陣列
    if (!actualData.steps || !Array.isArray(actualData.steps) || actualData.steps.length === 0) {
      return 'pending';
    }

    const steps = actualData.steps;
    // console.log('檢查 steps:', steps);

    // 檢查是否有任何步驟被拒絕
    const hasRejected = steps.some((step: any) => step.status === 'rejected');
    if (hasRejected) {
      return 'rejected';
    }

    // 計算已處理的步驟數量
    const processedSteps = steps.filter((step: any) => step.status === 'approved' || step.status === 'rejected').length;
    const approvedSteps = steps.filter((step: any) => step.status === 'approved').length;


    // 如果所有步驟都已核准（且沒有被拒絕的），則為已完成
    if (approvedSteps === steps.length) {
      return 'completed';
    }

    // 如果有部分步驟已處理但還沒全部完成，則為進行中
    if (processedSteps > 0) {
      return 'pending';
    }

    // 如果沒有任何步驟被處理，則為待處理
    return 'pending';
  }

  // 檢查是否有簽名
  checkHasSignature(fieldValue: any): boolean {
    if (!fieldValue) return false;

    // 處理具有 type 和 value 結構的資料
    if (fieldValue.type && fieldValue.value !== undefined) {
      const signatureData = fieldValue.value;

      // 檢查是否有簽名資料
      if (signatureData && typeof signatureData === 'object') {
        // 檢查是否有 signatureData 或類似的簽名資料結構
        if (signatureData.signatureData || signatureData.signature || signatureData.data) {
          return true;
        }

        // 檢查是否有 base64 格式的簽名圖片
        if (typeof signatureData === 'string' && signatureData.startsWith('data:image/')) {
          return true;
        }
      }

      // 檢查字串型態的簽名資料
      if (typeof signatureData === 'string' && signatureData.trim() !== '') {
        return true;
      }

      return false;
    }

    // 處理直接的簽名資料
    if (typeof fieldValue === 'string') {
      // 檢查是否為 base64 圖片格式
      if (fieldValue.startsWith('data:image/')) {
        return true;
      }
      // 檢查是否有內容且不只是空白字符
      return fieldValue.trim() !== '';
    }

    // 處理物件型態的簽名資料
    if (typeof fieldValue === 'object') {
      // 檢查常見的簽名欄位
      if (fieldValue.signatureData || fieldValue.signature || fieldValue.data) {
        return true;
      }
    }

    return false;
  }

  // 檢查是否有大頭照
  checkHasAvatar(fieldValue: any): boolean {
    if (!fieldValue) return false;

    // 處理具有 type 和 value 結構的資料
    if (fieldValue.type && fieldValue.value !== undefined) {
      const avatarData = fieldValue.value;

      // 檢查是否有大頭照資料
      if (avatarData && typeof avatarData === 'object') {
        // 檢查是否有 fileName、fileUrl 等大頭照相關欄位
        if (avatarData.fileName || avatarData.fileUrl || avatarData.blobName) {
          return true;
        }

        // 檢查是否有 previewUrl
        if (avatarData.previewUrl && avatarData.previewUrl.trim() !== '') {
          return true;
        }
      }

      // 檢查字串型態的大頭照資料（可能是直接的 URL）
      if (typeof avatarData === 'string' && avatarData.trim() !== '') {
        return true;
      }

      return false;
    }

    // 處理直接的大頭照資料
    if (typeof fieldValue === 'string') {
      // 檢查是否有內容且不只是空白字符
      return fieldValue.trim() !== '';
    }

    // 處理物件型態的大頭照資料
    if (typeof fieldValue === 'object') {
      // 檢查常見的大頭照欄位
      if (fieldValue.fileName || fieldValue.fileUrl || fieldValue.blobName || fieldValue.previewUrl) {
        return true;
      }
    }

    return false;
  }

  // 檢查是否有檔案
  checkHasFiles(fieldValue: any): boolean {
    if (!fieldValue) return false;

    // 處理具有 type 和 value 結構的資料
    if (fieldValue.type && fieldValue.value !== undefined) {
      const fileData = fieldValue.value;

      // 檢查是否有檔案陣列且陣列有內容
      if (fileData && fileData.files && Array.isArray(fileData.files)) {
        return fileData.files.length > 0;
      }

      // 檢查 metadata 中的檔案數量
      if (fileData && fileData.metadata && fileData.metadata.totalFiles) {
        return fileData.metadata.totalFiles > 0;
      }

      return false;
    }

    // 處理直接的檔案資料物件
    if (typeof fieldValue === 'object') {
      // 檢查是否有 files 陣列
      if (fieldValue.files && Array.isArray(fieldValue.files)) {
        return fieldValue.files.length > 0;
      }

      // 檢查 metadata 中的檔案數量
      if (fieldValue.metadata && fieldValue.metadata.totalFiles) {
        return fieldValue.metadata.totalFiles > 0;
      }
    }

    return false;
  }

  // 清除搜尋
  clearSearch(): void {
    this.searchConditions = [];

    let results = [...this.originalData];

    // 無効化されたレコードはスーパーユーザーのみ表示
    const userRoles = this.authService.getUserRolesForModule(this.assistantNo);
    const isSuperAdmin = userRoles.includes('super_admin');

    if (!isSuperAdmin) {
      // スーパーユーザーでない場合、deleted: true のレコードを除外
      results = results.filter(item => !item.deleted);
    }

    this.dynamicFilteredData = results;
  }

  getSelectedOptions(options: { name: string; value: string; selected: boolean }[] | any): string {
    // 檢查 options 是否為陣列
    if (!Array.isArray(options)) {
      // 如果不是陣列，嘗試解析為陣列（可能是字串或其他格式）
      if (typeof options === 'string') {
        try {
          const parsed = JSON.parse(options);
          if (Array.isArray(parsed)) {
            options = parsed;
          } else {
            return options; // 如果解析後不是陣列，直接返回字串值
          }
        } catch {
          return options; // 解析失敗，直接返回原值
        }
      } else if (options === null || options === undefined) {
        return ''; // 空值返回空字串
      } else {
        return String(options); // 其他類型轉為字串返回
      }
    }

    // 確保陣列中的元素有正確的結構
    return options.filter((option: any) => option && option.selected === true)
                  .map((option: any) => option.value || option.name || String(option))
                  .join('; ');
  }

  // 新增方法：處理多選框的選中項目顯示
  getMultiCheckboxSelectedText(options: { option: string; selected: boolean; textValue?: string }[] | any): string {
    // 檢查 options 是否為陣列
    if (!Array.isArray(options)) {
      // 如果不是陣列，嘗試解析為陣列
      if (typeof options === 'string') {
        try {
          const parsed = JSON.parse(options);
          if (Array.isArray(parsed)) {
            options = parsed;
          } else {
            return options; // 如果解析後不是陣列，直接返回字串值
          }
        } catch {
          return options; // 解析失敗，直接返回原值
        }
      } else if (options === null || options === undefined) {
        return ''; // 空值返回空字串
      } else {
        return String(options); // 其他類型轉為字串返回
      }
    }

    // 找出所有被選中的選項
    const selectedOptions = options.filter((option: any) => option && option.selected === true);

    // 將選中的選項轉換為顯示文字
    const displayTexts = selectedOptions.map((option: any) => {
      // 處理新的 key/value 資料結構
      let text;

      if (option.option && typeof option.option === 'object' && option.option.value) {
        // 新格式：option 是 { key, value } 物件
        text = option.option.value;
      } else if (option.name && typeof option.name === 'object' && option.name.value) {
        // 新格式：name 是 { key, value } 物件
        text = option.name.value;
      } else {
        // 舊格式相容性
        text = option.option || option.name || String(option);
      }

      // 處理底線替換邏輯
      if (option.textValue && option.textValue.trim() !== '') {
        // 如果選項文字中包含底線，且有textValue，則用textValue替換所有底線
        if (text.includes('_')) {
          text = text.replace(/_+/g, " [" + option.textValue + "] ");
        } else {
          // 如果沒有底線但有textValue，則按原邏輯加上文字值（適用於"其他"等選項）
          text += `: ${option.textValue}`;
        }
      }

      return text;
    });

    return displayTexts.join(', ');
  }

  toggleSearchColumn(col: string, isChecked: boolean) {
    if (isChecked) {
      if (!this.searchColumns.includes(col)) {
        this.searchColumns.push(col);
      }
    } else {
      this.searchColumns = this.searchColumns.filter(c => c !== col);
    }
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.displayedColumns, event.previousIndex, event.currentIndex);
  }

  convertUnixToLocal(timestamp: any): Date | null {
    // 檢查空值或無效值
    if (!timestamp || timestamp === null || timestamp === undefined) {
      return null;
    }

    // 轉換為字串進行檢查
    const timestampStr = String(timestamp);
    if (timestampStr.trim() === '' || timestampStr === '0') {
      return null;
    }

    const numTimestamp = Number(timestampStr);
    // 檢查是否為有效數字且不為 0
    if (isNaN(numTimestamp) || numTimestamp === 0) {
      return null;
    }

    return new Date(numTimestamp * 1000);
  }

  parsePhotos(photos: Photo[] | null): Photo[] {
    return photos ?? [];
  }

  viewImageFullSize(imageUrl: string): void {
    if (imageUrl) {
      window.open(imageUrl, '_blank', 'noopener,noreferrer');
    }
  }

  // Avatar 資料處理輔助方法 - 支援兩種資料格式
  getAvatarData(columnData: any): any {
    if (!columnData || !columnData.value) return null;

    // 新格式：檢查是否有 value.value 結構
    if (columnData.value.value && typeof columnData.value.value === 'object') {
      return columnData.value.value;
    }

    // 舊格式：直接使用 value
    return columnData.value;
  }

  getAvatarDisplayMode(columnData: any): string {
    const avatarData = this.getAvatarData(columnData);
    return avatarData?.displayMode || 'square';
  }

  getAvatarImageUrl(columnData: any): string {
    const avatarData = this.getAvatarData(columnData);
    return avatarData?.previewUrl || avatarData?.fileUrl || '';
  }

  getAvatarFileName(columnData: any): string {
    const avatarData = this.getAvatarData(columnData);
    return avatarData?.fileName || 'Avatar';
  }

  // 處理大頭照圖片載入錯誤
  onAvatarImageError(item: any, column: string, event: Event): void {
    const img = event.target as HTMLImageElement;
    const avatarData = this.getAvatarData(item[column]);

    console.log(this.i18nService.translate('showlist.console.avatar_load_failed_retry_generate_url'), avatarData);

    if (avatarData && avatarData.blobName) {
      // 嘗試重新產生預覽 URL
      this.fileUploadService.generateViewUrl(avatarData.blobName).subscribe({
        next: (response) => {
          if (response && response.data.viewUrl) {
            // 更新前端記憶體中的 URL
            avatarData.previewUrl = response.data.viewUrl;
            avatarData.fileUrl = response.data.viewUrl; // 同時更新 fileUrl
            img.src = response.data.viewUrl;
            console.log(this.i18nService.translate('showlist.console.avatar_url_regenerate_success'), response.data.viewUrl);

            // 🎯 關鍵：將更新後的 URL 保存回資料庫
            this.updateAvatarUrlInDatabase(avatarData, response.data.viewUrl);
          }
        },
        error: (error) => {
          console.error(this.i18nService.translate('showlist.console.avatar_url_regenerate_failed'), error);
          // 設定預設頭像
          img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmNWY1ZjUiLz4KPGNpcmNsZSBjeD0iMjAiIGN5PSIxNiIgcj0iNiIgZmlsbD0iI2NjYyIvPgo8cGF0aCBkPSJNOCAzMmMwLTYuNjI3IDUuMzczLTEyIDEyLTEyaDhjNi42MjcgMCAxMiA1LjM3MyAxMiAxMnYwSDh2MHoiIGZpbGw9IiNjY2MiLz4KPC9zdmc+';
        }
      });
    } else {
      // 沒有 blobName，直接設定預設頭像
      img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmNWY1ZjUiLz4KPGNpcmNsZSBjeD0iMjAiIGN5PSIxNiIgcj0iNiIgZmlsbD0iI2NjYyIvPgo8cGF0aCBkPSJNOCAzMmMwLTYuNjI3IDUuMzczLTEyIDEyLTEyaDhjNi42MjcgMCAxMiA1LjM3MyAxMiAxMnYwSDh2MHoiIGZpbGw9IiNjY2MiLz4KPC9zdmc+';
    }
  }

  // 處理照片圖片載入錯誤
  onPhotoImageError(photo: any, event: Event): void {
    const img = event.target as HTMLImageElement;

    console.log(this.i18nService.translate('showlist.console.photo_load_failed_retry_generate_url'), photo);

    if (photo && photo.blobName) {
      // 嘗試重新產生預覽 URL
      this.fileUploadService.generateViewUrl(photo.blobName).subscribe({
        next: (response) => {
          if (response && response.data.viewUrl) {
            // 更新前端記憶體中的 URL
            photo.fileUrl = response.data.viewUrl;
            photo.previewUrl = response.data.viewUrl; // 同時更新 previewUrl
            img.src = response.data.viewUrl;
            console.log(this.i18nService.translate('showlist.console.photo_url_regenerate_success'), response.data.viewUrl);

            // 🎯 關鍵：將更新後的 URL 保存回資料庫
            this.updateFileUrlInDatabase(photo, response.data.viewUrl);
          }
        },
        error: (error) => {
          console.error(this.i18nService.translate('showlist.console.photo_url_regenerate_failed'), error);
          // 設定破圖圖示
          img.style.display = 'none';
          const brokenIcon = document.createElement('i');
          brokenIcon.className = 'fas fa-image text-muted';
          brokenIcon.title = '圖片載入失敗';
          img.parentNode?.appendChild(brokenIcon);
        }
      });
    } else {
      // 沒有 blobName，顯示破圖圖示
      img.style.display = 'none';
      const brokenIcon = document.createElement('i');
      brokenIcon.className = 'fas fa-image text-muted';
      brokenIcon.title = '圖片載入失敗';
      img.parentNode?.appendChild(brokenIcon);
    }
  }

  // 音訊重新產生相關屬性
  isRegeneratingAudio: { [key: string]: boolean } = {};

  // 文件下載相關屬性
  isDownloadingDocument: { [key: string]: boolean } = {};

  // 產生音訊唯一鍵值
  getAudioKey(item: any, column: string): string {
    return `${item.id || 'unknown'}_${column}`;
  }

  // 處理音訊載入錯誤
  onAudioError(item: any, column: string, event: Event): void {
    const audio = event.target as HTMLAudioElement;
    const audioValue = item[column].value;

    console.log(this.i18nService.translate('showlist.console.audio_load_failed_retry_url'), audioValue);

    if (audioValue && audioValue.blobName) {
      this.regenerateAudioUrl(item, column);
    }
  }

  // 重新產生音訊 URL
  regenerateAudioUrl(item: any, column: string): void {
    const audioKey = this.getAudioKey(item, column);
    const audioValue = item[column].value;

    if (!audioValue) {
      console.error(this.i18nService.translate('showlist.console.no_audio_data_cannot_regenerate_url'));
      return;
    }

    // 設定載入狀態
    this.isRegeneratingAudio[audioKey] = true;

    // 如果有 blobName，嘗試通過 API 重新產生 URL - 加強安全檢查
    if (audioValue && audioValue.blobName) {
      this.fileUploadService.generateViewUrl(audioValue.blobName).subscribe({
        next: (response) => {
          if (response && response.data.viewUrl) {
            // 更新音訊 URL
            audioValue.previewUrl = response.data.viewUrl;
            audioValue.fileUrl = response.data.viewUrl;

            console.log(this.i18nService.translate('showlist.console.audio_url_regenerate_success_generated'), response.data.viewUrl);

            // 🎯 關鍵：將更新後的 URL 保存回資料庫
            this.updateFileUrlInDatabase(audioValue, response.data.viewUrl);
          }
          this.isRegeneratingAudio[audioKey] = false;
        },
        error: (error) => {
          console.error(this.i18nService.translate('showlist.console.audio_url_regenerate_failed'), error);
          this.isRegeneratingAudio[audioKey] = false;
        }
      });
    } else {
      // 沒有 blobName，可能是舊資料格式，嘗試其他方法
      console.warn(this.i18nService.translate('showlist.console.audio_no_blob_try_filename'));

      if (audioValue.fileName) {
        this.fileUploadService.generateViewUrl(audioValue.fileName).subscribe({
          next: (response) => {
            if (response && response.data.viewUrl) {
              audioValue.previewUrl = response.data.viewUrl;
              audioValue.fileUrl = response.data.viewUrl;
              console.log(this.i18nService.translate('showlist.console.audio_filename_regenerate_success'), response.data.viewUrl);

              // 🎯 關鍵：將更新後的 URL 保存回資料庫
              this.updateFileUrlInDatabase(audioValue, response.data.viewUrl);
            }
            this.isRegeneratingAudio[audioKey] = false;
          },
          error: (error) => {
            console.error(this.i18nService.translate('showlist.console.audio_url_filename_regenerate_failed'), error);
            this.isRegeneratingAudio[audioKey] = false;
          }
        });
      } else {
        console.error(this.i18nService.translate('showlist.console.audio_data_no_blob_no_filename'));
        this.isRegeneratingAudio[audioKey] = false;
      }
    }
  }

  // 產生文件唯一鍵值
  getDocumentKey(item: any, column: string): string {
    return `${item.id || 'unknown'}_${column}`;
  }

  // 統一處理不同格式的必要文件資料，提取檔案物件
  getRequiredDocumentFile(documentValue: any): any {
    if (!documentValue) return null;


    // 格式1：直接在 documentValue.file 中（舊的單一檔案格式）
    if (documentValue.file) {
      return documentValue.file;
    }

    // 格式2：在 documentValue.originalValue.file 中（新格式）
    if (documentValue.originalValue && documentValue.originalValue.file) {
      return documentValue.originalValue.file;
    }

    // 格式3：深層巢狀 - documentValue.originalValue.originalValue.file
    if (documentValue.originalValue &&
        documentValue.originalValue.originalValue &&
        documentValue.originalValue.originalValue.file) {
      return documentValue.originalValue.originalValue.file;
    }

    // 格式4：在 documentValue.files 中（檔案管理格式）
    if (documentValue.files && documentValue.files.length > 0) {
      return documentValue.files[0];
    }

    // 格式5：在 documentValue.originalValue.files 中（檔案管理格式）
    if (documentValue.originalValue && documentValue.originalValue.files && documentValue.originalValue.files.length > 0) {
      return documentValue.originalValue.files[0];
    }

    // 格式6：遞迴搜尋所有層級的 originalValue
    let current = documentValue;
    let depth = 0;
    const maxDepth = 5; // 防止無限遞迴

    while (current && depth < maxDepth) {
      if (current.file) {
        return current.file;
      }
      if (current.files && current.files.length > 0) {
        return current.files[0];
      }
      current = current.originalValue;
      depth++;
    }

    return null;
  }

  // 統一處理不同格式的 AI 解析結果
  getRequiredDocumentAIResponse(documentValue: any): string {
    if (!documentValue) return '';

    // 格式1：直接在 documentValue.aiParseResult 中
    if (documentValue.aiParseResult) {
      // 優先使用 response 欄位（新格式）
      if (documentValue.aiParseResult.response) {
        return documentValue.aiParseResult.response;
      }
      // 向下相容：使用 AI_Response 欄位（舊格式）
      if (documentValue.aiParseResult.AI_Response) {
        return documentValue.aiParseResult.AI_Response;
      }
    }

    // 格式2：在 documentValue.originalValue.aiParseResult 中
    if (documentValue.originalValue && documentValue.originalValue.aiParseResult) {
      // 優先使用 response 欄位（新格式）
      if (documentValue.originalValue.aiParseResult.response) {
        return documentValue.originalValue.aiParseResult.response;
      }
      // 向下相容：使用 AI_Response 欄位（舊格式）
      if (documentValue.originalValue.aiParseResult.AI_Response) {
        return documentValue.originalValue.aiParseResult.AI_Response;
      }
    }

    return '';
  }

  // 取得單選按鈕的選擇值
  getRadiobuttonSelectedValue(radioValue: any): string {
    if (!radioValue) return '';

    // console.log('getRadiobuttonSelectedValue 收到的資料:', radioValue);

    // 處理直接傳入的物件格式 { key: "option1", value: "主承攬商" }
    if (radioValue.key && radioValue.value) {
      return radioValue.value;
    }

    // 優先使用 selectedValue.value（您要求的格式）
    if (radioValue.selectedValue && radioValue.selectedValue.value) {
      return radioValue.selectedValue.value;
    }

    // 備用：selectedLabel
    if (radioValue.selectedLabel) {
      return radioValue.selectedLabel;
    }

    // 向下相容：其他格式
    if (radioValue.value && typeof radioValue.value === 'object' && radioValue.value.value) {
      return radioValue.value.value;
    }

    // 如果是字串直接返回
    if (typeof radioValue === 'string') {
      return radioValue;
    }

    return '';
  }

  // 檢查是否有條件資料
  hasRadiobuttonConditionalData(radioValue: any): boolean {
    if (!radioValue || !radioValue.conditionalData) return false;

    return !!(radioValue.conditionalData.remarks || radioValue.conditionalData.audioRecording);
  }

  // 顯示單選按鈕詳細資料的對話框
  showRadiobuttonDetails(item: any, column: string): void {
    const radioValue = item[column].value;
    if (!radioValue || !radioValue.conditionalData) return;

    // 設置對話框資料
    this.radiobuttonDetailData = {
      selectedValue: this.getRadiobuttonSelectedValue(radioValue),
      remarks: radioValue.conditionalData.remarks || '',
      audioRecording: radioValue.conditionalData.audioRecording || null,
      uploadedFiles: radioValue.conditionalData.uploadedFiles || [], // 加入上傳檔案
      completeText: radioValue.completeText || ''
    };

    this.showRadiobuttonDetailModal = true;
  }

  // 關閉單選按鈕詳細資料對話框
  closeRadiobuttonDetails(): void {
    this.showRadiobuttonDetailModal = false;
    this.radiobuttonDetailData = null;
  }

  // 統一處理不同格式的檔案管理器資料，優先返回包含完整資訊的檔案物件
  getFileManagerFiles(documentValue: any): any[] {
    if (!documentValue) return [];

    // 格式1：優先檢查 originalValue.files（新格式中包含完整資訊）
    if (documentValue.originalValue && documentValue.originalValue.files && Array.isArray(documentValue.originalValue.files)) {
      const originalFiles = documentValue.originalValue.files;
      // 檢查第一個檔案是否有完整的 blobName 或 viewUrl - 加強安全檢查
      if (originalFiles.length > 0 && originalFiles[0] && originalFiles[0] && (originalFiles[0].blobName || originalFiles[0].viewUrl)) {
        // console.log('使用 originalValue.files，檔案有完整資訊:', originalFiles[0]);
        return originalFiles;
      }
    }

    // 格式2：如果 originalValue 不存在或不完整，使用 documentValue.files（舊格式）
    if (documentValue.files && Array.isArray(documentValue.files)) {
      const files = documentValue.files;
      // 檢查第一個檔案是否有完整的 blobName 或 viewUrl - 加強安全檢查
      if (files.length > 0 && files[0] && files[0] && (files[0].blobName || files[0].viewUrl)) {
        // console.log('使用 documentValue.files，檔案有完整資訊:', files[0]);
        return files;
      } else if (files.length > 0 && files[0]) {
        // console.log('documentValue.files 中的檔案缺少 blobName/viewUrl:', files[0]);
        // 如果檔案缺少關鍵資訊但仍然存在，仍然返回（讓 viewFile 方法處理）
        return files;
      }
    }

    return [];
  }

  // 檔案列表展開狀態管理
  private expandedFilesLists: { [key: string]: boolean } = {};

  // 切換檔案列表展開/收起狀態
  toggleFilesList(key: string): void {
    this.expandedFilesLists[key] = !this.expandedFilesLists[key];
  }

  // 檢查檔案列表是否已展開
  isFilesListExpanded(key: string): boolean {
    return !!this.expandedFilesLists[key];
  }

  // 格式化檔案大小
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  // 取得可見的檔案列表（支援展開/收起）
  getVisibleFiles(files: any[], key: string): any[] {
    if (!files || files.length === 0) return [];

    const maxInitialFiles = 3; // 初始顯示的檔案數量
    const isExpanded = this.isFilesListExpanded(key);

    if (files.length <= maxInitialFiles || isExpanded) {
      return files; // 顯示所有檔案
    }

    return files.slice(0, maxInitialFiles); // 只顯示前3個檔案
  }

  // 檢視必要文件（使用檔案瀏覽器）
  downloadRequiredDocument(item: any, column: string): void {
    const documentKey = this.getDocumentKey(item, column);
    const documentValue = item[column].value;

    // 使用統一的方法獲取檔案資料
    const file = this.getRequiredDocumentFile(documentValue);

    if (!file) {
      console.error(this.i18nService.translate('showlist.console.no_file_data_cannot_download'), documentValue);
      alert(this.i18nService.translate('showlist.file_link_unavailable'));
      return;
    }

    console.log(this.i18nService.translate('showlist.console.found_file_data'), file);

    // 設定下載狀態
    this.isDownloadingDocument[documentKey] = true;

    // 如果有 blobName，嘗試重新產生下載 URL - 加強安全檢查
    if (file && file.blobName) {
      this.fileUploadService.generateViewUrl(file.blobName).subscribe({
        next: (response) => {
          if (response && response.data.viewUrl) {
            // 使用檔案檢視器開啟檔案
            this.openFileInNewTab(response.data.viewUrl);

            // 同時更新檔案資料中的 URL（以供下次使用）
            file.downloadUrl = response.data.viewUrl;
            file.previewUrl = response.data.viewUrl;
            file.viewUrl = response.data.viewUrl;

            console.log(this.i18nService.translate('showlist.console.document_download_url_regenerate_success'), response.data.viewUrl);

            // 🎯 關鍵：將更新後的 URL 保存回資料庫
            this.updateFileUrlInDatabase(file, response.data.viewUrl);
          } else {
            console.error(this.i18nService.translate('showlist.console.document_url_regenerate_failed_no_valid_url'));
            // 嘗試使用原始 URL
            this.tryOriginalDownloadUrl(file);
          }
          this.isDownloadingDocument[documentKey] = false;
        },
        error: (error) => {
          console.error(this.i18nService.translate('showlist.console.document_url_regenerate_failed'), error);
          // 嘗試使用原始 URL
          this.tryOriginalDownloadUrl(file);
          this.isDownloadingDocument[documentKey] = false;
        }
      });
    } else {
      // 沒有 blobName，嘗試使用現有的 URL
      this.tryOriginalDownloadUrl(file);
      this.isDownloadingDocument[documentKey] = false;
    }
  }

  /**
   * 將重新產生的檔案 URL 更新到資料庫（適用於照片、音訊等一般檔案）
   */
  private updateFileUrlInDatabase(fileData: any, newUrl: string): void {
    // 找到包含這個檔案的資料項目
    const targetItem = this.data.find(item => {
      // 遍歷所有欄位，尋找包含這個 fileData 的項目
      return Object.values(item).some(fieldValue => {
        // 檢查是否是包含檔案資料的物件
        if (typeof fieldValue === 'object' && fieldValue && (fieldValue as any).value) {
          const fieldValueObj = fieldValue as any;
          // 如果 value 是陣列，檢查陣列中的每個元素
          if (Array.isArray(fieldValueObj.value)) {
            return fieldValueObj.value.some((fileItem: any) => fileItem === fileData);
          }
          // 如果 value 是物件，檢查是否是目標檔案
          return fieldValueObj.value === fileData;
        }
        return false;
      });
    });

    if (!targetItem) {
      console.warn(this.i18nService.translate('showlist.console.file_item_not_found_cannot_update_db'));
      return;
    }

    console.log(this.i18nService.translate('showlist.console.prepare_update_file_url_to_db'), {
      itemId: targetItem.id,
      newUrl: newUrl,
      fileData: fileData
    });

    // 準備更新資料
    const updateData = {
      companyid: environment.companyid,
      container_name: this.formName,
      form_template_id: this.formId,
      id: targetItem.id,
      form_data: targetItem // 包含更新後的 fileData
    };

    // 呼叫更新 API
    const url = `${environment.apiUrl}/api/form-manager/save_form_container`;
    this.http.post<any>(url, updateData).subscribe({
      next: (response) => {
        if (response.success) {
          console.log(this.i18nService.translate('showlist.console.file_url_updated_to_db_success'));
        } else {
          console.error(this.i18nService.translate('showlist.console.update_file_url_database_failed'), response);
        }
      },
      error: (error) => {
        console.error(this.i18nService.translate('showlist.console.update_file_url_api_failed'), error);
      }
    });
  }

  /**
   * 將重新產生的大頭照 URL 更新到資料庫
   */
  private updateAvatarUrlInDatabase(avatarData: any, newUrl: string): void {
    // 找到包含這個大頭照的資料項目
    const targetItem = this.data.find(item => {
      // 遍歷所有欄位，尋找包含這個 avatarData 的項目
      return Object.values(item).some(fieldValue => {
        if (typeof fieldValue === 'object' && fieldValue === avatarData) {
          return true;
        }
        return false;
      });
    });

    if (!targetItem) {
      console.warn(this.i18nService.translate('showlist.console.avatar_item_not_found_cannot_update_db'));
      return;
    }

    console.log(this.i18nService.translate('showlist.console.prepare_update_avatar_url_to_db'), {
      itemId: targetItem.id,
      newUrl: newUrl,
      avatarData: avatarData
    });

    // 準備更新資料
    const updateData = {
      companyid: environment.companyid,
      container_name: this.formName,
      form_template_id: this.formId,
      id: targetItem.id,
      form_data: targetItem // 包含更新後的 avatarData
    };

    // 呼叫更新 API
    const url = `${environment.apiUrl}/api/form-manager/save_form_container`;
    this.http.post<any>(url, updateData).subscribe({
      next: (response) => {
        if (response.success) {
          console.log(this.i18nService.translate('showlist.console.avatar_url_updated_to_db_success'));
        } else {
          console.error(this.i18nService.translate('showlist.console.update_avatar_url_database_failed'), response);
        }
      },
      error: (error) => {
        console.error(this.i18nService.translate('showlist.console.update_avatar_url_api_failed'), error);
      }
    });
  }

  // 嘗試使用原始 URL 開啟檔案檢視器
  private tryOriginalDownloadUrl(file: any): void {
    const downloadUrl = file.viewUrl || file.downloadUrl || file.previewUrl;
    if (downloadUrl) {
      console.log(this.i18nService.translate('showlist.console.use_original_url_open_file_viewer'), downloadUrl);
      this.openFileInNewTab(downloadUrl);
    } else {
      console.error(this.i18nService.translate('showlist.console.no_available_file_url'));
      alert(this.i18nService.translate('showlist.file_link_expired_contact_admin'));
    }
  }

  // 觸發檔案下載
  private triggerFileDownload(url: string, filename: string): void {
    try {
      // 建立臨時連結並觸發下載
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      // 暫時加入到 DOM 並點擊
      document.body.appendChild(link);
      link.click();

      // 清理
      document.body.removeChild(link);
    } catch (error) {
      console.error(this.i18nService.translate('showlist.console.trigger_download_failed'), error);
      // 後備方案：開啟新視窗
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  getGoogleMapsUrl(location: any): string {
    let locationString = '';

    // 向下相容：處理不同的資料格式
    if (typeof location === 'string') {
      // 舊格式：直接是座標字串
      locationString = location;
    } else if (location && typeof location === 'object') {
      // 新格式：物件包含座標資訊
      if (location.originalValue && typeof location.originalValue === 'string') {
        locationString = location.originalValue;
      } else if (location.address && typeof location.address === 'string') {
        locationString = location.address;
      } else if (location.summary && typeof location.summary === 'string') {
        // 從 summary 中提取座標 (格式: "位置: lat, lng")
        const match = location.summary.match(/位置:\s*([-\d.]+,\s*[-\d.]+)/);
        if (match) {
          locationString = match[1];
        }
      }
    }

    if (!locationString) {
      return '#';
    }

    // 解析座標
    const parts = locationString.split(',');
    if (parts.length === 2) {
      const lat = parts[0].trim();
      const lng = parts[1].trim();

      // 驗證座標格式
      if (!isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng))) {
        return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
      }
    }

    return '#';
  }

  // 檔案查看方法
  viewFile(file: any): void {
    console.log(this.i18nService.translate('showlist.console.viewfile_called_file_info'), file);

    // 檢查 viewUrl 是否需要重新產生（過期或缺少 Content-Type）
    const isUrlPotentiallyExpired = (url: string): boolean => {
      if (!url.includes('se=')) return false; // 沒有過期時間參數

      try {
        const urlObj = new URL(url);

        // 檢查是否缺少 Content-Type 參數（對於 PDF 檔案很重要）
        const rsct = urlObj.searchParams.get('rsct');
        if (!rsct && url.toLowerCase().includes('.pdf')) {
          console.log(this.i18nService.translate('showlist.console.pdf_missing_content_type_regenerate_url'));
          return true;
        }

        // 檢查是否過期
        const se = urlObj.searchParams.get('se'); // 取得過期時間
        if (se) {
          const expiryTime = new Date(se);
          const now = new Date();
          // 如果還有5分鐘就過期或已過期，重新產生
          return (expiryTime.getTime() - now.getTime()) < 5 * 60 * 1000;
        }
      } catch (error) {
        console.warn(this.i18nService.translate('showlist.console.url_parse_error'), error);
      }
      return false;
    };

    // 優先順序1：檢查是否有 viewUrl 且未過期
    if (file.viewUrl && !isUrlPotentiallyExpired(file.viewUrl)) {
      console.log(this.i18nService.translate('showlist.console.using_existing_view_url'), file.viewUrl);
      this.openFileInNewTab(file.viewUrl);
      return;
    }

    // 如果 viewUrl 已過期，記錄並繼續到重新產生邏輯
    if (file.viewUrl && isUrlPotentiallyExpired(file.viewUrl)) {
      console.log(this.i18nService.translate('showlist.console.view_url_expired'), file.viewUrl);
    }

    // 優先順序2：如果有 blobName，重新產生 viewUrl - 加強安全檢查
    if (file && file.blobName) {
      console.log(this.i18nService.translate('showlist.console.regenerating_view_url'), file.blobName);
      // 使用 FileUploadService 產生瀏覽連結
      this.fileUploadService.generateViewUrl(file.blobName).subscribe({
        next: (response: any) => {
          if (response.success && response.data.viewUrl) {
            console.log(this.i18nService.translate('showlist.console.regenerated_view_url'), response.data.viewUrl);

            // 更新檔案物件中的 URL
            file.viewUrl = response.data.viewUrl;
            file.previewUrl = response.data.viewUrl;

            // 🎯 關鍵：將更新後的 URL 保存回資料庫
            this.updateFileUrlInDatabase(file, response.data.viewUrl);

            this.openFileInNewTab(response.data.viewUrl);
          } else {
            console.warn(this.i18nService.translate('showlist.console.cannot_generate_view_link'), response);
            alert(this.i18nService.translate('showlist.file_link_generate_error'));
          }
        },
        error: (error) => {
          console.error(this.i18nService.translate('showlist.console.generate_view_link_error'), error);
          alert(this.i18nService.translate('showlist.file_link_generate_failed'));
        }
      });
      return;
    }

    // 優先順序3：備用方案，使用 downloadUrl（可能會觸發下載）
    if (file.downloadUrl) {
      console.log(this.i18nService.translate('showlist.console.using_download_url_fallback'), file.downloadUrl);
      window.open(file.downloadUrl, '_blank');
      return;
    }

    // 找不到任何可用的 URL - 加強安全檢查
    console.warn(this.i18nService.translate('showlist.console.file_missing_required_info'), file || 'file is undefined');
    alert(this.i18nService.translate('showlist.file_link_unavailable'));
  }

  // Files Modal 相關方法
  openFilesModal(files: any[], item: any, column: string): void {
    this.modalFiles = files;
    this.modalItem = item;
    this.modalColumn = column;
    this.showFilesModal = true;
  }

  closeFilesModal(): void {
    this.showFilesModal = false;
    this.modalFiles = [];
    this.modalItem = null;
    this.modalColumn = '';
  }

  getTotalFileSize(files: any[]): string {
    if (!files || files.length === 0) return '0 B';

    const totalBytes = files.reduce((sum, file) => {
      // 嘗試從不同的屬性取得檔案大小
      const size = file.size || file.fileSize || file.contentLength || 0;
      return sum + (typeof size === 'number' ? size : 0);
    }, 0);

    // 轉換為適當的單位
    const units = ['B', 'KB', 'MB', 'GB'];
    let unitIndex = 0;
    let size = totalBytes;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(unitIndex > 0 ? 2 : 0)} ${units[unitIndex]}`;
  }

  formatUploadTime(file: any): string {
    // 嘗試從不同的屬性取得上傳時間
    const timestamp = file.uploadTime || file.createdAt || file.lastModified || file.timestamp;

    if (!timestamp) return '';

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';

      // 格式化為 YYYY-MM-DD HH:mm
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.warn(this.i18nService.translate('showlist.console.format_upload_time_error'), error);
      return '';
    }
  }

  onToggleSearchColumn(col: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    this.toggleSearchColumn(col, target.checked);
  }

  editItem(item: any) {

    // 檢查項目是否有 id 欄位（必須有才能更新）
    if (!item.id) {
      alert(this.i18nService.translate('showlist.edit_missing_id'));
      return;
    }

    // 檢查簽核狀態 - 如果包含簽核元件且已啟動，則禁止編輯
    const approvalCheck = this.checkApprovalStatus(item);
    if (!approvalCheck.canEdit) {
      alert(this.i18nService.translate(approvalCheck.reason));
      return;
    }


    this.editingItem = JSON.parse(JSON.stringify(item)); // 深度複製避免直接修改原始資料

    // console.log('✅ ' + this.i18nService.translate('showlist.console.setting_editing_item_id') + ':', this.editingItem.id);
    // console.log('✅ ' + this.i18nService.translate('showlist.console.id_passed_to_dynamic_form'));
    // console.log('✅ ' + this.i18nService.translate('showlist.console.parent_formid_check') + ':');
    // console.log('  - editingItem.parent_formid:', this.editingItem.parent_formid);
    // console.log('  - showlist.parentFormId:', this.parentFormId);
    // console.log('  - ' + this.i18nService.translate('showlist.console.will_use_value'), this.editingItem.parent_formid || this.parentFormId);

    // 調試資訊：顯示所有可用的欄位
    // console.log('=== ' + this.i18nService.translate('showlist.console.debugging_edit_function') + ' ===');
    // console.log(this.i18nService.translate('showlist.console.editing_item_fields'), Object.keys(this.editingItem));
    // console.log(this.i18nService.translate('showlist.console.displayed_columns'), this.displayedColumns);
    // console.log(this.i18nService.translate('showlist.console.editable_columns'), this.getEditableColumns());
    // console.log(this.i18nService.translate('showlist.console.original_form_config'), this.originalFormConfig);

    // 生成編輯表單配置
    this.editFormConfig = this.generateEditFormConfig(this.editingItem);

    // 準備初始資料
    this.editingItemData = this.prepareInitialData(this.editingItem);

    // console.log('Edit form config:', this.editFormConfig);
    // console.log('Initial data:', this.editingItemData);

    // 特別檢查關鍵欄位的初始值
    // console.log('🎯 ' + this.i18nService.translate('showlist.console.select_1_initial_value'), this.editingItemData.select_1);
    // console.log('🎯 ' + this.i18nService.translate('showlist.console.multi_checkbox_1_initial_value'), this.editingItemData.multi_checkbox_1);

    // 檢查表單配置中的 initialValue
    // const select1Config = this.editFormConfig.find(f => f.name === 'select_1');
    // const multiCheckbox1Config = this.editFormConfig.find(f => f.name === 'multi_checkbox_1');
    // console.log('🎯 ' + this.i18nService.translate('showlist.console.select_1_config_initial_value'), select1Config ? (select1Config as any).initialValue : 'NOT FOUND');
    // console.log('🎯 ' + this.i18nService.translate('showlist.console.multi_checkbox_1_config_initial_value'), multiCheckbox1Config ? (multiCheckbox1Config as any).initialValue : 'NOT FOUND');

    // 使用 setTimeout 確保資料完全準備好後再顯示編輯視窗，避免閃現未初始化的欄位
    setTimeout(() => {
      this.showEditDialog = true;
    }, 0);
  }

  /**
   * 直接開啟簽核視窗（不開啟編輯視窗）
   * 用於審核通知連結，直接觸發對應記錄的簽核步驟
   * 透過 ViewChildren 找到對應項目的 approval-flow-display 元件並呼叫其 openSingleStepApproval 方法
   * @param item 要簽核的項目
   * @param stepIndex 要開啟的簽核步驟索引
   */
  openApprovalOnly(item: any, stepIndex: number = 0): void {
    // console.log('🔍 [openApprovalOnly] 直接開啟簽核視窗', { itemId: item.id, stepIndex });

    // 檢查項目是否有 id 欄位
    if (!item.id) {
      alert(this.i18nService.translate('showlist.edit_missing_id'));
      return;
    }

    // 🔒 安全檢查：禁止對已廢止或停用的項目進行簽核操作
    if (item.deleted) {
      console.error('❌ [openApprovalOnly] 此項目已被廢止或停用，無法進行簽核操作', { itemId: item.id, deleted: item.deleted });
      alert(this.i18nService.translate('showlist.approval_disabled_for_deleted_item') || '此項目已被廢止或停用，無法進行簽核操作');
      return;
    }

    // 檢查項目是否有簽核流程欄位
    const approvalFlowColumn = this.displayedColumns.find(col => {
      const fieldConfig = this.originalFormConfig?.find(field => field.name === col);
      return fieldConfig && (
        fieldConfig.type === 'approval-flow' ||
        fieldConfig.type === 'approval-personnel-selector'
      );
    });

    if (!approvalFlowColumn) {
      console.error('❌ [openApprovalOnly] 找不到簽核流程欄位');
      alert(this.i18nService.translate('approval.error.no_permission'));
      return;
    }

    const approvalFlowData = item[approvalFlowColumn];
    if (!approvalFlowData || !approvalFlowData.value) {
      console.error('❌ [openApprovalOnly] 簽核流程資料不存在');
      alert('此記錄沒有簽核流程資料');
      return;
    }

    // 取得簽核流程步驟
    const steps = this.convertToApprovalFlowSteps(approvalFlowData);
    const approvalFlow = this.getApprovalFlowData(approvalFlowData);

    if (!steps || steps.length === 0) {
      console.error('❌ [openApprovalOnly] 簽核流程步驟為空');
      alert('此記錄沒有簽核步驟');
      return;
    }

    if (stepIndex < 0 || stepIndex >= steps.length) {
      console.error('❌ [openApprovalOnly] 簽核步驟索引超出範圍', { stepIndex, stepsLength: steps.length });
      alert('簽核步驟索引錯誤');
      return;
    }

    const targetStep = steps[stepIndex];
    if (!targetStep.stepDefinitionId) {
      console.error('❌ [openApprovalOnly] 缺少步驟定義ID');
      alert('缺少步驟定義ID，無法開啟簽核視窗');
      return;
    }

    // console.log('✅ [openApprovalOnly] 驗證通過，準備呼叫 approval-flow-display 元件');
    // console.log('📋 [openApprovalOnly] 步驟資訊:', { stepIndex, stepName: targetStep.stepName, stepDefinitionId: targetStep.stepDefinitionId });

    // 【關鍵】找到對應項目的 approval-flow-display 元件
    // 因為列表中每個項目都有一個 approval-flow-display 元件
    // 我們需要找到對應項目的那一個
    const dataIndex = this.paginatedData.findIndex(dataItem => dataItem.id === item.id);

    if (dataIndex === -1) {
      console.error('❌ [openApprovalOnly] 在當前頁面找不到對應的項目');
      alert('在當前頁面找不到對應的項目，請確認項目是否在當前頁面中');
      return;
    }

    // 取得所有 approval-flow-display 元件
    const approvalFlowComponentsArray = this.approvalFlowComponents?.toArray();

    if (!approvalFlowComponentsArray || approvalFlowComponentsArray.length === 0) {
      console.error('❌ [openApprovalOnly] 找不到 approval-flow-display 元件');
      alert('找不到簽核流程元件，請稍後再試');
      return;
    }

    // 找到對應索引的元件 (假設每個資料項目對應一個 approval-flow-display 元件)
    const targetComponent = approvalFlowComponentsArray[dataIndex];

    if (!targetComponent) {
      console.error('❌ [openApprovalOnly] 找不到對應索引的 approval-flow-display 元件', { dataIndex });
      alert('找不到對應的簽核流程元件');
      return;
    }

    // console.log('✅ [openApprovalOnly] 找到 approval-flow-display 元件，準備開啟簽核視窗');

    // 呼叫 approval-flow-display 元件的 openSingleStepApproval 方法
    targetComponent.openSingleStepApproval(targetStep, stepIndex);

    // console.log('🎉 [openApprovalOnly] 已呼叫 approval-flow-display 元件開啟簽核視窗');
  }

  /**
   * 觸發指定記錄的簽核視窗
   * 透過資料索引找到對應的 DOM 元素並觸發簽核步驟點擊
   * @param item 記錄物件（直接傳遞，避免重複查找）
   * @param stepIndex 簽核步驟索引
   * @param retryCount 重試次數
   */
  private triggerApprovalModalForItem(item: any, stepIndex: number, retryCount: number = 0): void {
    const maxRetries = 15; // 增加最大重試次數 (從 10 增加到 15)
    const itemId = item.id;
    // console.log(`🔍 [triggerApproval] 嘗試觸發簽核視窗 (第 ${retryCount + 1}/${maxRetries} 次)`, { itemId, stepIndex });
    // console.log(`📊 [triggerApproval] 當前列表資料數量: ${this.data.length}`);
    // console.log(`📊 [triggerApproval] 顯示的資料數量: ${this.paginatedData.length}`);

    // 檢查資料是否已載入
    if (this.data.length === 0) {
      console.warn('⚠️ [triggerApproval] 列表資料尚未載入');

      // 如果重試次數未達上限，延遲重試
      if (retryCount < maxRetries) {
        // console.log(`⏳ [triggerApproval] 等待資料載入，${500}ms 後重試...`);
        setTimeout(() => {
          this.triggerApprovalModalForItem(item, stepIndex, retryCount + 1);
        }, 500);
        return;
      } else {
        console.error('❌ [triggerApproval] 資料載入超時');
        alert('資料載入超時，請重新整理頁面後再試。');
        return;
      }
    }

    // 從資料中找到對應的記錄索引
    const dataIndex = this.data.findIndex(dataItem => dataItem.id === itemId);

    if (dataIndex === -1) {
      console.error(`❌ [triggerApproval] 在資料陣列中找不到記錄: ${itemId}`);
      // console.log('📋 [triggerApproval] 資料陣列中的前 5 個 ID:', this.data.slice(0, 5).map(dataItem => dataItem.id));

      // 可能是異步問題，嘗試將記錄加入資料陣列
      if (retryCount === 0) {
        // console.log('📝 [triggerApproval] 嘗試將記錄加入資料陣列');
        this.data.push(item);
        this.performSearch(); // 重新整理分頁資料
        setTimeout(() => {
          this.triggerApprovalModalForItem(item, stepIndex, retryCount + 1);
        }, 300);
        return;
      }

      // 如果重試次數未達上限，延遲重試
      if (retryCount < maxRetries) {
        // console.log(`⏳ [triggerApproval] 等待資料同步，${500}ms 後重試...`);
        setTimeout(() => {
          this.triggerApprovalModalForItem(item, stepIndex, retryCount + 1);
        }, 500);
        return;
      } else {
        console.error('❌ [triggerApproval] 多次重試後仍找不到記錄');
        alert('找不到對應的記錄，可能資料尚未載入完成或記錄不存在。');
        return;
      }
    }

    // console.log(`✅ [triggerApproval] 在資料陣列中找到記錄，索引: ${dataIndex}`);

    // 檢查記錄是否在當前頁面中
    const paginatedIndex = this.paginatedData.findIndex(item => item.id === itemId);

    if (paginatedIndex === -1) {
      console.warn('⚠️ [triggerApproval] 記錄不在當前頁面中，需要切換頁面');

      // 計算記錄所在的頁面
      const itemsPerPage = this.pageSize || 10;
      const targetPage = Math.floor(dataIndex / itemsPerPage) + 1;

      // console.log(`🔄 [triggerApproval] 切換到第 ${targetPage} 頁 (當前頁: ${this.currentPage})`);
      this.currentPage = targetPage;

      // 等待 DOM 更新後再次嘗試
      setTimeout(() => {
        this.triggerApprovalModalForItem(item, stepIndex, retryCount + 1);
      }, 500);
      return;
    }

    // console.log(`✅ [triggerApproval] 記錄在當前頁面中，分頁索引: ${paginatedIndex}`);

    // 等待 DOM 完全渲染後，根據分頁索引找到對應的表格行
    // 使用 requestAnimationFrame 確保在下一個渲染週期執行
    setTimeout(() => {
      const rows = document.querySelectorAll('.data-table tbody tr');
      // console.log(`📊 [triggerApproval] 找到的表格行數: ${rows.length}`);

      if (rows.length === 0) {
        console.warn('⚠️ [triggerApproval] DOM 中沒有找到表格行');

        // 如果重試次數未達上限，延遲重試
        if (retryCount < maxRetries) {
          // console.log(`⏳ [triggerApproval] 等待 DOM 渲染，${500}ms 後重試...`);
          setTimeout(() => {
            this.triggerApprovalModalForItem(item, stepIndex, retryCount + 1);
          }, 500);
          return;
        } else {
          console.error('❌ [triggerApproval] DOM 載入超時');
          alert('表格載入超時，請稍後再試。');
          return;
        }
      }

      // 使用分頁索引直接定位行
      if (paginatedIndex >= rows.length) {
        console.error(`❌ [triggerApproval] 分頁索引超出範圍 (索引: ${paginatedIndex}, 行數: ${rows.length})`);

        // DOM 可能還在更新，重試
        if (retryCount < maxRetries) {
          // console.log(`⏳ [triggerApproval] 等待 DOM 更新，${300}ms 後重試...`);
          setTimeout(() => {
            this.triggerApprovalModalForItem(item, stepIndex, retryCount + 1);
          }, 300);
          return;
        } else {
          alert('找不到對應的表格行。');
          return;
        }
      }

      const targetRow = rows[paginatedIndex] as HTMLElement;
      // console.log(`✅ [triggerApproval] 找到對應記錄的表格行 (分頁索引: ${paginatedIndex})`);

      // 在該行中找到簽核流程元件的步驟節點
      // 使用 .step-node 而不是 .approval-step（這是 approval-flow-display 元件的實際 class）
      const approvalSteps = targetRow.querySelectorAll('.step-node');
      // console.log(`📊 [triggerApproval] 找到的簽核步驟數量: ${approvalSteps.length}`);

      if (approvalSteps && approvalSteps.length > stepIndex) {
        const targetStep = approvalSteps[stepIndex] as HTMLElement;
        // console.log(`✅ [triggerApproval] 找到簽核步驟元素 (索引: ${stepIndex})`);

        // 檢查該步驟是否可點擊
        if (targetStep.classList.contains('clickable')) {
          // console.log('✅ [triggerApproval] 簽核步驟可點擊，準備觸發點擊事件');
          targetStep.click();
          // console.log('🎉 [triggerApproval] 已觸發簽核步驟點擊，簽核視窗應該已開啟');
        } else {
          console.warn('⚠️ [triggerApproval] 該簽核步驟不可點擊');
          // console.warn('   步驟的 class 列表:', Array.from(targetStep.classList).join(', '));
          alert('您沒有權限審核此步驟，或此步驟尚未輪到您審核。');
        }
      } else {
        console.warn(`⚠️ [triggerApproval] 未找到對應的簽核步驟`);
        // console.warn(`   可用步驟數量: ${approvalSteps.length}`);
        // console.warn(`   需要的索引: ${stepIndex}`);

        // 簽核元件可能還在載入，重試
        if (retryCount < maxRetries) {
          // console.log(`⏳ [triggerApproval] 等待簽核元件載入，${400}ms 後重試...`);
          setTimeout(() => {
            this.triggerApprovalModalForItem(item, stepIndex, retryCount + 1);
          }, 400);
          return;
        } else {
          console.error('❌ [triggerApproval] 多次重試後仍找不到簽核步驟');
          alert('找不到對應的簽核步驟');
        }
      }
    }, 300);
  }

  /**
   * 開啟編輯視窗並自動觸發簽核視窗
   * @deprecated 此方法已棄用，請使用 openApprovalOnly() 代替
   * @param item 要編輯的項目
   * @param stepIndex 要開啟的簽核步驟索引
   */
  editItemAndOpenApproval(item: any, stepIndex: number = 0): void {
    // console.log('⚠️ editItemAndOpenApproval 已棄用，請使用 openApprovalOnly()');
    // console.log('🔍 editItemAndOpenApproval 被呼叫', { item, stepIndex });

    // 檢查項目是否有 id 欄位
    if (!item.id) {
      alert(this.i18nService.translate('showlist.edit_missing_id'));
      return;
    }

    // 檢查簽核狀態
    const approvalCheck = this.checkApprovalStatus(item);
    if (!approvalCheck.canEdit) {
      // console.log('⚠️ 項目正在簽核中，但仍允許開啟簽核視窗');
    }

    // 執行一般的 editItem 邏輯
    this.editingItem = JSON.parse(JSON.stringify(item));
    this.editFormConfig = this.generateEditFormConfig(this.editingItem);
    this.editingItemData = this.prepareInitialData(this.editingItem);
    this.showEditDialog = true;

    // 等待 DOM 更新後自動觸發簽核視窗
    setTimeout(() => {
      this.triggerApprovalModalByStepIndex(stepIndex);
    }, 500);
  }

  /**
   * 根據步驟索引自動觸發簽核視窗
   * 這個方法使用 DOM 操作來觸發簽核流程中對應步驟的點擊
   * @param stepIndex 要開啟的簽核步驟索引
   */
  private triggerApprovalModalByStepIndex(stepIndex: number): void {
    // console.log('🔍 嘗試觸發簽核視窗，步驟索引:', stepIndex);

    // 查找編輯視窗中的簽核流程步驟
    // 使用更具體的選擇器以確保找到正確的元素
    const approvalSteps = document.querySelectorAll('.edit-dialog .approval-step');
    // console.log('📊 找到的簽核步驟數量:', approvalSteps.length);

    if (approvalSteps && approvalSteps.length > stepIndex) {
      const targetStep = approvalSteps[stepIndex] as HTMLElement;
      // console.log('✅ 找到簽核步驟元素（索引 ' + stepIndex + '）');

      // 檢查該步驟是否可點擊
      if (targetStep.classList.contains('clickable')) {
        // console.log('✅ 簽核步驟可點擊，準備觸發點擊事件');
        targetStep.click();
        // console.log('✅ 已觸發簽核步驟點擊');
      } else {
        console.warn('⚠️ 該簽核步驟不可點擊（可能不是當前使用者負責的步驟）');
        // console.warn('   步驟的 class 列表:', Array.from(targetStep.classList).join(', '));
      }
    } else {
      console.warn('⚠️ 未找到對應的簽核步驟');
      // console.warn('   可用步驟數量:', approvalSteps.length);
      // console.warn('   需要的索引:', stepIndex);
    }
  }

  // 處理編輯項目的資料格式，確保日期時間欄位能在表單中正確顯示
  processEditingItemForDisplay(): void {
    if (!this.editingItem) return;

    // 只處理可編輯的欄位（排除系統欄位）
    const editableColumns = this.getEditableColumns();

    editableColumns.forEach(column => {
      const value = this.editingItem[column];

      if (value && typeof value === 'object' && value.type) {
        if (value.type === 'datetime') {
          // 將 Unix 時間戳轉換為 datetime-local 格式
          if (value.value && (typeof value.value === 'number' || !isNaN(Number(value.value)))) {
            const date = new Date(Number(value.value) * 1000);
            // 格式化為 YYYY-MM-DDTHH:mm（datetime-local 所需格式）
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');

            this.editingItem[column].displayValue = `${year}-${month}-${day}T${hours}:${minutes}`;
          } else {
            // 如果沒有值或解析失敗，設定為空字串
            this.editingItem[column].displayValue = '';
          }
        } else if (value.type === 'date') {
          // 將日期格式轉換為 date input 所需格式
          if (value.value) {
            if (typeof value.value === 'string') {
              const date = new Date(value.value);
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                this.editingItem[column].displayValue = `${year}-${month}-${day}`;
              } else {
                // 如果日期解析失敗，設定為空字串
                this.editingItem[column].displayValue = '';
              }
            } else {
              // 如果不是字串，設定為空字串
              this.editingItem[column].displayValue = '';
            }
          } else {
            // 如果沒有值，設定為空字串
            this.editingItem[column].displayValue = '';
          }
        }
      }
    });
  }

  // 取得可編輯的欄位（排除系統欄位）
  getEditableColumns(): string[] {
    // 當使用 programmaticColumns 時，編輯功能應該能使用所有可用欄位，
    // 而不是僅限於顯示的欄位，這樣編輯功能才不會因為欄位篩選而受影響
    const sourceColumns = this.programmaticColumns?.columns ? this.availableColumns : this.displayedColumns;

    return sourceColumns.filter(column => {
      // 排除一些不應該編輯的欄位
      const nonEditableFields = ['createAt', 'createat', 'created', 'timestamp'];
      return !nonEditableFields.some(field => column.toLowerCase().includes(field.toLowerCase()));
    });
  }

  /**
   * 檢查使用者是否有權限查看此元件
   * 如果 componentPermissions 傳入空陣列 []，代表使用者沒有任何權限
   * 如果 componentPermissions 未傳入（undefined），則使用預設邏輯（允許查看）
   */
  hasAnyPermission(): boolean {
    // 如果有傳入 componentPermissions
    if (this.componentPermissions !== undefined) {
      // 如果是空陣列，代表沒有任何權限
      if (this.componentPermissions.length === 0) {
        return false;
      }
      // 有權限陣列且不為空，則有權限
      return true;
    }

    // 未傳入 componentPermissions，使用預設邏輯（允許查看）
    return true;
  }

 /**
   * 檢查是否有新增權限
   * 優先使用 @Input componentPermissions，如果未提供則使用預設邏輯
   */
  hasCreatePermission(): boolean {
    // 如果有傳入 componentPermissions，檢查是否包含 'create'
    if (this.componentPermissions !== undefined) {
      return this.componentPermissions.includes('create');
    }

    // 預設邏輯：
    // 如果隱藏新增按鈕，則不允許新增
    if (this.hideAddButton) {
      return false;
    }
    // 檢查使用者角色，如果是 contractor 角色則不允許新增
    if (this.userRoles.includes('contractor')) {
      return false;
    }
    // 其他角色允許新增
    return true;
  }

  // 檢查是否有操作權限
  /**
   * 檢查是否有編輯權限
   * 優先使用 @Input componentPermissions，如果未提供則使用預設邏輯
   */
  hasEditPermission(): boolean {
    // 如果有傳入 componentPermissions，檢查是否包含 'edit'
    if (this.componentPermissions !== undefined) {
      return this.componentPermissions.includes('edit');
    }

    // 預設邏輯：允許所有人編輯
    return true;
  }

  /**
   * 檢查是否有刪除權限
   * 優先使用 @Input componentPermissions，如果未提供則使用預設邏輯
   */
  hasDeletePermission(): boolean {
    // 如果有傳入 componentPermissions，檢查是否包含 'delete'
    if (this.componentPermissions !== undefined) {
      return this.componentPermissions.includes('delete');
    }

    // 預設邏輯：只有 super_admin 可以執行刪除操作
    const userRoles = this.authService.getUserRolesForModule(this.assistantNo);
    return userRoles.includes('super_admin');
  }

  /**
   * 檢查是否有無效化權限
   * 優先使用 @Input componentPermissions，如果未提供則使用預設邏輯
   */
  hasDisablePermission(): boolean {
    // 如果有傳入 componentPermissions，檢查是否包含 'disable'
    if (this.componentPermissions !== undefined) {
      return this.componentPermissions.includes('disable');
    }

    // 預設邏輯：只有 super_admin 且 group_id = IAD 可以執行無効化操作
    const userRoles = this.authService.getUserRolesForModule(this.assistantNo);
    return userRoles.includes('super_admin') && environment.group_id === 'IAD';
  }

  /**
   * 檢查是否有撤回權限
   * 優先使用 @Input componentPermissions，如果未提供則使用預設邏輯
   */
  hasWithdrawPermission(): boolean {
    // 如果有傳入 componentPermissions，檢查是否包含 'withdraw'
    if (this.componentPermissions !== undefined) {
      return this.componentPermissions.includes('withdraw');
    }

    // 預設邏輯：只有 group_id = IAD 時才顯示撤回按鈕
    // 實際執行時會再檢查是否為申請者本人
    return environment.group_id === 'IAD';
  }

 

  /**
   * 取得總資料筆數
   */
  getTotalRecordsCount(): number {
    if (!this.data) {
      return 0;
    }
    return this.data.length;
  }

//針對那些沒有特別建立switch case的欄位做處理
  getDirectDisplayValue(value: any, column: string): string {


    if (!value) {
      return '';
    }

    // 如果是物件且有 type，使用 getDisplayValue 處理
    if (typeof value === 'object' && value.type) {

      return this.getDisplayValue(value.value);
    }

    // 如果是一般物件，嘗試轉為 JSON 或顯示友善訊息
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    // 其他情況直接返回
    return String(value);
  }

  // 取得展開的 referencedata 欄位標籤
  getReferenceDataLabel(refKey: string): string {
    // 尋找任何包含此 refKey 的 referencedata，並返回其 label
    if (this.data && this.data.length > 0) {
      for (const item of this.data) {
        if (item.referencedata) {
          try {
            const parsedData = typeof item.referencedata === 'string'
              ? JSON.parse(item.referencedata)
              : item.referencedata;

            if (parsedData && parsedData[refKey] && parsedData[refKey].label) {
              return parsedData[refKey].label;
            }
          } catch (e) {
            console.error('Error parsing referencedata for label:', e);
          }
        }
      }
    }

    // 如果找不到 label，返回格式化的欄位名稱
    return refKey.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  // 取得展開的 referencedata 欄位值
  getReferenceDataValue(item: any, column: string): string {
    if (!column.startsWith('referencedata_')) {
      return '';
    }

    const refKey = column.replace('referencedata_', '');

    if (!item.referencedata) {
      return '';
    }

    try {
      // 如果是字串，嘗試解析為 JSON
      const parsedData = typeof item.referencedata === 'string'
        ? JSON.parse(item.referencedata)
        : item.referencedata;

      if (parsedData && parsedData[refKey]) {
        const refItem = parsedData[refKey];

        // 如果有 value 屬性，使用它；否則返回整個物件的字串形式
        if (refItem.value !== undefined) {
          return String(refItem.value);
        } else if (refItem.key !== undefined) {
          return String(refItem.key);
        } else {
          return JSON.stringify(refItem);
        }
      }
    } catch (e) {
      console.error('Error parsing referencedata for value:', e);
    }

    return '';
  }

  // 取得參考資料的項目數量
  getReferenceDataCount(data: any): number {
    if (!data) return 0;

    try {
      // 如果是字串，嘗試解析為 JSON
      const parsedData = typeof data === 'string' ? JSON.parse(data) : data;

      // 計算物件的 key 數量
      if (typeof parsedData === 'object' && parsedData !== null) {
        return Object.keys(parsedData).length;
      }
    } catch (e) {
      console.error('Error parsing reference data:', e);
    }

    return 0;
  }

  // 開啟參考資料對話框
  openReferenceDataDialog(data: any): void {
    if (!data) return;

    try {
      // 如果是字串，嘗試解析為 JSON
      const parsedData = typeof data === 'string' ? JSON.parse(data) : data;

      if (typeof parsedData === 'object' && parsedData !== null) {
        this.referenceDataDialogTitle = '參考資料';
        this.referenceDataContent = parsedData;
        this.showReferenceDataDialog = true;
      }
    } catch (e) {
      console.error('Error parsing reference data:', e);
      alert(this.i18nService.translate('showlist.reference_data_parse_failed'));
    }
  }

  // 關閉參考資料對話框
  closeReferenceDataDialog(): void {
    this.showReferenceDataDialog = false;
    this.referenceDataContent = null;
  }

  // 取得參考資料項目列表供模板使用
  getReferenceDataItems(): any[] {
    if (!this.referenceDataContent) return [];

    const items: any[] = [];

    for (const key in this.referenceDataContent) {
      if (this.referenceDataContent.hasOwnProperty(key)) {
        const value = this.referenceDataContent[key];

        // 處理 pre-filled 類型的資料
        if (value && typeof value === 'object') {
          items.push({
            label: value.label || key,
            displayValue: value.value || '',
            keyId: value.key || null
          });
        } else {
          // 處理簡單的鍵值對
          items.push({
            label: key,
            displayValue: value || '',
            keyId: null
          });
        }
      }
    }

    return items;
  }

  // 處理編輯項目的資料格式，確保提交到資料庫的格式正確
  processEditingItemForSave(): void {
    if (!this.editingItem) return;

    const editableColumns = this.getEditableColumns();

    editableColumns.forEach(column => {
      const value = this.editingItem[column];

      if (value && typeof value === 'object' && value.type) {
        if (value.type === 'datetime' && value.displayValue) {
          // 將 datetime-local 格式轉換回 Unix 時間戳
          const date = new Date(value.displayValue);
          if (!isNaN(date.getTime())) {
            this.editingItem[column].value = Math.floor(date.getTime() / 1000);
          }
          // 移除臨時的 displayValue
          delete this.editingItem[column].displayValue;
        } else if (value.type === 'date' && value.displayValue) {
          // 保存日期格式
          this.editingItem[column].value = value.displayValue;
          delete this.editingItem[column].displayValue;
        }
      }
    });
  }

  // 廢止項目
  disableItem(item: any) {
    // console.log('Disable item:', item);

    // 檢查項目是否有 id 欄位（必須有才能廢止）
    if (!item.id) {
      alert(this.i18nService.translate('showlist.delete_missing_id'));
      return;
    }

    // 無効化可能かチェック（承認済みのみ）
    if (!this.canDisableItem(item)) {
      if (item.deleted) {
        alert(this.i18nService.translate('form.list.already_disabled'));
      } else {
        alert(this.i18nService.translate('form.list.disable_not_allowed_not_approved'));
      }
      return;
    }

    this.disablingItem = item;
    this.disableComment = ''; // 🆕 無効理由をリセット
    this.showDisableConfirm = true;
  }

  // 取り下げ項目（申請を下書きに戻す）
  withdrawItem(item: any) {
    // console.log('🔍 [withdrawItem] Withdraw item:', item);

    // IDチェック
    if (!item.id) {
      alert(this.i18nService.translate('showlist.delete_missing_id'));
      return;
    }

    // 🔍 DEBUG: 檢查 canWithdrawItem 的返回值
    const canWithdraw = this.canWithdrawItem(item);
    // console.log('🔍 [withdrawItem] canWithdrawItem 返回值:', canWithdraw);

    // 取り下げ可能かチェック
    if (!canWithdraw) {
      const reason = this.getWithdrawTooltip(item);
      // console.log('🔍 [withdrawItem] 無法撤回，原因:', reason);
      alert(reason);
      return;
    }

    // console.log('✅ [withdrawItem] 可以撤回，顯示確認對話框');
    this.withdrawingItem = item;
    this.showWithdrawConfirm = true;
  }

  // 廢止項目（軟刪除，帶廢止原因）
  abolishItem(item: any) {
    console.log('Abolish item:', item);

    // 檢查項目是否有 id 欄位
    if (!item.id) {
      alert(this.i18nService.translate('showlist.delete_missing_id'));
      return;
    }

    // 檢查是否已被廢止
    if (item.deleted) {
      alert(this.i18nService.translate('form.list.already_abolished'));
      return;
    }

    this.abolishingItem = item;
    this.abolishReason = ''; // 清空廢止原因
    this.showAbolishConfirm = true;
  }

  deleteItem(item: any) {
    console.log('Delete item:', item);

    // 檢查項目是否有 id 欄位（必須有才能刪除）
    if (!item.id) {
      alert(this.i18nService.translate('showlist.delete_missing_id'));
      return;
    }

    this.deletingItem = item;
    this.showDeleteConfirm = true;
  }

  // 新增：結案按鈕點擊處理
  closeItem(item: any) {
    console.log('Close item:', item);
    // 發射結案按鈕點擊事件給父元件處理
    this.closeButtonClicked.emit(item);
  }

  // 檢查是否應該顯示結案按鈕
  // 邏輯：第 4 關完成就顯示（無論是 4 關或 8 關）
  shouldShowCloseButton(item: any): boolean {
    try {
      const approvalField = this.findApprovalFlowField(item);
      if (!approvalField) return false;

      const steps = this.convertToApprovalFlowSteps(approvalField);
      if (steps.length < 4) return false; // 至少要有 4 關

      // 檢查第 4 關是否已核准
      const step4 = steps[3];
      return step4 && step4.status === 'approved';

    } catch (error) {
      console.error('檢查結案按鈕顯示條件錯誤:', error);
      return false;
    }
  }

  // 檢查結案按鈕是否應該 disabled
  // 邏輯：
  // 1. 如果已經是 8 關，表示驗收流程已啟動，按鈕 disabled
  // 2. 如果狀態是「已廢止」，按鈕 disabled
  isCloseButtonDisabled(item: any): boolean {
    try {
      // 檢查是否已廢止
      if (item.deleted === true) {
        return true;
      }

      const approvalField = this.findApprovalFlowField(item);
      if (!approvalField) return true;

      const steps = this.convertToApprovalFlowSteps(approvalField);
      // 如果有 8 關或更多，表示驗收流程已啟動
      return steps.length >= 8;

    } catch (error) {
      console.error('檢查結案按鈕 disabled 狀態錯誤:', error);
      return true;
    }
  }

  // 取得結案按鈕的 tooltip
  getCloseButtonTooltip(item: any): string {
    // 檢查是否已廢止
    if (item.deleted === true) {
      return '此申請單已廢止';
    }

    // 檢查是否已啟動驗收流程
    if (this.isCloseButtonDisabled(item)) {
      return '驗收流程已啟動';
    }

    return '施工完成，啟動驗收簽核';
  }

  /**
   * 🆕 取得自定義操作按鈕
   * 透過父元件傳入的 customActionButtons 函數來產生按鈕配置
   */
  getCustomButtons(item: any): CustomActionButton[] {
    if (!this.customActionButtons) {
      return [];
    }

    try {
      return this.customActionButtons(item);
    } catch (error) {
      console.error('取得自定義按鈕配置時發生錯誤:', error);
      return [];
    }
  }

  // 舊方法保留（可能其他地方有用到）
  isApprovalFlowCompleted(item: any): boolean {
    try {
      // 尋找簽核流程欄位
      const approvalField = this.findApprovalFlowField(item);
      if (!approvalField) {
        // 如果沒有簽核流程欄位，視為可以結案
        return true;
      }

      // 轉換為 ApprovalStep 格式
      const steps = this.convertToApprovalFlowSteps(approvalField);

      // 檢查所有步驟是否都已完成（狀態為 'approved'）
      return steps.length > 0 && steps.every(step => step.status === 'approved');

    } catch (error) {
      console.error(this.i18nService.translate('showlist.console.check_approval_flow_error'), error);
      // 出錯時保守做法：不允許結案
      return false;
    }
  }

  // 新增：找到簽核流程欄位
  private findApprovalFlowField(item: any): any {
    // 遍歷所有欄位，找到簽核流程相關的欄位
    for (const key in item) {
      if (item[key] && item[key].type &&
          (item[key].type === 'approval-flow' || item[key].type === 'approval-personnel-selector')) {
        return item[key];
      }
    }
    return null;
  }

  // 關閉編輯對話框
  closeEditDialog(): void {
    this.showEditDialog = false;
    this.editingItem = null;
  }

  // 關閉廢止確認對話框
  closeDisableConfirm(): void {
    this.showDisableConfirm = false;
    this.disablingItem = null;
    this.disableComment = ''; // 🆕 無効理由をリセット
  }

  // 關閉取り下げ確認對話框
  closeWithdrawConfirm(): void {
    this.showWithdrawConfirm = false;
    this.withdrawingItem = null;
  }

  // 關閉 Abolish 確認對話框
  closeAbolishConfirm(): void {
    this.showAbolishConfirm = false;
    this.abolishingItem = null;
    this.abolishReason = ''; // 清空廢止原因
  }

  // 關閉刪除確認對話框
  closeDeleteConfirm(): void {
    this.showDeleteConfirm = false;
    this.deletingItem = null;
  }

  // 確認廢止 Disable（軟刪除）
  confirmDisable(): void {
    if (!this.disablingItem) return;

    // 🆕 無効理由のバリデーション（承認コメントと同じパターン）
    if (!this.disableComment || this.disableComment.trim() === '') {
      alert(this.i18nService.translate('approval.reject.validation.reason_required'));
      return;
    }

    this.isLoading = true;

    // 呼叫 API 進行軟刪除
    const apiUrl = `${environment.apiUrl}/api/form-manager/delete_record`;
    const requestBody = {
      companyid: this.companyid,
      id: this.disablingItem.id,
      hardDelete: false,  // 關鍵：廢止使用軟刪除
      invalidationComment: this.disableComment.trim()
    };

    console.log('Disable request with comment:', requestBody);

    this.http.post(apiUrl, requestBody).subscribe({
      next: (response: any) => {
        console.log('Disable successful:', response);

        if (response.success) {
          // 關閉對話框
          this.closeDisableConfirm();

          // 重新載入資料
          this.reloadFormData();

          // 發射資料更新事件
          this.dataUpdated.emit();

          // 顯示成功訊息
          alert(this.i18nService.translate('form.list.disable_success'));
        } else {
          alert(this.i18nService.translate('form.list.disable_failed'));
        }

        this.isLoading = false;
      },
      error: (error) => {
        console.error('Disable error:', error);
        alert(this.i18nService.translate('form.list.disable_failed'));
        this.isLoading = false;
      }
    });
  }

  // 確認廢止 Abolish（軟刪除，帶廢止原因）
  async confirmAbolish(): Promise<void> {
    if (!this.abolishingItem) return;

    this.isLoading = true;

    try {
      // 取得廢止原因（如果沒填寫則設為「無」）
      const finalReason = this.abolishReason.trim() || '無';
      console.log('💾 [showlist] 廢止原因:', finalReason);

      // 直接在 delete_record API 中加入廢止原因參數
      // ⚠️ 後端 API 參數名稱是 invalidationComment，不是 abolishReason
      const deleteUrl = `${environment.apiUrl}/api/form-manager/delete_record`;
      const deleteBody = {
        companyid: this.companyid,
        id: this.abolishingItem.id,
        hardDelete: false,  // 關鍵：廢止使用軟刪除
        invalidationComment: finalReason  // 加入廢止原因（後端欄位名稱）
      };

      const deleteResponse: any = await firstValueFrom(this.http.post(deleteUrl, deleteBody));
      console.log('Abolish successful:', deleteResponse);

      if (deleteResponse.success) {
        // 將廢止原因加到項目中，以便父元件使用
        this.abolishingItem.abolishReason = finalReason;

        // 發射項目廢止事件，讓父元件知道哪個項目被廢止了
        console.log('📢 [showlist] 發射 itemAbolished 事件，項目:', this.abolishingItem);
        this.itemAbolished.emit(this.abolishingItem);

        // 關閉對話框
        this.closeAbolishConfirm();

        // 重新載入資料以保持篩選條件
        this.reloadFormData();

        // 發射資料更新事件
        this.dataUpdated.emit();

        // 顯示成功訊息
        alert(this.i18nService.translate('form.list.abolish_success'));
      } else {
        alert(this.i18nService.translate('form.list.abolish_failed'));
      }

      this.isLoading = false;
    } catch (error) {
      console.error('Abolish error:', error);
      alert(this.i18nService.translate('form.list.abolish_failed'));
      this.isLoading = false;
    }
  }

  // 確認取り下げ（申請を下書きに戻す）
  confirmWithdraw(): void {
    if (!this.withdrawingItem) return;

    console.log('🔍 [confirmWithdraw] 開始撤回流程');
    console.log('🔍 [confirmWithdraw] withdrawingItem 完整資料:', this.withdrawingItem);

    // 🔧 支援多種命名格式：createdBy (駝峰) 或 created_by (底線)
    const itemCreator = this.withdrawingItem.createdBy || this.withdrawingItem.created_by || this.withdrawingItem.createby;
    const isCreator = itemCreator === this.currentUserId;
    const isAdmin = this.hasAdminPermission();

    console.log('🔍 [confirmWithdraw] withdrawingItem.createdBy (駝峰):', this.withdrawingItem.createdBy);
    console.log('🔍 [confirmWithdraw] withdrawingItem.created_by (底線):', this.withdrawingItem.created_by);
    console.log('🔍 [confirmWithdraw] withdrawingItem.createby (無底線):', this.withdrawingItem.createby);
    console.log('🔍 [confirmWithdraw] 最終判定的申請者:', itemCreator);
    console.log('🔍 [confirmWithdraw] currentUserId (當前使用者):', this.currentUserId);
    console.log('🔍 [confirmWithdraw] localStorage.userid:', localStorage.getItem('userid'));

    // 🔍 DEBUG: 權限檢查分析
    console.log('� [confirmWithdraw] 權限檢查分析:', {
      'itemCreator': itemCreator,
      'currentUserId': this.currentUserId,
      'isCreator (是否為建立者)': isCreator,
      'isAdmin (是否為管理員)': isAdmin,
      'hasPermission (是否有權限)': isCreator || isAdmin
    });

    this.isLoading = true;

    // 呼叫 withdraw_to_draft API
    const apiUrl = `${environment.apiUrl}/api/form-manager/withdraw_to_draft`;
    const requestBody = {
      id: this.withdrawingItem.id,
      requesterId: this.currentUserId
    };

    console.log('📤 [confirmWithdraw] 發送撤回請求:', requestBody);

    this.http.post(apiUrl, requestBody).subscribe({
      next: (response: any) => {
        console.log('✅ [confirmWithdraw] 撤回 API 回應成功:', response);

        if (response.success) {
          // 顯示成功訊息（優先使用API返回的message）
          alert(response.message || this.i18nService.translate('form.list.withdraw_success'));

          // 關閉對話框
          this.closeWithdrawConfirm();

          // 重新載入資料
          this.reloadFormData();

          // 發射資料更新事件
          this.dataUpdated.emit();
        } else {
          console.error('❌ [confirmWithdraw] API 回應失敗:', response);
          alert(response.message || this.i18nService.translate('form.list.withdraw_failed'));

          // 🔧 修正：失敗時也要關閉對話框
          this.closeWithdrawConfirm();
        }

        this.isLoading = false;
      },
      error: (error) => {
        console.error('❌ [confirmWithdraw] 撤回 API 錯誤:', error);
        console.error('❌ [confirmWithdraw] 錯誤詳細資訊:', error.error);

        // エラーメッセージの処理
        let errorMessage = this.i18nService.translate('form.list.withdraw_failed');
        if (error.error?.message) {
          errorMessage = error.error.message;
        }

        alert(errorMessage);

        // 🔧 修正：錯誤時也要關閉對話框
        this.closeWithdrawConfirm();
        this.isLoading = false;
      }
    });
  }

  // 確認刪除（真刪除）
  confirmDelete(): void {
    if (!this.deletingItem) return;

    this.isLoading = true;

    // 呼叫 API 進行真刪除
    const apiUrl = `${environment.apiUrl}/api/form-manager/delete_record`;
    const requestBody = {
      companyid: this.companyid,
      id: this.deletingItem.id,
      hardDelete: true  // 刪除使用真刪除
    };

    this.http.post(apiUrl, requestBody).subscribe({
      next: (response: any) => {
        console.log('Delete successful:', response);

        if (response.success) {
          // 關閉對話框
          this.closeDeleteConfirm();

          // 重新載入資料以保持篩選條件
          this.reloadFormData();

          // 發射資料更新事件
          this.dataUpdated.emit();

          // 顯示成功訊息
          alert(response.message || this.i18nService.translate('showlist.delete_success'));
        } else {
          console.error(this.i18nService.translate('showlist.console.api_response_failed') + ':', response);
          alert(response.message || this.i18nService.translate('showlist.delete_failed'));
        }
      },
      error: (error) => {
        console.error('Delete failed:', error);

        // 根據錯誤狀態碼顯示適當的錯誤訊息
        let errorMessage = this.i18nService.translate('showlist.delete_failed');
        if (error.status === 400) {
          errorMessage = this.i18nService.translate('showlist.update_error_bad_request');
        } else if (error.status === 404) {
          errorMessage = this.i18nService.translate('showlist.update_error_not_found');
        } else if (error.status === 403) {
          errorMessage = this.i18nService.translate('showlist.update_error_forbidden');
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }

        alert(errorMessage);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  isObject(val: any): boolean {
    return val !== null && typeof val === 'object' && !Array.isArray(val);
  }



  // 處理簽核流程顯示
  parseApprovalFlow(approvalFlowValue: any): any[] {
    if (!approvalFlowValue) return [];

    try {
      // 如果已經是陣列，直接返回
      if (Array.isArray(approvalFlowValue)) {
        return approvalFlowValue;
      }

      // 如果是字串，嘗試解析 JSON
      if (typeof approvalFlowValue === 'string') {
        return JSON.parse(approvalFlowValue);
      }

      // 如果是物件且有 value 屬性 - 模仿 form-approval-list 的處理邏輯
      if (approvalFlowValue.value) {
        const approvalData = approvalFlowValue.value;

        // 檢查雙層巢狀結構 (approvalData.value?.steps) - 這是關鍵修復
        if (approvalData.value?.steps) {
          return approvalData.value.steps;
        }
        // 檢查單層結構 (approvalData.steps)
        else if (approvalData.steps) {
          return approvalData.steps;
        }
        // 如果 approvalData 直接是陣列
        else if (Array.isArray(approvalData)) {
          return approvalData;
        }
        // 如果 approvalFlowValue.value 是字串，嘗試解析
        else if (typeof approvalData === 'string') {
          return JSON.parse(approvalData);
        }
      }

      return [];
    } catch (error) {
      console.error(this.i18nService.translate('showlist.console.parse_approval_flow_error'), error);
      return [];
    }
  }

  // 取得簽核步驟的狀態顏色
  getApprovalStepColor(step: any): string {
    if (!step) return '#e9ecef'; // 預設灰色

    const status = step.status || 'pending';

    switch (status.toLowerCase()) {
      case 'approved':
        return '#28a745'; // 綠色 - 已核准
      case 'rejected':
        return '#dc3545'; // 紅色 - 已拒絕
      case 'in_progress':
        return '#ffc107'; // 黃色 - 進行中
      case 'pending':
      default:
        return '#6c757d'; // 灰色 - 待處理
    }
  }

  // 取得簽核步驟的狀態文字
  getApprovalStepStatusText(step: any): string {
    if (!step) return this.i18nService.translate('approval.status.unknown');

    const status = step.status || 'pending';

    switch (status.toLowerCase()) {
      case 'approved':
        return this.i18nService.translate('approval.status.approved');
      case 'rejected':
        return this.i18nService.translate('approval.status.rejected');
      case 'in_progress':
        return this.i18nService.translate('approval.status.in_progress');
      case 'pending':
      default:
        return this.i18nService.translate('approval.status.pending');
    }
  }

  // 建立簽核步驟的 tooltip 內容
  getApprovalStepTooltip(step: any): string {
    if (!step) return '';

    const stepName = step.stepName || step.name || this.i18nService.translate('approval.step.unnamed_step');
    const approverName = step.approverName || step.userName || step.approver?.userName || this.i18nService.translate('approval.step.unspecified_approver');
    const status = this.getApprovalStepStatusText(step);
    const actionDate = step.actionDate ? new Date(step.actionDate).toLocaleString() : '';

    let tooltip = `${this.i18nService.translate('approval.step.name')}: ${stepName}\n${this.i18nService.translate('approval.step.approver')}: ${approverName}\n${this.i18nService.translate('approval.step.status')}: ${status}`;

    if (actionDate) {
      tooltip += `\n${this.i18nService.translate('approval.step.process_time')}: ${actionDate}`;
    }

    if (step.comment) {
      tooltip += `\n${this.i18nService.translate('approval.step.comment')}: ${step.comment}`;
    }

    return tooltip;
  }

  /**
   * 將原始簽核流程資料轉換為 ApprovalFlowDisplayComponent 需要的格式
   */
  convertToApprovalFlowSteps(approvalFlowValue: any): ApprovalStep[] {
    const rawSteps = this.parseApprovalFlow(approvalFlowValue);

    if (!rawSteps || rawSteps.length === 0) {
      return [];
    }

    return rawSteps.map((step, index) => {
      // 決定步驟狀態 - 根據圖片中的顏色規則
      let status: 'pending' | 'in_progress' | 'approved' | 'rejected' = 'pending';

      if (step.status) {
        // 如果有明確的狀態，使用它
        status = step.status;
      } else {
        // 預設邏輯：第一步設為進行中（黃色），其他為等待中（灰色）
        if (index === 0) {
          status = 'in_progress';
        } else {
          status = 'pending';
        }
      }

      // 處理簽核者名稱 - 支援多種格式
      let approverName = '無指定人員';
      if (step.approverName) {
        approverName = step.approverName;
      } else if (step.userName) {
        approverName = step.userName;
      } else if (step.approver?.userName) {
        approverName = step.approver.userName;
      } else if (step.approverList && Array.isArray(step.approverList) && step.approverList.length > 0) {
        // 處理簽核者列表
        approverName = step.approverList.map((approver: any) => approver.name || approver.id).join(', ');
      }

      return {
        stepDefinitionId: step.stepDefinitionId || "",
        level:step.level || 0,
        stepName: step.stepName,
        approverName: approverName,
        status: status,
        comment: step.comment,
        actionDate: step.actionDate
      };
    });
  }

  /**
   * 取得簽核流程原始資料
   */
  getApprovalFlowData(approvalFlowValue: any): any[] {
    return this.parseApprovalFlow(approvalFlowValue);
  }

  /**
   * 簽核完成回調
   */
  onApprovalComplete(approvalInstance: any): void {
    console.log(this.i18nService.translate('showlist.console.approval_completed'), approvalInstance);

    // 取得表單資料並包含在事件中
    const formId = approvalInstance.data?.id || approvalInstance.formId;
    const formData = this.getFormDataById(formId);

    // 準備包含表單資料的完整事件資料
    const completeEventData = {
      ...approvalInstance,
      formData: formData,
      hasFormData: !!formData,
      formId: formId
    };

    console.log('📦 ' + this.i18nService.translate('showlist.console.emit_approval_completed_with_form_data') + ':', formId, this.i18nService.translate('showlist.console.form_data_exists') + ':', !!formData);

    // 發出通用簽核完成事件給父元件處理
    this.approvalCompleted.emit(completeEventData);

    // 只有當 enableConstructionNotification 為 true 時才執行舊的施工通知邏輯（向下相容）
    if (this.enableConstructionNotification) {
      console.log('✅ ' + this.i18nService.translate('showlist.console.construction_notification_enabled'));
      this.checkAndTriggerConstructionNotification(approvalInstance);
    } else {
      console.log('⚠️ ' + this.i18nService.translate('showlist.console.construction_notification_disabled_parent_handle'));
    }

    // 重新載入資料以更新狀態
    this.loadData();

    // 發出資料更新事件
    this.dataUpdated.emit();
  }

  /**
   * 檢查並觸發施工通知（當最後一關簽核完成時）
   */
  private checkAndTriggerConstructionNotification(approvalInstance: any): void {

    // 檢查是否啟用施工通知功能
    if (!this.enableConstructionNotification) {
      console.log(this.i18nService.translate('showlist.console.construction_notification_disabled'));
      return;
    }


    // 檢查是否為最後一關簽核完成
    const isLastStage = this.isLastApprovalStageCompleted(approvalInstance);

    if (isLastStage) {
      console.log(this.i18nService.translate('showlist.console.last_stage_approval_completed'));

      // 發出事件給父元件處理（可選）
      const formId = approvalInstance.data?.id || approvalInstance.formId;
      this.beforeConstructionNotification.emit({
        approvalInstance,
        formData: this.getFormDataById(formId),
        formType: this.formName
      });

      // 觸發施工通知
      this.triggerConstructionNotification(approvalInstance);
    } else {
    }
  }

  /**
   * 檢查是否為最後一關簽核完成
   */
  private isLastApprovalStageCompleted(approvalInstance: any): boolean {
    try {

      // 檢查是否為API成功回應
      if (!approvalInstance.success || !approvalInstance.data) {
        return false;
      }

      const data = approvalInstance.data;

      // 檢查是否有簽核步驟相關資訊
      if (!data.totalSteps) {
        return false;
      }

      console.log('   - ' + this.i18nService.translate('showlist.console.total_steps') + ':', data.totalSteps);
      console.log('   - ' + this.i18nService.translate('showlist.console.completed_step') + ':', data.updatedStepName);
      console.log('   - ' + this.i18nService.translate('showlist.console.next_step') + ':', data.nextStepName);

      // 判斷是否為最後一關：如果沒有下一個步驟，就是最後一關
      const isLastStage = !data.nextStepName || data.nextStepName === null || data.nextStepName === '';


      if (isLastStage) {
        return true;
      } else {
        return false;
      }

    } catch (error) {
      console.error(this.i18nService.translate('showlist.console.check_last_approval_step_error'), error);
      return false;
    }
  }

  /**
   * 觸發施工通知
   */
  private triggerConstructionNotification(approvalInstance: any): void {
    try {
      // 取得表單ID
      const formId = approvalInstance.data?.id || approvalInstance.formId;

      // 取得表單資料
      const formData = this.getFormDataById(formId);

      if (!formData) {
        console.error(this.i18nService.translate('showlist.console.cannot_get_form_data'));
        return;
      }

      // 顯示確認對話框
      const message = '施工申請已獲核准，是否立即通知相關人員可以開始施工？';

      if (confirm(message)) {
        this.sendConstructionNotification(formData, approvalInstance);
      }
    } catch (error) {
      console.error(this.i18nService.translate('showlist.console.trigger_construction_notification_error'), error);
    }
  }

  /**
   * 發送施工通知
   */
  private async sendConstructionNotification(formData: any, approvalInstance: any): Promise<void> {
    console.log('🚀 ===== ' + this.i18nService.translate('showlist.console.start_sending_construction_notification') + ' =====');
    console.log('📋 ' + this.i18nService.translate('showlist.console.original_form_data') + ':', formData);
    console.log('✅ ' + this.i18nService.translate('showlist.console.approval_instance') + ':', approvalInstance);

    // 解析承攬商公司資料
    const contractorCompanyData = this.extractContractorCompanyData(formData);
    console.log('🏢 ===== ' + this.i18nService.translate('showlist.console.contractor_company_data') + ' =====');
    console.log(this.i18nService.translate('showlist.console.company_code'), contractorCompanyData.companyKey);
    console.log(this.i18nService.translate('showlist.console.company_name'), contractorCompanyData.companyName);
    console.log(this.i18nService.translate('showlist.console.contractor_data_object'), contractorCompanyData);

    // 解析工作人員資料
    const workersData = this.extractWorkersData(formData);
    console.log('👷 ===== ' + this.i18nService.translate('showlist.console.workers_list') + ' =====');
    console.log(this.i18nService.translate('showlist.console.workers_total'), workersData.length);

    workersData.forEach((worker, index) => {
      console.log(`👷‍♂️ ${this.i18nService.translate('showlist.console.worker_info', {index: index + 1})}:`);
      console.log('  - ' + this.i18nService.translate('showlist.console.worker_name'), worker.name);
      console.log('  - ' + this.i18nService.translate('showlist.console.worker_phone'), worker.phone);
      console.log('  - ' + this.i18nService.translate('showlist.console.worker_is_safety_personnel'), worker.isSafetyPersonnel ? this.i18nService.translate('showlist.console.yes') : this.i18nService.translate('showlist.console.no'));
      console.log('  - ' + this.i18nService.translate('showlist.console.contractor_company'), worker.contractorCompanyName);
      console.log('  - ' + this.i18nService.translate('showlist.console.complete_data'), worker);
    });

    // 解析施工期間
    const workPeriod = this.extractWorkPeriod(formData);
    console.log('📅 ===== ' + this.i18nService.translate('showlist.console.construction_period') + ' =====');
    console.log(this.i18nService.translate('showlist.console.start_date'), workPeriod.startDate);
    console.log(this.i18nService.translate('showlist.console.end_date'), workPeriod.endDate);
    console.log(this.i18nService.translate('showlist.console.period_data'), workPeriod);

    // 構建通知資料
    const constructionInfo = {
      // 承攬商公司資訊
      contractorCompany: contractorCompanyData,

      // 工作人員資訊
      workers: workersData,

      // 表單基本資訊
      formId: formData.id || '',
      workOrderId: formData.id || '',
      assignmentDate: new Date().toLocaleDateString('zh-TW'),
      constructionFormName: this.formName || '施工申請單',
      systemUrl: window.location.origin,

      // 施工期間
      workPeriod: workPeriod
    };

    console.log('📦 ===== ' + this.i18nService.translate('showlist.console.complete_notification_data_package') + ' =====');
    console.log(this.i18nService.translate('showlist.console.construction_info_complete_object') + ':', constructionInfo);

    // 額外的原始資料檢查
    console.log('🔍 ===== ' + this.i18nService.translate('showlist.console.original_data_field_check') + ' =====');
    console.log(this.i18nService.translate('showlist.console.form_data_all_fields') + ':', Object.keys(formData));
    console.log(this.i18nService.translate('showlist.console.form_data_complete_content') + ':', formData);

    // 搜尋可能的承攬商公司欄位
    console.log('🔍 ' + this.i18nService.translate('showlist.console.search_select_fields') + ':');
    Object.keys(formData).forEach(key => {
      if (key.toLowerCase().includes('select') || key.toLowerCase().includes('company') || key.toLowerCase().includes('contractor')) {
        console.log(`  - ${key}:`, formData[key]);
      }
    });

    // 搜尋可能的工作人員表格欄位
    console.log('🔍 ' + this.i18nService.translate('showlist.console.search_table_fields') + ':');
    Object.keys(formData).forEach(key => {
      if (key.toLowerCase().includes('table') || key.toLowerCase().includes('worker') || key.toLowerCase().includes('staff')) {
        console.log(`  - ${key}:`, formData[key]);
      }
    });

    // 搜尋可能的日期範圍欄位
    console.log('🔍 ' + this.i18nService.translate('showlist.console.search_date_range_fields') + ':');
    Object.keys(formData).forEach(key => {
      if (key.toLowerCase().includes('date') || key.toLowerCase().includes('range') || key.toLowerCase().includes('period')) {
        console.log(`  - ${key}:`, formData[key]);
      }
    });

    console.log('formData["dynamic_api-select_1"]:', formData['dynamic_api-select_1']);
    console.log('formData.dynamic_api_table_1:', formData.dynamic_api_table_1);
    console.log('formData.date_range_1:', formData.date_range_1);

    // 開始取得信箱資料
    console.log('📧 ===== ' + this.i18nService.translate('showlist.console.start_get_mailbox_data') + ' =====');

    try {
      // 同時呼叫兩個 API 取得信箱資料
      const [contractorEmail, personnelDetails] = await Promise.all([
        this.getContractorContactEmail(contractorCompanyData.companyKey),
        this.getPersonnelDetails(workersData.map(w => w.name))
      ]);

      console.log('📧 ' + this.i18nService.translate('showlist.console.contractor_contact_email') + ':', contractorEmail);
      console.log('📧 ' + this.i18nService.translate('showlist.console.worker_details') + ':', personnelDetails);

      // 整合所有信箱資料 (分類為 TO/CC)
      const emailData = this.consolidateEmailData(contractorEmail, personnelDetails);
      console.log('📧 ' + this.i18nService.translate('showlist.console.consolidated_email_data') + ':', emailData);

      // 發送通知 (支援 TO/CC 分類，含 QR Code)
      await this.sendNotificationEmails(constructionInfo, emailData);

    } catch (error) {
      console.error('❌ ' + this.i18nService.translate('showlist.console.get_mailbox_data_error') + ':', error);
      alert(this.i18nService.translate('showlist.get_mailbox_data_failed') + ': ' + (error as Error).message);
    }

    console.log('🚀 ===== ' + this.i18nService.translate('showlist.console.construction_notification_process_complete') + ' =====');
  }

  /**
   * 解析承攬商公司資料
   */
  private extractContractorCompanyData(formData: any): any {
    // 修正欄位名稱：使用短橫線而非下底線
    const dynamicApiSelect = formData['dynamic_api-select_1'];

    if (dynamicApiSelect && dynamicApiSelect.value) {
      return {
        companyKey: dynamicApiSelect.value.key,        // 公司資料代碼
        companyName: dynamicApiSelect.value.value      // 公司名稱
      };
    }

    return {
      companyKey: '',
      companyName: ''
    };
  }

  /**
   * 解析工作人員資料
   */
  private extractWorkersData(formData: any): any[] {
    const dynamicApiTable = formData.dynamic_api_table_1;

    if (dynamicApiTable && dynamicApiTable.value && Array.isArray(dynamicApiTable.value)) {
      return dynamicApiTable.value.map((worker: any) => ({
        name: worker.姓名 || '',
        phone: worker.電話 || '',
        isSafetyPersonnel: worker.工安人員 === '是',
        contractorCompanyName: worker.承攬商公司名稱 || ''
      }));
    }

    return [];
  }

  /**
   * 解析施工期間
   */
  private extractWorkPeriod(formData: any): any {
    const dateRange = formData.date_range_1;

    if (dateRange && dateRange.value) {
      return {
        startDate: dateRange.value.startDate || '',
        endDate: dateRange.value.endDate || ''
      };
    }

    return {
      startDate: '',
      endDate: ''
    };
  }

  /**
   * 根據表單 ID 取得表單資料
   */
  private getFormDataById(formId: string): any {
    if (!this.data || !Array.isArray(this.data)) {
      return null;
    }

    return this.data.find(item => item.id === formId);
  }

  // 載入原始表單配置
  loadOriginalFormConfig(): void {
    this.formConfigService.getFormConfig(this.companyid, this.formId, this.formName)
      .subscribe({
        next: (config: FormFieldConfig[]) => {
          this.originalFormConfig = config;
          console.log('Original form config loaded:', this.originalFormConfig);
        },
        error: (error) => {
          console.error('Failed to load original form config:', error);
          this.originalFormConfig = [];
        }
      });
  }

  /**
檢視事   * 預載入所有子表單的資料筆數（優化版：使用進階批次 API）
   */
  private preloadSubformRecordCounts(): void {
    if (!this.data || this.data.length === 0) return;

    // 收集所有獨特的 (formid, parent_formid) 查詢組合
    const queries: Array<{ formid: string; parent_formid: string }> = [];
    const queryKeys = new Set<string>(); // 用於去重

    // 找出所有 subform-relation 類型的欄位和對應的父記錄
    this.data.forEach(item => {
      this.displayedColumns.forEach(column => {
        const value = item[column];
        if (value && typeof value === 'object' && value.type === 'subform-relation' && value.value && item.id) {
          // 🔧 取得實際的 formid（可能在 value.value 或 value.value.originalValue）
          let formid: string;

          if (typeof value.value === 'string') {
            // 情況 1: value.value 是字串
            formid = value.value;
          } else if (value.value && typeof value.value === 'object' && value.value.originalValue) {
            // 情況 2: value.value 是物件，實際值在 originalValue
            formid = value.value.originalValue;
          } else {
            // 無法取得有效的 formid，跳過
            console.warn('[批次預載入] 無法取得 formid，跳過:', value.value);
            return;
          }

          const parent_formid = item.id;
          const queryKey = `${formid}|${parent_formid}`;

          // 避免重複查詢
          if (!queryKeys.has(queryKey)) {
            queryKeys.add(queryKey);
            queries.push({ formid, parent_formid });
          }
        }
      });
    });

    if (queries.length === 0) {
      console.log('[批次預載入] 沒有子表單欄位需要載入');
      return;
    }

    console.log(`[批次預載入] 收集到 ${queries.length} 個獨特的查詢組合，準備一次性批次載入`);

    // 一次性載入所有查詢
    this.loadAllSubformRecordCountsAdvanced(queries);
  }

  // 生成編輯表單配置
  generateEditFormConfig(editingItem: any): FormFieldConfig[] {
    if (!this.originalFormConfig || this.originalFormConfig.length === 0) {
      console.warn('Original form config not available, using fallback');
      return this.generateFallbackFormConfig(editingItem);
    }

    const editableColumns = this.getEditableColumns();
    const editConfig: FormFieldConfig[] = [];

    editableColumns.forEach(column => {
      // 在原始配置中尋找對應的欄位
      const originalField = this.originalFormConfig.find(field =>
        field.name === column || field.id === column
      );

      if (originalField) {
        // 使用原始配置建立編輯欄位
        const editField: FormFieldConfig = {
          ...originalField,
          // 確保編輯時的必要屬性
          id: `edit_${column}`,
          name: column
        };

        // 為 select 和 multi-checkbox 確保選項被正確傳遞
        // 選項應該來自原始表單配置，而不是資料記錄
        if (originalField.type === 'select' || originalField.type === 'multi-checkbox') {
          if (originalField.options && Array.isArray(originalField.options)) {
            editField.options = originalField.options;
            console.log(`✅ ` + this.i18nService.translate('showlist.console.set_form_config_options', {column: column}) + `:`, editField.options.length, this.i18nService.translate('showlist.console.options_count'));
          } else {
            console.warn(`⚠️ ` + this.i18nService.translate('showlist.console.missing_options_in_config', {column: column}));
          }
        }

        // 為欄位設定初始值
        this.setInitialValueForField(editField, editingItem[column]);

        editConfig.push(editField);
      } else {
        // 如果在原始配置中找不到，建立基本的文字輸入欄位
        const basicField: FormFieldConfig = {
          type: 'text-input',
          label: this.getColumnDisplayName(column),
          id: `edit_${column}`,
          name: column,
          placeholder: `請輸入${this.getColumnDisplayName(column)}`
        };

        // 為基本欄位設定初始值
        this.setInitialValueForField(basicField, editingItem[column]);

        editConfig.push(basicField);
      }
    });

    return editConfig;
  }

  // 為欄位設定初始值
  setInitialValueForField(field: FormFieldConfig, value: any): void {
    // 添加 initialValue 屬性來攜帶初始值
    (field as any).initialValue = '';

    if (value && typeof value === 'object' && value.value !== undefined) {
      // 處理雙層嵌套情況
      let actualValue = value.value;

      // 如果 value.value 也是一個包含 type 和 value 的物件，則進一步展開
      // 但要排除 select/multi-checkbox 的 key-value 結構，避免錯誤展開
      if (actualValue && typeof actualValue === 'object' && actualValue.value !== undefined) {
        // 檢查是否是 key-value 結構（有 key 屬性的是選項資料，不應該進一步展開）
        if (!actualValue.key) {
          actualValue = actualValue.value;
        }
      }

      // 處理具有類型的複雜資料結構
      if (value.type === 'datetime' && actualValue) {
        // 將 Unix 時間戳轉換為適當的格式
        const timestamp = typeof actualValue === 'number' ? actualValue : Number(actualValue);
        if (!isNaN(timestamp)) {
          (field as any).initialValue = timestamp;
        } else {
          (field as any).initialValue = actualValue;
        }
      } else if (value.type === 'select' && actualValue) {
        // 下拉選單：處理 key-value 結構
        console.log(`🔍 ` + this.i18nService.translate('showlist.console.dropdown_actual_value_type', {field: field.name}) + `:`, typeof actualValue, actualValue);
        if (typeof actualValue === 'object' && actualValue.key) {
          // 資料庫中的格式：{ key: "option_3", value: "製造業" }
          (field as any).initialValue = actualValue.key;
          console.log(`✅ ` + this.i18nService.translate('showlist.console.dropdown_set_key_initial_value', {field: field.name}) + `:`, actualValue.key);
        } else {
          // 簡單字串值 - 這裡不應該進入，如果進入了說明資料準備有問題
          (field as any).initialValue = actualValue;
          console.log(`❌ ` + this.i18nService.translate('showlist.console.dropdown_set_string_initial_value', {field: field.name}) + `:`, actualValue);
        }
        // 確保選項被正確傳遞（優先使用原始表單配置中的選項）
        if (value.options && Array.isArray(value.options)) {
          field.options = value.options;
        }
      } else if (value.type === 'multi-checkbox' && actualValue) {
        // 多選框：處理複雜的選項結構
        if (Array.isArray(actualValue)) {
          // 提取所有 selected: true 的選項的 key
          const selectedKeys = actualValue
            .filter((item: any) => item.selected === true)
            .map((item: any) => item.option?.key || item.key)
            .filter((key: string) => key);
          (field as any).initialValue = selectedKeys;
          console.log(`✅ ` + this.i18nService.translate('showlist.console.checkbox_set_selected_keys_initial_value', {field: field.name}) + `:`, selectedKeys);
        } else if (typeof actualValue === 'string') {
          // 如果是逗號分隔的字串，轉換為陣列
          (field as any).initialValue = actualValue.split(',').map((v: string) => v.trim()).filter((v: string) => v);
          console.log(this.i18nService.translate('showlist.console.checkbox_set_string_array_initial_value', {field: field.name}) + ':', (field as any).initialValue);
        } else {
          (field as any).initialValue = actualValue;
          console.log(this.i18nService.translate('showlist.console.checkbox_set_original_initial_value', {field: field.name}) + ':', actualValue);
        }
        // 確保選項被正確傳遞（優先使用原始表單配置中的選項）
        if (value.options && Array.isArray(value.options)) {
          field.options = value.options;
        }
      } else if (value.type === 'radiobutton' && actualValue) {
        // 單選按鈕：處理 key-value 結構
        console.log(`🔍 ` + this.i18nService.translate('showlist.console.radiobutton_actual_value_type', {field: field.name}) + `:`, typeof actualValue, actualValue);
        if (typeof actualValue === 'object' && actualValue.key) {
          // 資料庫中的格式：{ key: "option_1", value: "選項一" }
          (field as any).initialValue = actualValue.key;
          console.log(`✅ ` + this.i18nService.translate('showlist.console.radiobutton_set_key_initial_value', {field: field.name}) + `:`, actualValue.key);
        } else {
          // 簡單字串值
          (field as any).initialValue = actualValue;
          console.log(this.i18nService.translate('showlist.console.radiobutton_set_string_initial_value', {field: field.name}) + `:`, actualValue);
        }
      } else if (value.type === 'subform-relation' || value.type === 'approval-flow' || value.type === 'approval-personnel-selector') {
        // 子表單和簽核流程的特殊處理，直接使用整個值
        (field as any).initialValue = actualValue || value.value;
      } else {
        (field as any).initialValue = actualValue;
      }
    } else {
      // 簡單值直接使用
      (field as any).initialValue = value || '';
    }

    console.log(this.i18nService.translate('showlist.console.set_field_initial_value', {field: field.name}) + ':', (field as any).initialValue);
  }

  // 當原始配置不可用時的後備配置
  generateFallbackFormConfig(editingItem: any): FormFieldConfig[] {
    const editableColumns = this.getEditableColumns();
    const fallbackConfig: FormFieldConfig[] = [];

    editableColumns.forEach(column => {
      const value = editingItem[column];
      let fieldType: FormFieldConfig['type'] = 'text-input';

      // 根據資料結構推測欄位類型
      if (value && typeof value === 'object' && value.type) {
        switch (value.type) {
          case 'textarea':
            fieldType = 'textarea';
            break;
          case 'select':
            fieldType = 'select';
            break;
          case 'date':
            fieldType = 'date';
            break;
          case 'datetime':
            fieldType = 'datetime';
            break;
          case 'number-input':
            fieldType = 'number-input';
            break;
          case 'location':
            fieldType = 'location';
            break;
          case 'photo-upload':
            fieldType = 'photo-upload';
            break;
          case 'signature':
            fieldType = 'signature';
            break;
          case 'multi-checkbox':
            fieldType = 'multi-checkbox';
            break;
          default:
            fieldType = 'text-input';
        }
      }

      const fieldConfig: FormFieldConfig = {
        type: fieldType,
        label: this.getColumnDisplayName(column),
        id: `edit_${column}`,
        name: column,
        placeholder: `請輸入${this.getColumnDisplayName(column)}`
      };

      // 如果是選擇類型，添加選項
      if (value && value.options) {
        fieldConfig.options = value.options;
      }

      fallbackConfig.push(fieldConfig);
    });

    return fallbackConfig;
  }

  // 準備編輯的初始資料
  prepareInitialData(editingItem: any): any {

    const initialData: any = {};
    const editableColumns = this.getEditableColumns();

    editableColumns.forEach(column => {
      const value = editingItem[column];

      if (value && typeof value === 'object' && value.value !== undefined) {
        // 處理雙層嵌套情況
        let actualValue = value.value;

        // 如果 value.value 也是一個包含 type 和 value 的物件，則進一步展開
        // 但要排除 select/multi-checkbox 的 key-value 結構，避免錯誤展開
        if (actualValue && typeof actualValue === 'object' && actualValue.value !== undefined) {
          // 檢查是否是 key-value 結構（有 key 屬性的是選項資料，不應該進一步展開）
          if (!actualValue.key) {
            actualValue = actualValue.value;
          }
        }

        // 處理具有類型的複雜資料結構
        if (value.type === 'datetime' && actualValue) {
          // 將 Unix 時間戳轉換為適當的格式
          const timestamp = typeof actualValue === 'number' ? actualValue : Number(actualValue);
          if (!isNaN(timestamp)) {
            initialData[column] = timestamp;
          } else {
            initialData[column] = actualValue;
          }
        } else if (value.type === 'select') {
          // 下拉選單：處理資料庫中的 key-value 結構
          console.log(`🔍 ` + this.i18nService.translate('showlist.console.prepare_initial_data_select_actual_value', {column: column}) + `:`, typeof actualValue, actualValue);
          if (actualValue && typeof actualValue === 'object' && actualValue.key) {
            // 確保 key 值存在於表單配置的選項中
            const formField = this.originalFormConfig?.find((field: any) => field.name === column);
            if (formField && formField.options && Array.isArray(formField.options)) {
              const fieldOptions = formField.options; // 創建本地變數避免 TypeScript 警告
              // 嘗試透過 key 匹配 (key 可能是 value 或 label)
              let matchingOption = fieldOptions.find((opt: any) => opt.value === actualValue.key);
              if (!matchingOption) {
                // 如果透過 value 找不到，嘗試透過 label 找
                matchingOption = fieldOptions.find((opt: any) => opt.label === actualValue.key);
              }

              if (matchingOption) {
                initialData[column] = matchingOption.value; // 使用表單配置中的 value
                console.log(`✅ ` + this.i18nService.translate('showlist.console.dropdown_found_matching_option', {column: column}) + `:`, matchingOption.value);
              } else {
                console.warn(`⚠️ ` + this.i18nService.translate('showlist.console.dropdown_key_not_found_in_config', {column: column, key: actualValue.key}));
                initialData[column] = actualValue.key; // 仍然使用，讓前端處理
              }
            } else {
              initialData[column] = actualValue.key;
              console.log(this.i18nService.translate('showlist.console.dropdown_use_key_no_config_check', {column: column}) + `:`, actualValue.key);
            }
          } else {
            initialData[column] = actualValue || value.value || '';
            console.log(this.i18nService.translate('showlist.console.dropdown_use_value', {column: column}) + `:`, actualValue);
          }
        } else if (value.type === 'multi-checkbox') {
          // 多選框：保留完整的資料結構，包括 textValue
          if (Array.isArray(actualValue)) {

            // 提取所有 selected: true 的選項，並保留完整資料
            const selectedItems = actualValue
              .filter((item: any) => item.selected === true)
              .map((item: any) => {
                // 提取 option 的 key（可能是巢狀的 {key, value} 或直接的字串）
                let optionKey = '';
                if (item.option && typeof item.option === 'object' && item.option.key) {
                  optionKey = item.option.key;
                } else if (typeof item.option === 'string') {
                  optionKey = item.option;
                } else if (item.key) {
                  optionKey = item.key;
                }

                // 建立包含完整資料的物件，特別保留 textValue
                const result = {
                  option: optionKey,
                  selected: true,
                  textValue: item.textValue !== undefined ? item.textValue : ''  // 保留 textValue，即使是空字串
                };

                return result;
              })
              .filter((item: any) => item.option); // 過濾掉沒有 option 的項目

            console.log(`✅ ` + this.i18nService.translate('showlist.console.checkbox_retain_complete_structure', {column: column}));
            initialData[column] = selectedItems;
          } else if (typeof actualValue === 'string' && actualValue) {
            // 如果是逗號分隔的字串，轉換為 CheckboxValue[] 格式
            const keys = actualValue.split(',').map((v: string) => v.trim()).filter((v: string) => v);
            const checkboxValues = keys.map((key: string) => ({
              option: key,
              selected: true,
              textValue: ''  // 字串格式沒有 textValue 資訊
            }));
            initialData[column] = checkboxValues;
            console.log(this.i18nService.translate('showlist.console.checkbox_convert_string_to_array', {column: column}) + ':', checkboxValues);
          } else {
            // 其他情況，設為空陣列
            initialData[column] = [];
            console.log(this.i18nService.translate('showlist.console.checkbox_set_empty_array', {column: column}));
          }
        } else if (value.type === 'radiobutton') {
          // 單選按鈕：處理 key-value 結構
          console.log(`🔍 ` + this.i18nService.translate('showlist.console.prepare_initial_data_radiobutton_actual_value', {column: column}) + `:`, typeof actualValue, actualValue);
          if (actualValue && typeof actualValue === 'object' && actualValue.key) {
            // 檢查表單配置，尋找匹配的選項
            const formField = this.originalFormConfig?.find((field: any) => field.name === column);
            if (formField && formField.options && Array.isArray(formField.options)) {
              // 嘗試透過 key 匹配 (key 可能是 value 或 label)
              let matchingOption = formField.options.find((opt: any) => opt.value === actualValue.key);
              if (!matchingOption) {
                // 如果透過 value 找不到，嘗試透過 label 找
                matchingOption = formField.options.find((opt: any) => opt.label === actualValue.key);
              }

              if (matchingOption) {
                initialData[column] = matchingOption.value; // 使用表單配置中的 value
                console.log(`✅ ` + this.i18nService.translate('showlist.console.radiobutton_found_matching_option', {column: column}) + `:`, matchingOption.value);
              } else {
                initialData[column] = actualValue.key;
                console.log(`⚠️ ` + this.i18nService.translate('showlist.console.radiobutton_no_matching_option', {column: column}) + `:`, actualValue.key);
              }
            } else {
              initialData[column] = actualValue.key;
              console.log(this.i18nService.translate('showlist.console.radiobutton_no_form_config', {column: column}) + `:`, actualValue.key);
            }
          } else {
            // 簡單字串值或其他格式
            initialData[column] = actualValue;
            console.log(this.i18nService.translate('showlist.console.radiobutton_use_original_value', {column: column}) + `:`, actualValue);
          }
        } else if (value.type === 'required-document-upload') {
          // 指定證件上傳元件：保持完整的資料結構包含檔案和 AI 解析結果
          console.log(`🔍 ` + this.i18nService.translate('showlist.console.prepare_initial_data_required_document_upload', {column: column}) + `:`, actualValue);
          initialData[column] = actualValue; // 保持完整結構，元件會自行解析
          console.log(`✅ ` + this.i18nService.translate('showlist.console.required_document_upload_retain_structure', {column: column}));
        } else if (value.type === 'file-upload-manager') {
          // 檔案上傳管理器：直接使用資料庫中的完整結構，確保新增和編輯時格式一致
          console.log(`🔍 ` + this.i18nService.translate('showlist.console.prepare_initial_data_file_upload_manager', {column: column}) + `:`, actualValue);

          if (actualValue && actualValue.files && Array.isArray(actualValue.files)) {
            // 直接使用 actualValue，因為它已經包含完整的檔案資料結構
            initialData[column] = {
              files: actualValue.files, // 直接使用，包含 id, blobName, viewUrl, downloadUrl 等關鍵欄位
              metadata: actualValue.metadata || {
                totalFiles: actualValue.files.length,
                totalSize: actualValue.files.reduce((sum: number, file: any) => sum + (file.size || 0), 0),
                lastUpdated: new Date()
              }
            };
            console.log(`✅ ` + this.i18nService.translate('showlist.console.file_upload_manager_use_complete_structure', {column: column}));
          } else {
            // 空值處理
            initialData[column] = { files: [], metadata: { totalFiles: 0, totalSize: 0, lastUpdated: new Date() } };
            console.log(`⚠️ ` + this.i18nService.translate('showlist.console.file_upload_manager_data_empty', {column: column}));
          }
        } else if (value.type === 'subform-relation' || value.type === 'approval-flow' || value.type === 'approval-personnel-selector') {
          // 子表單和簽核流程的特殊處理
          initialData[column] = actualValue || value.value;
        } else {
          initialData[column] = actualValue;
        }
      } else {
        // 簡單值直接使用
        initialData[column] = value || '';
      }
    });

    // 🆕 處理 referencedata 物件，同時保留原始物件和建立攤平欄位
    if (editingItem.referencedata && typeof editingItem.referencedata === 'object') {
      console.log('🔍 ' + this.i18nService.translate('showlist.console.found_referencedata_object') + ':', editingItem.referencedata);

      // 1. 保留原始的 referencedata 物件供 dynamic-form 顯示使用
      initialData.referencedata = editingItem.referencedata;
      console.log('✅ ' + this.i18nService.translate('showlist.console.retain_original_referencedata'));

      // 2. 將 referencedata 物件的每個屬性攤平為 referencedata_* 格式讓 Field Relationship Service 監聽
      Object.keys(editingItem.referencedata).forEach(key => {
        const flattenedKey = `referencedata_${key}`;
        const fieldValue = editingItem.referencedata[key];

        console.log(`🔍 ` + this.i18nService.translate('showlist.console.flatten_field', {key: key, flattenedKey: flattenedKey}) + `:`, fieldValue);

        // 將攤平的欄位加入 initialData，讓 dynamic-form 可以建立對應的 FormControl
        initialData[flattenedKey] = fieldValue;

        // 特別記錄 special-operations 欄位
        if (key === 'special-operations') {
          console.log('🎯 ' + this.i18nService.translate('showlist.console.found_special_operations_field') + ':', fieldValue);
          console.log('🎯 ' + this.i18nService.translate('showlist.console.field_type') + ':', typeof fieldValue);
          if (fieldValue && fieldValue.value) {
            console.log('🎯 ' + this.i18nService.translate('showlist.console.special_operations_actual_value') + ':', fieldValue.value);
            if (Array.isArray(fieldValue.value)) {
              console.log('🎯 ' + this.i18nService.translate('showlist.console.special_operations_array_content') + ':', fieldValue.value);
              console.log('🎯 ' + this.i18nService.translate('showlist.console.contains_hanging') + ':', fieldValue.value.includes('吊掛'));
            }
          }
        }
      });
    }

    // 同時處理已經攤平的 referencedata 欄位（向後相容）
    const referencedataFields = Object.keys(editingItem).filter(key => key.startsWith('referencedata_'));
    if (referencedataFields.length > 0) {
      console.log('🔍 ' + this.i18nService.translate('showlist.console.found_flattened_referencedata_fields') + ':', referencedataFields);

      referencedataFields.forEach(fieldKey => {
        const fieldValue = editingItem[fieldKey];
        console.log(`🔍 ` + this.i18nService.translate('showlist.console.flattened_field_value', {fieldKey: fieldKey}) + `:`, fieldValue);

        // 直接將攤平的欄位加入 initialData
        initialData[fieldKey] = fieldValue;
      });
    }

    console.log('Prepared initial data for editing:', initialData);
    return initialData;
  }

  // 處理 Dynamic Form 編輯完成
  handleEditComplete(event: any): void {
    console.log('✅ ' + this.i18nService.translate('showlist.console.dynamic_form_edit_complete_event') + ':', event);

    // 刷新資料列表以顯示最新的資料
    this.loadData();

    // 關閉編輯對話框
    this.closeEditDialog();

    // 顯示成功訊息
    if (!event?.isDraft) {
      // 只有非暫存的情況才顯示完成訊息
      const successMessage = this.i18nService.translate('form.edit.success');
      if (successMessage !== 'form.edit.success') {
        // 如果有翻譯就顯示，沒有就不顯示避免顯示翻譯鍵
        alert(successMessage);
      }
    }
  }

  // performUpdate 方法已移除，現在由 dynamic-form 直接處理儲存


  // 子表單資料筆數緩存
  private subformRecordCountCache: { [key: string]: { count: number; timestamp: number } } = {};
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5分鐘緩存

  // 追蹤正在進行的請求，避免重複請求
  private pendingSubformRequests: Set<string> = new Set();

  // 🔑 子表單計數快速查詢 Map（避免模板中重複呼叫函數）
  // Key 格式: "formId|parentFormId"
  public subformCountMap: Map<string, number> = new Map();

  /**
   * 從 value 物件中提取實際的 formid（用於 Map key）
   * @param value 可能是字串或包含 originalValue 的物件
   * @returns formid 字串
   */
  public getFormIdFromValue(value: any): string {
    if (typeof value === 'string') {
      return value;
    } else if (value && typeof value === 'object') {
      if (value.originalValue) {
        return value.originalValue;
      } else if (value.value && typeof value.value === 'string') {
        return value.value;
      }
    }
    return '';
  }

  /**
   * 取得子表單資料筆數（同步方法，優先使用緩存）
   * @param formId 子表單ID
   * @param parentFormId 父記錄ID（必填）
   * @returns 資料筆數
   */
  getSubformRecordCount(formId: any, parentFormId: string): number {
    // 處理複雜的 formId 物件結構
    let actualFormId: string = '';

    if (typeof formId === 'string') {
      actualFormId = formId.trim();
    } else if (formId && typeof formId === 'object') {
      // 處理 subform-relation 物件結構
      if (formId.originalValue) {
        actualFormId = formId.originalValue;
      } else if (formId.value && formId.value.originalValue) {
        actualFormId = formId.value.originalValue;
      } else if (formId.value && typeof formId.value === 'string') {
        actualFormId = formId.value;
      }
    }

    if (!actualFormId || !parentFormId) {
      return 0;
    }

    // 檢查緩存，緩存鍵包含 parent_formid
    const cacheKey = `${this.companyid}_${actualFormId}_${parentFormId}`;
    const cached = this.subformRecordCountCache[cacheKey];
    const now = Date.now();

    if (cached && (now - cached.timestamp) < this.CACHE_DURATION) {
      return cached.count;
    }

    // 緩存過期或不存在，非同步載入新資料
    this.loadSubformRecordCount(actualFormId, parentFormId);

    // 返回緩存值或預設值
    return cached ? cached.count : 0;
  }

  /**
   * 非同步載入子表單資料筆數
   * @param formId 子表單ID
   * @param parentFormId 父記錄ID（必填）
   */
  private loadSubformRecordCount(formId: string, parentFormId: string): void {
    if (!formId || !this.companyid || !parentFormId) return;

    const cacheKey = `${this.companyid}_${formId}_${parentFormId}`;

    // 如果已經有相同的請求正在進行中，直接返回
    if (this.pendingSubformRequests.has(cacheKey)) {
      return;
    }

    // 標記此請求為進行中
    this.pendingSubformRequests.add(cacheKey);

    const apiUrl = `${environment.apiUrl}/api/form-manager/get_form_container_count`;
    const params = new URLSearchParams({
      formid: formId,
      parent_formid: parentFormId
    });

    this.http.get(`${apiUrl}?${params.toString()}`)
      .subscribe({
        next: (response: any) => {
          // 請求完成，移除進行中標記
          this.pendingSubformRequests.delete(cacheKey);

          if (response.success) {
            // 更新緩存鍵，包含 parent_formid
            this.subformRecordCountCache[cacheKey] = {
              count: response.data || 0,
              timestamp: Date.now()
            };

            //TODO:這邊似乎會呼叫非常多次，需要優化
            // console.log(`子表單 ${formId} 資料筆數已更新: ${response.data} (父記錄: ${parentFormId})`);
          } else {
            console.error(this.i18nService.translate('showlist.console.get_subform_data_api_failed'), response);

            // API 回應失敗時，也設定緩存避免重複請求
            this.subformRecordCountCache[cacheKey] = {
              count: 0,
              timestamp: Date.now()
            };
          }
        },
        error: (error) => {
          // 請求失敗，移除進行中標記
          this.pendingSubformRequests.delete(cacheKey);

          // 只在第一次失敗時記錄錯誤，避免刷屏
          const firstError = !this.subformRecordCountCache[cacheKey];
          if (firstError) {
            console.error(this.i18nService.translate('showlist.console.get_subform_data_failed'), error);
          }

          // 發生錯誤時，設定預設值到緩存，避免重複請求
          // 錯誤時使用較短的緩存時間（1分鐘），以便之後重試
          this.subformRecordCountCache[cacheKey] = {
            count: 0,
            timestamp: Date.now() - this.CACHE_DURATION + 60000 // 1分鐘後過期
          };
        }
      });
  }

  /**
   * 進階批次載入所有子表單記錄計數（一次性查詢所有組合）
   * @param queries 查詢組合陣列
   */
  private loadAllSubformRecordCountsAdvanced(queries: Array<{ formid: string; parent_formid: string }>): void {
    if (!queries || queries.length === 0) {
      return;
    }

    // console.log(`[進階批次載入] 準備一次性查詢 ${queries.length} 個組合`);

    // 🔑 關鍵優化：預先在緩存中放入「載入中」標記，防止個別請求
    const now = Date.now();
    queries.forEach(({ formid, parent_formid }) => {
      const cacheKey = `${this.companyid}_${formid}_${parent_formid}`;
      const mapKey = `${formid}|${parent_formid}`;

      // 放入暫時值 0，並標記為載入中（使用 timestamp 標記）
      this.subformRecordCountCache[cacheKey] = {
        count: 0,
        timestamp: now
      };
      // 同時填入快速查詢 Map
      this.subformCountMap.set(mapKey, 0);

      // 同時加入 pending 集合，防止個別請求
      this.pendingSubformRequests.add(cacheKey);
    });

    // console.log(`[進階批次載入] 已預先填充 ${queries.length} 個緩存佔位符，防止個別請求`);

    const apiUrl = `${environment.apiUrl}/api/form-manager/get_form_container_counts_batch_advanced`;
    const body = {
      queries: queries,
      includeDeleted: true
    };

    // console.log(`[進階批次載入] API URL:`, apiUrl);
    // console.log(`[進階批次載入] Request Body:`, body);

    this.http.post(apiUrl, body).subscribe({
      next: (response: any) => {
        // console.log(`[進階批次載入] API 回應:`, response);

        if (response && response.success && response.data) {
          const responseTime = Date.now();
          let successCount = 0;

          // 將所有結果寫入緩存（覆蓋佔位符）
          // response.data 的格式: { "formid|parent_formid": count }
          for (const [key, count] of Object.entries(response.data)) {
            const [formid, parent_formid] = key.split('|');
            const cacheKey = `${this.companyid}_${formid}_${parent_formid}`;
            const mapKey = `${formid}|${parent_formid}`;

            this.subformRecordCountCache[cacheKey] = {
              count: count as number,
              timestamp: responseTime
            };

            // 🔑 同時填入快速查詢 Map（供模板使用）
            this.subformCountMap.set(mapKey, count as number);

            // 移除 pending 標記
            this.pendingSubformRequests.delete(cacheKey);

            successCount++;
            // console.log(`[進階批次載入] 緩存已更新: ${cacheKey} = ${count}`);
          }

          // console.log(`[進階批次載入] ✅ 完成！共載入 ${successCount} 筆子表單計數`);
        } else {
          // console.warn(`[進階批次載入] API 回應格式不正確:`, response);

          // 失敗時設定預設值，並移除 pending 標記
          queries.forEach(({ formid, parent_formid }) => {
            const cacheKey = `${this.companyid}_${formid}_${parent_formid}`;
            const mapKey = `${formid}|${parent_formid}`;

            this.subformRecordCountCache[cacheKey] = {
              count: 0,
              timestamp: Date.now()
            };
            // 同時更新 Map
            this.subformCountMap.set(mapKey, 0);

            this.pendingSubformRequests.delete(cacheKey);
          });
        }
      },
      error: (error) => {
        console.error(`[進階批次載入] ❌ 批次載入失敗:`, error);

        // 發生錯誤時，設定預設值到緩存，並移除 pending 標記
        queries.forEach(({ formid, parent_formid }) => {
          const cacheKey = `${this.companyid}_${formid}_${parent_formid}`;
          const mapKey = `${formid}|${parent_formid}`;

          this.subformRecordCountCache[cacheKey] = {
            count: 0,
            timestamp: Date.now() - this.CACHE_DURATION + 60000 // 1分鐘後過期，允許重試
          };
          // 同時更新 Map
          this.subformCountMap.set(mapKey, 0);

          this.pendingSubformRequests.delete(cacheKey);
        });
      }
    });
  }

  /**
   * 清理過期的緩存
   */
  private cleanExpiredCache(): void {
    const now = Date.now();
    Object.keys(this.subformRecordCountCache).forEach(key => {
      if ((now - this.subformRecordCountCache[key].timestamp) > this.CACHE_DURATION) {
        delete this.subformRecordCountCache[key];
      }
    });
  }

  /**
   * 手動清除特定子表單的緩存
   * @param formId 子表單ID
   * @param parentFormId 父記錄ID（可選，如果提供則只清除特定父記錄的緩存；否則清除所有相關緩存）
   */
  clearSubformCache(formId: string, parentFormId?: string): void {
    if (!formId || !this.companyid) return;

    if (parentFormId) {
      // 清除特定父記錄的緩存
      const cacheKey = `${this.companyid}_${formId}_${parentFormId}`;
      delete this.subformRecordCountCache[cacheKey];

      // 重新載入該特定父記錄的資料
      this.loadSubformRecordCount(formId, parentFormId);
    } else {
      // 清除所有與該 formId 相關的緩存
      const prefix = `${this.companyid}_${formId}`;
      Object.keys(this.subformRecordCountCache).forEach(key => {
        if (key.startsWith(prefix)) {
          delete this.subformRecordCountCache[key];
        }
      });

      // 不進行重新載入，因為沒有 parentFormId 無法載入
      console.log(this.i18nService.translate('showlist.console.cleared_all_cache_for_formid', {formId: formId}));
    }
  }


  // 新增記錄相關方法
  createNewRecord(): void {
    this.showAddRecordDialog = true;
    // 阻止背景滾动
    document.body.style.overflow = 'hidden';
  }

  closeAddRecordDialog(): void {
    this.showAddRecordDialog = false;
    // 恢復背景滾動
    document.body.style.overflow = 'auto';
  }

  // 處理新記錄添加完成後的回調
  onRecordAdded(): void {
    this.closeAddRecordDialog();
    // 重新載入資料
    this.loadData();
    // 發射資料更新事件
    this.dataUpdated.emit();
  }

  // 處理新資料建立完成後的回調，接收新建立的資料
  onDataCreated(createdData: any): void {
    console.log(this.i18nService.translate('showlist.console.new_data_created'), createdData);
    this.closeAddRecordDialog();
    // 重新載入資料
    this.loadData();
    // 發射資料更新事件
    this.dataUpdated.emit();
    // 發射新資料建立事件，傳遞新建立的資料
    this.newDataCreated.emit(createdData);
  }


  // 從資料庫載入表單配置
  loadFormConfigFromDatabase(formName: string): void {
    // 建立查詢條件
    const filter: any = {
      formName: formName
    };

    this.databaseService.getDataByQuery(filter, 1, 'form-config')
      .subscribe({
        next: (result) => {
          console.log(this.i18nService.translate('showlist.console.loaded_form_config'), result);

          // 配置載入完成後，顯示對話框並觸發變更檢測
          setTimeout(() => {
            this.showEditFormDialog = true;
            document.body.style.overflow = 'hidden';
          }, 10);
        },
        error: (error) => {
          console.error(this.i18nService.translate('showlist.console.load_form_config_failed'), error);

          // 即使載入失敗，也要顯示對話框
          setTimeout(() => {
            this.showEditFormDialog = true;
            document.body.style.overflow = 'hidden';
          }, 10);
        }
      });
  }

  // 關閉編輯表單配置對話框
  closeEditFormDialog(): void {
    this.showEditFormDialog = false;
    this.editingFormConfig = null;
    this.resetEditFormState();
    document.body.style.overflow = 'auto';
  }

  // 重置編輯表單相關狀態
  resetEditFormState(): void {
    this.editFormBuilderMode = 'ai';
    this.editFormAiMessage = '';
    this.editFormAiMessages = [];
    this.isEditFormSending = false;
    this.editResponseId = '';
  }

  // 編輯表單 AI 相關方法
  onEditFormKeyDown(event: KeyboardEvent): void {
    // 按下 Enter 且沒有按住 Shift 發送消息
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.askEditFormAi();
    }
  }

  autoResizeEditFormInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  askEditFormAi(): void {
    console.log("askEditFormAi");

    if (!this.editFormAiMessage.trim()) {
      return;
    }

    // 添加用戶消息到列表
    this.editFormAiMessages.push({
      role: 'user',
      content: this.editFormAiMessage
    });

    // 清空輸入框
    const message = this.editFormAiMessage;
    this.editFormAiMessage = '';

    // 設置發送狀態
    this.isEditFormSending = true;

    // 模擬 AI 回應（這裡可以實際調用 AI 服務）
    setTimeout(() => {
      this.editFormAiMessages.push({
        role: 'assistant',
        content: `我了解您想要修改表單「${this.formName}」。目前這是一個簡化版本的編輯功能。您的請求：${message}`
      });

      this.isEditFormSending = false;
    }, 1000);
  }

  ngOnDestroy(): void {
    // 清理計時器
    if (this.cacheCleanupInterval) {
      clearInterval(this.cacheCleanupInterval);
    }

    // 清理所有緩存
    this.subformRecordCountCache = {};

    // 恢復頁面滾動（如果有打開的對話框）
    document.body.style.overflow = 'auto';

    // 關閉欄位設定對話框
    this.showColumnSettings = false;
  }

  /**
   * 載入子表單
   * @param subformId 子表單ID
   * @param parentItem 觸發顯示的父表單資料項目
   * @param columnName 觸發的欄位名稱，用於取得 label
   */
  loadSubform(subformId: any, parentItem?: any, columnName?: string): void {
    // 處理 subform-relation 類型的複雜物件結構
    let actualSubformId: string = '';

    if (typeof subformId === 'string') {
      actualSubformId = subformId.trim();
    } else if (subformId && typeof subformId === 'object') {
      // 處理 subform-relation 物件結構
      if (subformId.originalValue) {
        actualSubformId = subformId.originalValue;
      } else if (subformId.value && subformId.value.originalValue) {
        actualSubformId = subformId.value.originalValue;
      } else if (subformId.value && typeof subformId.value === 'string') {
        actualSubformId = subformId.value;
      }
    }

    // 確保最終得到的是有效的字符串
    const subformIdStr = actualSubformId?.toString()?.trim();
    if (!subformIdStr) {
      console.warn(this.i18nService.translate('showlist.console.subform_id_empty'), subformId);
      return;
    }

    console.log(this.i18nService.translate('showlist.console.loading_subform'), subformIdStr, this.i18nService.translate('showlist.console.source_item') + ':', parentItem, this.i18nService.translate('showlist.console.field_name') + ':', columnName);

    // 取得欄位的 label 作為子表單顯示標題
    let displayTitle = subformIdStr; // 預設使用 subformId
    if (parentItem && columnName) {
      // 優先使用原本的欄位名稱（通過 getColumnDisplayName 獲得）
      displayTitle = this.getColumnDisplayName(columnName);

      // 如果 getColumnDisplayName 返回的是原始欄位名稱，再嘗試從資料中取得 label
      if (displayTitle === columnName) {
        const fieldData = parentItem[columnName];
        if (fieldData && fieldData.label) {
          // 確保 label 是字串類型，如果不是則轉換成字串
          const labelValue = typeof fieldData.label === 'string'
            ? fieldData.label
            : (fieldData.label?.toString?.() || fieldData.label);

          if (labelValue && typeof labelValue === 'string') {
            displayTitle = labelValue;
          }
        }
      }
    }

    // 先關閉子表單以確保重新載入
    this.showSubform = false;

    // 使用 setTimeout 確保 DOM 更新完成後再重新開啟
    setTimeout(() => {
      // 設定子表單相關屬性
      this.subformId = subformIdStr;
      // 確保 displayTitle 是字串類型
      if (typeof displayTitle === 'string') {
        this.subformDisplayTitle = displayTitle;
      } else if (displayTitle && typeof (displayTitle as any).toString === 'function') {
        this.subformDisplayTitle = (displayTitle as any).toString();
      } else {
        this.subformDisplayTitle = subformIdStr;
      }
      this.activeSubformData = parentItem;
      this.showSubform = true;

      // 設定當前父表單ID，用於新增子記錄時使用
      if (parentItem && parentItem.id) {
        this.currentParentFormId = parentItem.id;
      } else {
        this.currentParentFormId = '';
      }

      // 新增：從主表單資料中提取日期範圍限制（用於限制子表單的日期輸入）
      this.extractParentDateRange(parentItem);

      // 呼叫 API 取得表單名稱
      this.loadSubformName(subformIdStr);

      // 滾動到子表單位置
      setTimeout(() => {
        const subformElement = document.getElementById('subform-container');
        if (subformElement) {
          subformElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }, 0);
  }

  /**
   * 關閉子表單
   */
  closeSubform(): void {
    this.showSubform = false;
    this.subformId = '';
    this.subformName = '';
    this.subformDisplayTitle = '';
    this.activeSubformData = null;
    this.currentParentFormId = ''; // 清空父表單ID
    this.parentDateRange = undefined; // 清空日期範圍限制
  }

  /**
   * 從主表單資料中提取日期範圍限制
   * @param parentItem 主表單資料項目
   */
  private extractParentDateRange(parentItem: any): void {
    try {
      // 重置日期範圍
      this.parentDateRange = undefined;

      console.log('[DateRange] ===== 開始提取日期範圍 =====');
      console.log('[DateRange] parentItem:', parentItem);

      if (!parentItem) {
        console.log('[DateRange] 無主表單資料，跳過日期範圍提取');
        return;
      }

      // 嘗試從多個可能的欄位名稱中提取日期範圍
      // 優先順序：date_range_1 > dateRange > date_range
      const possibleFields = ['date_range_1', 'dateRange', 'date_range'];
      let dateRangeData = null;
      let foundFieldName = '';

      console.log('[DateRange] 嘗試尋找日期範圍欄位...');
      for (const fieldName of possibleFields) {
        console.log(`[DateRange] 檢查欄位: ${fieldName}`, parentItem[fieldName]);
        if (parentItem[fieldName]) {
          dateRangeData = parentItem[fieldName];
          foundFieldName = fieldName;
          console.log(`[DateRange] ✅ 找到日期範圍欄位: ${fieldName}`);
          console.log(`[DateRange] 欄位資料:`, JSON.stringify(dateRangeData, null, 2));
          break;
        }
      }

      if (!dateRangeData) {
        console.log('[DateRange] ❌ 主表單中未找到日期範圍欄位，子表單日期不受限制');
        console.log('[DateRange] 可用的欄位:', Object.keys(parentItem));
        return;
      }

      // 處理不同的資料格式
      let startDate: string | undefined;
      let endDate: string | undefined;

      console.log('[DateRange] 開始解析日期資料...');
      console.log('[DateRange] dateRangeData 類型:', typeof dateRangeData);
      console.log('[DateRange] dateRangeData.value 存在?', !!dateRangeData.value);
      console.log('[DateRange] dateRangeData.value 類型:', typeof dateRangeData.value);

      // 格式1：{ value: { startDate: '...', endDate: '...' } }
      if (dateRangeData.value && typeof dateRangeData.value === 'object') {
        console.log('[DateRange] 使用格式1: { value: { startDate, endDate } }');
        startDate = dateRangeData.value.startDate;
        endDate = dateRangeData.value.endDate;
        console.log('[DateRange] 提取結果 - startDate:', startDate);
        console.log('[DateRange] 提取結果 - endDate:', endDate);
      }
      // 格式2：{ startDate: '...', endDate: '...' }
      else if (dateRangeData.startDate && dateRangeData.endDate) {
        console.log('[DateRange] 使用格式2: { startDate, endDate }');
        startDate = dateRangeData.startDate;
        endDate = dateRangeData.endDate;
        console.log('[DateRange] 提取結果 - startDate:', startDate);
        console.log('[DateRange] 提取結果 - endDate:', endDate);
      } else {
        console.warn('[DateRange] ⚠️ 資料格式不符合預期');
        console.log('[DateRange] dateRangeData 的屬性:', Object.keys(dateRangeData));
        console.log('[DateRange] dateRangeData 完整內容:', dateRangeData);
      }

      // 驗證日期格式並設置
      console.log('[DateRange] 驗證提取的日期...');
      console.log('[DateRange] startDate 有值?', !!startDate, 'endDate 有值?', !!endDate);

      if (startDate && endDate) {
        // 確保日期格式正確（YYYY-MM-DD 或 YYYY-MM-DDTHH:mm）
        const isValidDate = (dateStr: string) => {
          const date = new Date(dateStr);
          return !isNaN(date.getTime());
        };

        const startValid = isValidDate(startDate);
        const endValid = isValidDate(endDate);
        console.log('[DateRange] startDate 有效?', startValid);
        console.log('[DateRange] endDate 有效?', endValid);

        if (startValid && endValid) {
          this.parentDateRange = {
            startDate: startDate,
            endDate: endDate
          };
          console.log('[DateRange] ✅ 成功設置日期範圍限制:', this.parentDateRange);
        } else {
          console.warn('[DateRange] ❌ 日期格式無效:', { startDate, endDate, startValid, endValid });
        }
      } else {
        console.warn('[DateRange] ❌ 日期範圍資料不完整:', { startDate, endDate });
        console.log('[DateRange] 無法設置 parentDateRange');
      }

      console.log('[DateRange] ===== 提取日期範圍結束 =====');

    } catch (error) {
      console.error('[DateRange] ❌ 提取日期範圍時發生錯誤:', error);
      this.parentDateRange = undefined;
    }
  }

  /**
   * 子表單資料更新處理
   */
  onSubformDataUpdated(): void {
    console.log(this.i18nService.translate('showlist.console.subform_data_updated'));

    // 清除相關的子表單資料筆數緩存，以便重新載入最新筆數
    if (this.subformId && this.currentParentFormId) {
      // 清除特定父記錄的子表單緩存
      this.clearSubformCache(this.subformId, this.currentParentFormId);
    } else if (this.subformId) {
      // 如果沒有 currentParentFormId，清除所有相關緩存
      this.clearSubformCache(this.subformId);
    }

    // 重新載入當前表單的資料（支援 parentFormId 過濾）
    this.reloadFormData();

    // 發射資料更新事件，通知父組件
    this.dataUpdated.emit();
  }

  // === 欄位設定相關方法 ===

  /**
   * 開啟欄位設定對話框
   */
  openColumnSettings(): void {
    this.showColumnSettings = true;
    // 可選：防止背景滾動
    document.body.style.overflow = 'hidden';

    // 為無障礙性設定焦點
    setTimeout(() => {
      const dialog = document.querySelector('.column-settings-dialog h3') as HTMLElement;
      if (dialog) {
        dialog.focus();
      }
    }, 100);
  }

  /**
   * 關閉欄位設定對話框
   */
  closeColumnSettings(): void {
    this.showColumnSettings = false;
    // 恢復背景滾動
    document.body.style.overflow = 'auto';
  }

  /**
   * 載入欄位設定從 localStorage
   */
  loadColumnSettings(): void {
    try {
      const settingsKey = `showlist_${this.formName}_columnSettings`;
      const savedSettings = localStorage.getItem(settingsKey);

      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        this.columnSettings = {
          visibleColumns: parsedSettings.visibleColumns || [...this.defaultColumns],
          columnOrder: parsedSettings.columnOrder || [...this.defaultColumns]
        };
        console.log(this.i18nService.translate('showlist.console.loaded_column_settings'), this.columnSettings);
      } else {
        // 如果沒有儲存的設定，使用預設值
        this.columnSettings = {
          visibleColumns: [...this.defaultColumns],
          columnOrder: [...this.defaultColumns]
        };
        console.log(this.i18nService.translate('showlist.console.using_default_column_settings'), this.columnSettings);
      }

      // 應用設定到 displayedColumns
      this.applyColumnSettings();
    } catch (error) {
      console.error(this.i18nService.translate('showlist.console.load_column_settings_failed'), error);
      // 發生錯誤時使用預設設定
      this.resetColumnSettings();
    }
  }

  /**
   * 儲存欄位設定到 localStorage
   */
  saveColumnSettings(): void {
    try {
      const settingsKey = `showlist_${this.formName}_columnSettings`;
      localStorage.setItem(settingsKey, JSON.stringify(this.columnSettings));
      console.log(this.i18nService.translate('showlist.console.column_settings_saved'), this.columnSettings);
    } catch (error) {
      console.error(this.i18nService.translate('showlist.console.save_column_settings_failed'), error);
    }
  }

  /**
   * 應用欄位設定到 displayedColumns
   */
  applyColumnSettings(): void {
    // 如果有程式化欄位控制，優先使用程式設定
    if (this.programmaticColumns?.columns) {
      // 將 labels 轉換為對應的 column names
      const resolvedColumns = this.programmaticColumns.columns
        .map(labelOrColumn => this.resolveColumnName(labelOrColumn))
        .filter(column => this.availableColumns.includes(column));

      this.displayedColumns = resolvedColumns;
      return;
    }

    // 否則使用原有的使用者自定義設定
    // 根據順序和可見性設定 displayedColumns
    // 注意：actions 欄位不在 availableColumns 中，所以會自動保持在適當位置
    this.displayedColumns = this.columnSettings.columnOrder.filter(column =>
      this.columnSettings.visibleColumns.includes(column) &&
      this.availableColumns.includes(column)
    );
  }

  /**
   * 將 label 轉換為對應的 column name
   * @param labelOrColumn 可能是 label 或 column name
   * @returns column name
   */
  private resolveColumnName(labelOrColumn: string): string {
    // 如果直接是有效的 column name，直接返回
    if (this.availableColumns.includes(labelOrColumn)) {
      return labelOrColumn;
    }

    // 嘗試通過 label 找到對應的 column name
    for (const column of this.availableColumns) {
      const displayName = this.getColumnDisplayName(column);
      if (displayName === labelOrColumn) {
        return column;
      }
    }

    // 如果沒找到，返回原值（可能是新的 column name）
    return labelOrColumn;
  }

  /**
   * 檢查是否應該隱藏欄位設定功能
   */
  shouldHideColumnSettings(): boolean {
    return this.programmaticColumns?.hideColumnSettings === true;
  }

  /**
   * 重置欄位設定為預設值
   */
  resetColumnSettings(): void {
    this.columnSettings = {
      visibleColumns: [...this.defaultColumns],
      columnOrder: [...this.defaultColumns]
    };
    this.applyColumnSettings();
    this.saveColumnSettings();
  }

  /**
   * 切換欄位顯示狀態
   */
  toggleColumnVisibility(column: string): void {
    const index = this.columnSettings.visibleColumns.indexOf(column);
    if (index > -1) {
      this.columnSettings.visibleColumns.splice(index, 1);
    } else {
      this.columnSettings.visibleColumns.push(column);
    }
  }

  /**
   * 檢查欄位是否可見
   */
  isColumnVisible(column: string): boolean {
    return this.columnSettings.visibleColumns.includes(column);
  }

  /**
   * 拖拉排序欄位
   */
  dropColumn(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.columnSettings.columnOrder, event.previousIndex, event.currentIndex);
    }
  }

  /**
   * 套用欄位設定
   */
  applyColumnSettingsAndClose(): void {
    this.applyColumnSettings();
    this.saveColumnSettings();
    this.closeColumnSettings();
  }

  /**
   * 取消欄位設定變更
   */
  cancelColumnSettings(): void {
    // 重新載入設定（放棄變更）
    this.loadColumnSettings();
    this.closeColumnSettings();
  }

  /**
   * 取得已排序的可用欄位列表（用於設定介面顯示）
   */
  getOrderedAvailableColumns(): string[] {
    // 按照當前順序顯示所有可用欄位
    const orderedColumns = this.columnSettings.columnOrder.filter(col =>
      this.availableColumns.includes(col)
    );

    // 添加不在順序中的新欄位
    const missingColumns = this.availableColumns.filter(col =>
      !this.columnSettings.columnOrder.includes(col)
    );

    const result = [...orderedColumns, ...missingColumns];
    console.log(this.i18nService.translate('showlist.console.sorted_available_columns'), result);
    return result;
  }

  /**
   * 同步新欄位到設定中
   */
  syncNewColumnsToSettings(): void {
    let hasChanges = false;

    // 新增不在設定中的欄位
    this.availableColumns.forEach(column => {
      if (!this.columnSettings.columnOrder.includes(column)) {
        this.columnSettings.columnOrder.push(column);
        this.columnSettings.visibleColumns.push(column);
        hasChanges = true;
      }
    });

    // 移除不存在的欄位
    this.columnSettings.columnOrder = this.columnSettings.columnOrder.filter(column =>
      this.availableColumns.includes(column)
    );
    this.columnSettings.visibleColumns = this.columnSettings.visibleColumns.filter(column =>
      this.availableColumns.includes(column)
    );

    if (hasChanges) {
      console.log(this.i18nService.translate('showlist.console.sync_new_columns'), this.columnSettings);
      this.saveColumnSettings();
      this.applyColumnSettings();
    }
  }

  /**
   * 載入子表單名稱
   * @param formId 子表單ID
   */
  private loadSubformName(formId: string): void {
    if (!formId || !this.companyid) {
      // 如果沒有 formId 或 companyid，使用 formId 作為預設名稱
      this.subformName = formId;
      return;
    }

    const apiUrl = `${environment.apiUrl}/api/form-manager/get_form_name`;
    const params = new URLSearchParams({
      formid: formId
    });

    this.http.get(`${apiUrl}?${params.toString()}`)
      .subscribe({
        next: (response: any) => {
          if (response.success && response.data && response.data.formName) {
            this.subformName = response.data.formName;
            console.log(this.i18nService.translate('showlist.console.successfully_get_subform_name', {subformName: this.subformName, formId: formId}));
          } else {
            console.warn(this.i18nService.translate('showlist.console.get_subform_name_api_invalid'), response);
            this.subformName = formId; // 使用 formId 作為後備名稱
          }
        },
        error: (error) => {
          console.error(this.i18nService.translate('showlist.console.get_subform_name_failed'), error);
          this.subformName = formId; // 使用 formId 作為後備名稱
        }
      });
  }

  /**
   * 格式化數字輸入顯示 - 特別處理電話號碼格式
   * @param value 數字值
   * @returns 格式化後的字串
   */
  formatNumberInput(value: any): string {
    if (value === undefined || value === null || value === '') {
      return '';
    }

    const numberStr = String(value);
    // 如果是 8-9 位數字，前面補 0 (台灣電話號碼格式)
    // 10 位數字視為完整號碼，不補 0
    if (/^\d{8,9}$/.test(numberStr)) {
      return '0' + numberStr;
    }

    return numberStr;
  }

  /**
   * 處理預設顯示值 - 判斷是否為陣列且有 value 屬性
   * @param columnValue 欄位值
   * @returns 處理後的顯示值
   */
  getDisplayValue(columnValue: any): string {
    if (!columnValue) {
      return '';
    }

    // // Debug: 記錄所有包含 tags 的資料
    // if (columnValue && JSON.stringify(columnValue).includes('tags')) {
    //   console.log('🏷️ Found tags data in getDisplayValue:', columnValue);
    //   console.log('🏷️ Type check:', typeof columnValue, 'has type property:', !!columnValue.type);
    // }

    // // 除錯：檢查所有 dynamic-api-select 類型的資料
    // if (columnValue && typeof columnValue === 'object' && columnValue.type === 'dynamic-api-select') {
    //   console.log('🔍 發現 dynamic-api-select 在 getDisplayValue:', columnValue);
    // }

    // 處理經過轉換的複雜元件資料
    if (typeof columnValue === 'object' && columnValue.type) {
      // // Debug: 記錄 tags 類型的處理
      // if (columnValue.type === 'tags') {
      //   console.log('🏷️ Processing tags in switch case:', columnValue);
      // }

      // console.log("columnValue.type",columnValue.type);

      switch (columnValue.type) {
        case 'file-upload-manager':
          return columnValue.summary || '檔案管理資料';

        case 'required-document-upload':
          return columnValue.summary || '必要文件';

        case 'avatar-upload':
          return columnValue.summary || '頭像上傳';

        case 'signature':
          return columnValue.summary || '簽名';

        case 'meeting-signature-collection':
          return columnValue.summary || '簽名收集';

        case 'dynamic-api-table':
          return columnValue.summary || '表格資料';

        case 'auto-numbering':
          return columnValue.originalValue || columnValue.number || columnValue.summary || '自動編號';

        case 'risk-assessment-matrix':
          return columnValue.summary || '風險評估';

        case 'audio-recorder':
          return columnValue.summary || '音頻記錄';

        case 'video-player':
          return columnValue.summary || '影片播放';

        case 'fill-in':
          // 處理巢狀資料結構：檢查是否有 originalValue
          const actualData = columnValue.originalValue || columnValue;

          // 優先顯示完整文字結果
          if (actualData.completeText) {
            return actualData.completeText;
          }

          // 第二優先：從外層 summary 取得（但排除自動生成的格式）
          if (columnValue.summary && !columnValue.summary.includes('個欄位')) {
            return columnValue.summary;
          }

          // 第三優先：從內層 summary 取得
          if (actualData.summary && !actualData.summary.includes('個欄位')) {
            return actualData.summary;
          }

          // 如果都沒有，嘗試從結構化資料產生簡單摘要
          if (actualData.fieldValues) {
            const values = Object.values(actualData.fieldValues).filter(v => v);
            if (values.length > 0) {
              return `填空內容：${values.join(', ')}`;
            }
          }

          return '填空欄位';

        case 'radiobutton':
          // 優先顯示完整文字結果（來自 RadioButtonValue.completeText）
          if (columnValue.completeText) {
            return columnValue.completeText;
          }

          // 處理新的 key/value 資料結構
          if (columnValue.value && typeof columnValue.value === 'object' && columnValue.value.value) {
            return columnValue.value.value; // 返回顯示標籤
          }

          // 處理複雜物件格式（包含 selectedValue）
          if (columnValue.selectedValue) {
            // 新格式：selectedValue 是 { key, value } 物件
            if (typeof columnValue.selectedValue === 'object' && columnValue.selectedValue.value) {
              return columnValue.selectedValue.value; // 返回顯示標籤
            }
            // 舊格式：selectedValue 是字串（向下相容）
            if (typeof columnValue.selectedValue === 'string') {
              return `選擇：${columnValue.selectedValue}`;
            }
          }

          // 如果有 selectedLabel，顯示選擇的標籤（舊格式相容）
          if (columnValue.selectedLabel) {
            return `選擇：${columnValue.selectedLabel}`;
          }

          // 如果都沒有，從 summary 取得
          if (columnValue.summary) {
            return columnValue.summary;
          }

          return '選項按鈕';

        case 'subform-relation':
          return columnValue.summary || '關聯表單';

        case 'tags':
          // 處理 tags 陣列資料格式: { type: "tags", value: ["tag1", "tag2"] }
          if (columnValue.value && Array.isArray(columnValue.value)) {
            return columnValue.value.join(', ');
          }
          return columnValue.summary || '標籤';

        case 'location':
          return columnValue.summary || '位置資料';

        case 'timestamp':
          // 處理時間戳記顯示
          console.log('Showlist timestamp columnValue:', columnValue);
          if (columnValue.summary) {
            return columnValue.summary;
          } else if (columnValue.value) {
            // 處理雙重巢狀結構
            let timestamp: number;
            console.log('columnValue.value type:', typeof columnValue.value, 'value:', columnValue.value);
            if (typeof columnValue.value === 'object' && columnValue.value.value) {
              timestamp = columnValue.value.value;
              console.log('Using nested value:', timestamp);
            } else {
              timestamp = typeof columnValue.value === 'number' ? columnValue.value : Number(columnValue.value);
              console.log('Using direct value:', timestamp);
            }

            if (!isNaN(timestamp)) {
              const date = new Date(timestamp);
              // 格式化為 yyyy-mm-dd HH:mm:ss
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const hours = String(date.getHours()).padStart(2, '0');
              const minutes = String(date.getMinutes()).padStart(2, '0');
              const seconds = String(date.getSeconds()).padStart(2, '0');
              return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
            }
          }
          return '無效時間';

        case 'select':
          // 處理新的 select 資料結構：{ key: "原始值", value: "顯示標籤" }
          if (columnValue.value && typeof columnValue.value === 'object' && columnValue.value.value) {
            return columnValue.value.value; // 返回顯示標籤
          }
          // 向下相容：如果是舊格式（直接是字串），直接返回
          return columnValue.value || columnValue.summary || '';

        case 'multi-checkbox':
          // 處理新的 multi-checkbox 資料結構，使用現有的方法但要適應新格式
          return this.getMultiCheckboxSelectedText(columnValue.value);

        case 'dynamic-api-select':
          // 處理新的簡化格式：{ type, value: {key: "id", value: "label"} }
          if (columnValue.value && typeof columnValue.value === 'object') {
            // 新格式：value 是包含 key 和 value 字串的物件
            if (columnValue.value.value && typeof columnValue.value.value === 'string') {
              return columnValue.value.value;
            }
            // 舊格式相容：新的三層結構：value.value.value（最終要顯示的值）
            else if (columnValue.value.value && typeof columnValue.value.value === 'object' && columnValue.value.value.value) {
              return columnValue.value.value.value;
            }
            // 舊格式相容：如果 value.value 是物件，需要進一步提取 label 或 value
            else if (columnValue.value.value && typeof columnValue.value.value === 'object') {
              // 提取內嵌物件的 label 或 value
              const result = columnValue.value.value.label || columnValue.value.value.value || '';
              return result;
            }
            // 舊格式相容：如果沒有 value.value，嘗試用 key.value 或 key.label
            else if (columnValue.value.key && typeof columnValue.value.key === 'object') {
              const result = columnValue.value.key.label || columnValue.value.key.value || '';
              return result;
            }
          }
          // 向下相容：舊格式
          return columnValue.value || columnValue.summary || '';

        case 'personnel-selector':
          // 處理新的 personnel-selector 資料結構
          if (columnValue.value && typeof columnValue.value === 'object') {
            // 單選格式：{key, value}
            if (columnValue.value.key && columnValue.value.value) {
              return columnValue.value.value; // 返回顯示名稱
            }
            // 多選格式：[{key, value}, ...]
            if (Array.isArray(columnValue.value)) {
              const displayNames = columnValue.value
                .map((item: any) => item.value || item.key || item)
                .filter((name: any) => name)
                .join(', ');
              return displayNames || '未指定';
            }
          }
          // 向下相容：舊格式
          return columnValue.value || '';

        case 'department-selector':
          // 處理新的 department-selector 資料結構
          if (columnValue.value && typeof columnValue.value === 'object' && columnValue.value.value) {
            return columnValue.value.value; // 返回部門顯示名稱
          }
          // 向下相容：舊格式
          return columnValue.value || '';

        // 現有的轉換格式
        case 'approval-flow':
        case 'approval-personnel-selector':
          return columnValue.value || '';

        default:
          // 如果有 summary 屬性，優先使用
          if (columnValue.summary) {
            return columnValue.summary;
          }
          // 否則使用 value 屬性
          if (columnValue.value !== undefined) {
            return String(columnValue.value);
          }
          break;
      }
    }

    // 如果是陣列且有元素
    if (Array.isArray(columnValue) && columnValue.length > 0) {
      // 檢查第一個元素是否有 value 屬性
      if (columnValue[0] && typeof columnValue[0] === 'object' && columnValue[0].hasOwnProperty('value')) {
        return columnValue[0].value || '';
      }
      // 如果是簡單陣列，返回第一個元素
      return String(columnValue[0]);
    }

    // 如果是物件且有 value 屬性
    if (typeof columnValue === 'object' && columnValue.hasOwnProperty('value')) {
      return columnValue.value || '';
    }

    // 其他情況直接返回字串化的值
    return String(columnValue);
  }

  /**
   * 取得 dynamic-api-table 的資料筆數
   * @param value dynamic-api-table 的值
   * @returns 資料筆數
   */
  getDynamicApiTableCount(value: any): number {
    if (!value) {
      return 0;
    }

    if (Array.isArray(value)) {
      return value.length;
    }

    return 0;
  }

  /**
   * 取得 dynamic-api-select 的顯示值
   * @param columnData dynamic-api-select 的欄位資料
   * @returns 顯示值字串
   */
  getDynamicApiSelectDisplayValue(columnData: any): string {
    if (!columnData || !columnData.value) {
      return '';
    }

    // 處理新的簡化格式: { type: "dynamic-api-select", value: { key: "id", value: "label" } }
    const selectValue = columnData.value;

    // 新格式：直接從 value 物件中提取顯示值
    if (typeof selectValue === 'object' && selectValue.value && typeof selectValue.value === 'string') {
      return selectValue.value;
    }

    // 舊格式相容：提取 value.value.value 的值
    if (selectValue.value && selectValue.value.value) {
      return selectValue.value.value;
    }

    // 舊格式相容：提取 value.value.label
    if (selectValue.value && selectValue.value.label) {
      return selectValue.value.label;
    }

    // 舊格式相容：提取 value.key.value
    if (selectValue.key && selectValue.key.value) {
      return selectValue.key.value;
    }

    // 最終備用方案
    return '';
  }

  /**
   * 取得 department-selector 的顯示值
   * @param columnData department-selector 的欄位資料
   * @returns 顯示值字串
   */
  getDepartmentSelectorDisplayValue(columnData: any): string {
    if (!columnData || !columnData.value) {
      return '';
    }

    // 處理資料格式: { type: "department-selector", value: { key: {...}, value: {...} } }
    const selectorValue = columnData.value;

    // 處理標準格式：value.value (直接的值)
    if (selectorValue.value) {
      return selectorValue.value;
    }

    // 處理深層巢狀結構 value.value.value.value (選中的部門值)
    if (selectorValue.value && selectorValue.value.value && selectorValue.value.value.value) {
      return selectorValue.value.value.value;
    }

    // 處理深層巢狀結構 value.value.name (選中的部門名稱)
    if (selectorValue.value && selectorValue.value.name) {
      return selectorValue.value.name;
    }

    // 處理深層巢狀結構 value.key.value.value (部門key的值)
    if (selectorValue.key && selectorValue.key.value && selectorValue.key.value.value) {
      return selectorValue.key.value.value;
    }

    // 處理深層巢狀結構 value.key.name (部門key的名稱)
    if (selectorValue.key && selectorValue.key.name) {
      return selectorValue.key.name;
    }

    // 原有邏輯：提取 value.key.value
    if (selectorValue.key && selectorValue.key.value) {
      return selectorValue.key.value;
    }

    // 最終備用方案
    return '';
  }

  /**
   * trackBy function for tags ngFor
   * @param index
   * @param tag
   * @returns
   */
  trackByTag(index: number, tag: string): string {
    return tag;
  }

  /**
   * 檢查值是否為非空字串
   * @param value 要檢查的值
   * @returns 是否為非空字串
   */
  isNonEmptyString(value: any): boolean {
    return value && typeof value === 'string' && value.trim() !== '';
  }

  /**
   * 檢查位置資訊是否有效（物件格式）
   * @param locationValue 位置資訊值
   * @returns 是否有有效的位置資訊
   */
  hasValidLocationData(locationValue: any): boolean {
    return locationValue &&
           typeof locationValue === 'object' &&
           locationValue.originalValue &&
           locationValue.originalValue.trim() !== '';
  }

  /**
   * 檢查簽名資料是否有效
   */
  hasValidSignatureData(value: any): boolean {
    if (!value) return false;

    // 檢查是否為物件且有必要的屬性
    if (typeof value === 'object') {
      // 檢查是否有簽名圖片資料
      return this.getSignatureImageData(value) !== null;
    }

    return false;
  }

  /**
   * 檢查會議簽名收集資料是否有效
   */
  hasValidMeetingSignatureData(value: any): boolean {
    if (!value) return false;

    // 檢查是否為物件且有參與者資料
    if (typeof value === 'object') {
      try {
        const data = typeof value === 'string' ? JSON.parse(value) : value;
        const participants = data.participants || [];
        return participants.length > 0;
      } catch (error) {
        return false;
      }
    }

    return false;
  }

  /**
   * 取得簽名摘要文字
   * @param columnData 簽名欄位資料
   * @returns 簽名摘要文字
   */
  getSignatureSummary(columnData: any): string {
    if (!columnData || !columnData.value) {
      return '';
    }

    const signatureValue = columnData.value;

    // 優先取得 summary
    if (signatureValue.summary) {
      return signatureValue.summary;
    }

    // 根據 hasSignature 判斷
    if (signatureValue.hasSignature === true) {
      return this.i18nService.translate('form.approval.signed');
    } else if (signatureValue.hasSignature === false) {
      return this.i18nService.translate('form.approval.unsigned');
    }

    // 如果有圖片資料則認為已簽名
    if (this.getSignatureImageData(signatureValue)) {
      return this.i18nService.translate('form.approval.signed');
    }

    return this.i18nService.translate('form.approval.unsigned');
  }

  /**
   * 取得簽名時間
   * @param columnData 簽名欄位資料
   * @returns 格式化的簽名時間
   */
  getSignatureDate(columnData: any): string | null {
    if (!columnData || !columnData.value) {
      return null;
    }

    const signatureValue = columnData.value;

    // 處理巢狀格式中的時間
    if (signatureValue.originalValue && signatureValue.originalValue.signedAt) {
      return this.formatSignedDate(signatureValue.originalValue.signedAt);
    }

    // 處理直接格式中的時間
    if (signatureValue.signedAt) {
      return this.formatSignedDate(signatureValue.signedAt);
    }

    // 處理 signerInfo 中的時間
    if (signatureValue.signerInfo && signatureValue.signerInfo.signedAt) {
      return this.formatSignedDate(signatureValue.signerInfo.signedAt);
    }

    return null;
  }

  /**
   * 取得簽名欄位提示文字
   * @param columnData 簽名欄位資料
   * @returns 提示文字
   */
  getSignatureColumnTooltip(columnData: any): string {
    const summary = this.getSignatureSummary(columnData);
    const date = this.getSignatureDate(columnData);

    if (date) {
      return `${summary} - ${date}`;
    }

    return summary;
  }

  /**
   * 開啟 dynamic-api-table 資料顯示對話框
   * @param value dynamic-api-table 的值
   * @param columnName 欄位名稱
   */
  openDynamicApiTableDialog(value: any, columnName: string): void {
    if (!value || !Array.isArray(value) || value.length === 0) {
      return;
    }



    this.dynamicApiTableDialogTitle = `${columnName} - 資料檢視`;
    this.dynamicApiTableData = value;

    // 從第一筆資料取得欄位名稱
    if (value.length > 0) {
      this.dynamicApiTableColumns = Object.keys(value[0]);
    } else {
      this.dynamicApiTableColumns = [];
    }

    this.showDynamicApiTableDialog = true;
  }

  /**
   * 關閉 dynamic-api-table 資料顯示對話框
   */
  closeDynamicApiTableDialog(): void {
    this.showDynamicApiTableDialog = false;
    this.dynamicApiTableDialogTitle = '';
    this.dynamicApiTableData = [];
    this.dynamicApiTableColumns = [];
  }

  /**
   * 取得表格儲存格的顯示值（處理物件型態）
   */
  getDisplayValueForTableCell(cellValue: any): string {
    if (cellValue === null || cellValue === undefined) {
      return '';
    }

    // 如果是物件且有 value 屬性，顯示 value
    if (typeof cellValue === 'object' && cellValue.value !== undefined) {
      return cellValue.value;
    }

    // 如果是物件且有 key 和 value 屬性，顯示 value
    if (typeof cellValue === 'object' && cellValue.key && cellValue.value !== undefined) {
      return cellValue.value;
    }

    // 如果是物件但沒有 value 屬性，嘗試轉為 JSON 字串
    if (typeof cellValue === 'object') {
      try {
        return JSON.stringify(cellValue);
      } catch {
        return '[Object]';
      }
    }

    // 其他情況直接轉字串
    return String(cellValue);
  }

  /**
   * 取得會議簽名收集統計資訊
   */
  getMeetingSignatureCount(value: any): string {
    if (!value) return '0/0';

    try {
      const data = typeof value === 'string' ? JSON.parse(value) : value;
      const participants = data.participants || [];
      const signedCount = participants.filter((p: any) =>
        this.getSignatureImageData(p.signature) !== null
      ).length;
      return `${signedCount}/${participants.length}`;
    } catch (error) {
      console.error(this.i18nService.translate('showlist.console.parse_meeting_signature_failed'), error);
      return '0/0';
    }
  }

  /**
   * 開啟會議簽名詳細資訊對話框
   */
  openMeetingSignatureDialog(value: any, title: string): void {
    try {
      console.log(this.i18nService.translate('showlist.console.open_meeting_signature_dialog'), value);
      const data = typeof value === 'string' ? JSON.parse(value) : value;
      const participants = data.participants || [];

      console.log(this.i18nService.translate('showlist.console.parsed_participant_data'), participants);

      // 檢查每個參與者的簽名資料
      participants.forEach((p: any, index: number) => {
        console.log(this.i18nService.translate('showlist.console.participant_info', {index: index + 1, name: p.name}) + ':', {
          signature: p.signature,
          signatureType: typeof p.signature,
          hasSignature: !!(p.signature && p.signature !== null && p.signature !== '')
        });
      });

      // 計算已簽名人數
      const signedCount = participants.filter((p: any) =>
        this.getSignatureImageData(p.signature) !== null
      ).length;

      this.meetingSignatureData = {
        participants: participants,
        signedCount: signedCount,
        totalCount: participants.length
      };
      this.meetingSignatureDialogTitle = `${title} - ${this.i18nService.translate('meeting.signature.detailed_info')}`;

      console.log(this.i18nService.translate('showlist.console.final_signature_data'), this.meetingSignatureData);
    } catch (error) {
      console.error(this.i18nService.translate('showlist.console.parse_meeting_signature_failed'), error);
      console.error(this.i18nService.translate('showlist.console.raw_data'), value);
      this.meetingSignatureData = {
        participants: [],
        signedCount: 0,
        totalCount: 0
      };
      this.meetingSignatureDialogTitle = `${title} - ${this.i18nService.translate('meeting.signature.detailed_info')}`;
    }

    this.showMeetingSignatureDialog = true;
  }

  /**
   * 圖片載入錯誤處理
   */
  onImageError(signature: string): void {
    console.log(this.i18nService.translate('showlist.console.image_load_failed'), signature);
  }

  /**
   * 圖片載入成功處理
   */
  onImageLoad(name: string): void {
    console.log(this.i18nService.translate('showlist.console.image_load_success'), name);
  }

  /**
   * 取得簽名圖片資料
   */
  getSignatureImageData(signature: any): string | null {
    if (!signature) return null;

    // 處理巢狀格式：originalValue.imageData
    if (signature.originalValue && signature.originalValue.imageData) {
      return signature.originalValue.imageData;
    }

    // 如果是字串格式的 base64 資料（舊格式）
    if (typeof signature === 'string' && signature.startsWith('data:image/')) {
      return signature;
    }

    // 如果是物件格式包含 imageData（中等格式）
    if (typeof signature === 'object' && signature.imageData) {
      return signature.imageData;
    }

    return null;
  }

  /**
   * 關閉會議簽名詳細資訊對話框
   */
  closeMeetingSignatureDialog(): void {
    this.showMeetingSignatureDialog = false;
    this.meetingSignatureDialogTitle = '';
    this.meetingSignatureData = {
      participants: [],
      signedCount: 0,
      totalCount: 0
    };
  }

  /**
   * 判斷是否為日期欄位
   */
  isDateColumn(column: string): boolean {
    // 定義日期欄位名稱模式
    const dateColumns = [
      'updated_date',
      'created_date',
      'updatedat',
      'createdat',
      'updated_at',
      'created_at',
      'timestamp',
      'date',
      'time',
      '_ts'  // Cosmos DB 時間戳記欄位
    ];

    const lowerColumn = column.toLowerCase();
    return dateColumns.some(dateCol =>
      lowerColumn === dateCol.toLowerCase() ||
      lowerColumn.includes(dateCol) ||
      lowerColumn.endsWith('_date') ||
      lowerColumn.endsWith('_time') ||
      lowerColumn.endsWith('date') ||
      lowerColumn.endsWith('time') ||
      lowerColumn.startsWith('date') ||
      lowerColumn.startsWith('time')
    );
  }

  /**
   * 格式化時間戳記顯示 - 直接處理毫秒級時間戳記
   */
  formatTimestamp(timestamp: number): string {
    if (!timestamp || isNaN(timestamp)) {
      return '';
    }

    try {
      const date = new Date(timestamp);

      if (isNaN(date.getTime())) {
        return timestamp ? timestamp.toString() : '';
      }

      // 格式化為 yyyy-mm-dd HH:mm:ss
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error(this.i18nService.translate('showlist.console.timestamp_format_error'), error);
      return timestamp ? timestamp.toString() : '';
    }
  }

  /**
   * 格式化日期顯示 - 統一使用 yyyy-mm-dd hh:mm:ss 格式
   */
  formatDate(date: string | Date | number): string {
    if (!date) return '';

    try {
      let dateObj: Date;

      // 處理數字類型（Unix timestamp）
      if (typeof date === 'number') {
        // Cosmos DB 的 _ts 是秒級時間戳記
        // 如果數字小於 10000000000，視為秒級時間戳記
        if (date < 10000000000) {
          dateObj = new Date(date * 1000);
        } else {
          // 否則視為毫秒級時間戳記
          dateObj = new Date(date);
        }
      }
      // 如果是字符串，先檢查是否看起來像是 GUID 或其他非日期格式
      else if (typeof date === 'string') {
        // 檢查是否為數字字符串（可能是時間戳記）
        if (/^\d+$/.test(date)) {
          const timestamp = parseInt(date, 10);
          if (timestamp < 10000000000) {
            dateObj = new Date(timestamp * 1000);
          } else {
            dateObj = new Date(timestamp);
          }
        }
        // 檢查是否為 GUID 格式 (8-4-4-4-12 字符)
        else if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(date)) {
          console.warn(this.i18nService.translate('showlist.console.received_guid_not_date'), date);
          return '';
        }
        // 檢查字符串是否只包含非日期字符（避免誤判）
        else if (date.length > 50 || !/\d/.test(date)) {
          console.warn(this.i18nService.translate('showlist.console.string_not_date_format'), date);
          return '';
        }
        else {
          dateObj = new Date(date);
        }
      }
      else {
        dateObj = date as Date;
      }

      // 檢查日期是否有效 - 加強檢查邏輯
      if (!dateObj || dateObj === null || dateObj === undefined) {
        console.warn(this.i18nService.translate('showlist.console.date_empty_undefined'), date);
        return '';
      }

      // 確保dateObj是Date物件且有getTime方法
      if (!(dateObj instanceof Date) || typeof dateObj.getTime !== 'function') {
        console.warn(this.i18nService.translate('showlist.console.invalid_date_object_type'), typeof dateObj, dateObj);
        return '';
      }

      // 檢查日期是否有效
      if (isNaN(dateObj.getTime())) {
        console.warn(this.i18nService.translate('showlist.console.invalid_date_value'), date);
        return '';
      }

      // 統一使用 yyyy-mm-dd hh:mm:ss 格式
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const hours = String(dateObj.getHours()).padStart(2, '0');
      const minutes = String(dateObj.getMinutes()).padStart(2, '0');
      const seconds = String(dateObj.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error(this.i18nService.translate('showlist.console.format_date_failed'), error);
      return String(date);
    }
  }

  /**
   * 格式化日期範圍顯示
   */
  formatDateRange(dateRange: any): string {
    if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
      return '';
    }

    try {
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);

      // 檢查是否包含時間 (如果字串包含 'T' 表示有時間)
      const hasTime = dateRange.startDate.includes('T') || dateRange.endDate.includes('T');

      if (hasTime) {
        // 如果包含時間，格式化為 yyyy-mm-dd HH:MM
        const startFormatted = this.formatDateToYMD(startDate, true);
        const endFormatted = this.formatDateToYMD(endDate, true);
        return `${startFormatted} ~ ${endFormatted}`;
      } else {
        // 如果只有日期，格式化為 yyyy-mm-dd
        const startFormatted = this.formatDateToYMD(startDate, false);
        const endFormatted = this.formatDateToYMD(endDate, false);
        return `${startFormatted} ~ ${endFormatted}`;
      }
    } catch (error) {
      console.error(this.i18nService.translate('showlist.console.format_date_range_failed'), error);
      return `${dateRange.startDate} ~ ${dateRange.endDate}`;
    }
  }

  /**
   * 格式化日期為 yyyy-mm-dd 或 yyyy-mm-dd HH:MM 格式
   */
  private formatDateToYMD(date: Date, includeTime: boolean = false): string {
    // 加強日期驗證邏輯
    if (!date || date === null || date === undefined) {
      console.warn('formatDateToYMD: ' + this.i18nService.translate('showlist.console.date_empty_or_undefined'), date);
      return '';
    }

    // 確保是Date物件且有getTime方法
    if (!(date instanceof Date) || typeof date.getTime !== 'function') {
      console.warn('formatDateToYMD: ' + this.i18nService.translate('showlist.console.invalid_date_object_type'), typeof date, date);
      return '';
    }

    // 檢查日期是否有效
    if (isNaN(date.getTime())) {
      console.warn('formatDateToYMD: ' + this.i18nService.translate('showlist.console.invalid_date_value'), date);
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    if (includeTime) {
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } else {
      return `${year}-${month}-${day}`;
    }
  }

  /**
   * 產生簽名的 tooltip 文字
   */
  getSignatureTooltip(signatureValue: any): string {
    if (!signatureValue || !signatureValue.signerInfo) {
      return '簽名';
    }

    const signerInfo = signatureValue.signerInfo;
    let tooltip = `簽名者: ${signerInfo.username || '未知'}`;

    if (signerInfo.signedAt) {
      const signedDate = this.formatSignedDate(signerInfo.signedAt);
      tooltip += `\n簽名時間: ${signedDate}`;
    }

    if (signerInfo.companyId) {
      tooltip += `\n公司: ${signerInfo.companyId}`;
    }

    return tooltip;
  }

  /**
   * 格式化簽名日期
   */
  formatSignedDate(dateString: string): string {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      return this.formatDateToYMD(date, true);
    } catch (error) {
      console.error('Error formatting signed date:', error);
      return dateString;
    }
  }

  /**
   * 查看表單項目詳情
   */
  async viewItem(item: any): Promise<void> {
    console.log(this.i18nService.translate('showlist.console.view_item'), item);

    try {
      // 設定基本資訊
      this.viewFormName = this.formName;
      this.viewFormData = item;

      // 檢查是否已有原始表單配置
      if (this.originalFormConfig && this.originalFormConfig.length > 0) {
        this.viewFormConfig = this.originalFormConfig;
      } else {
        // 如果沒有，嘗試載入表單配置
        await this.loadFormConfigForView();
      }

      // 顯示查看視窗
      this.showViewForm = true;

    } catch (error) {
      console.error(this.i18nService.translate('showlist.console.load_form_details_failed'), error);
      alert(this.i18nService.translate('showlist.load_form_details_failed'));
    }
  }

  /**
   * 載入表單配置用於查看
   */
  private async loadFormConfigForView(): Promise<void> {
    if (!this.formId) {
      throw new Error('缺少表單ID');
    }

    const apiUrl = `${environment.apiUrl}/api/form-manager/get_specific_form_config`;
    const params = {
      formid: this.formId
    };

    try {
      const response = await this.http.get<any>(apiUrl, { params }).toPromise();
      if (response?.success && response?.data?.config) {
        this.viewFormConfig = response.data.config;
      } else {
        throw new Error('無法取得表單配置');
      }
    } catch (error) {
      console.error(this.i18nService.translate('showlist.console.load_form_config_failed'), error);
      // 如果無法載入配置，使用預設的簡單配置
      this.viewFormConfig = [];
    }
  }

  /**
   * 關閉表單查看視窗
   */
  closeViewForm(): void {
    console.log('準備關閉查看視窗');

    // 在關閉視窗前，檢查 form-display 是否有簽核完成
    if (this.viewFormDisplayComponent) {
      try {
        const approvalStatus = this.viewFormDisplayComponent.getApprovalStatus();

        if (approvalStatus.hasCompleted) {
          console.log('檢測到簽核已完成，觸發重新整理');

          // 重置 form-display 的簽核狀態
          this.viewFormDisplayComponent.resetApprovalStatus();

          // 直接調用重新整理方法（會保持當前頁碼和篩選條件）
          this.refreshData();
        } else {
          console.log('未檢測到簽核完成，不更新列表');
        }
      } catch (error) {
        console.error('檢查簽核狀態時發生錯誤:', error);
      }
    }

    // 關閉視窗
    this.showViewForm = false;
    this.viewFormConfig = [];
    this.viewFormData = {};
    this.viewFormName = '';

    console.log('查看視窗已關閉');
  }


  /**
   * 取得風險等級對應的 CSS 類別名稱
   * @param riskLevel 風險等級文字
   * @returns CSS 類別名稱
   */
  getRiskLevelClass(riskLevel: string): string {
    if (!riskLevel) {
      return 'unknown';
    }

    // 將風險等級轉換為對應的 CSS 類別
    switch (riskLevel) {
      case '很低':
      case '極低':
        return 'very-low';
      case '低':
        return 'low';
      case '中':
      case '中等':
        return 'medium';
      case '高':
        return 'high';
      case '很高':
      case '極高':
        return 'very-high';
      default:
        return 'unknown';
    }
  }

  // === 分頁控制相關方法 ===

  /**
   * 更改每頁資料筆數
   */
  onPageSizeChange(newPageSize: number): void {
    this.pageSize = newPageSize;
    this.currentPage = 1; // 重置到第一頁
  }

  /**
   * 前往指定頁面
   */
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  /**
   * 前往上一頁
   */
  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  /**
   * 前往下一頁
   */
  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  /**
   * 前往第一頁
   */
  firstPage(): void {
    this.currentPage = 1;
  }

  /**
   * 前往最後一頁
   */
  lastPage(): void {
    this.currentPage = this.totalPages;
  }

  /**
   * 取得分頁按鈕數組
   */
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;

    if (this.totalPages <= maxPagesToShow) {
      // 如果總頁數少於或等於最大顯示頁數，顯示所有頁碼
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 否則根據當前頁碼決定顯示範圍
      let startPage = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2));
      let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);

      // 調整開始頁碼
      if (endPage - startPage + 1 < maxPagesToShow) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  }

  /**
   * 取得當前顯示資料的範圍資訊
   */
  getPaginationInfo(): string {
    if (this.totalItems === 0) {
      return this.i18nService.translate('pagination.no_data');
    }

    const startItem = (this.currentPage - 1) * this.pageSize + 1;
    const endItem = Math.min(this.currentPage * this.pageSize, this.totalItems);

    return this.i18nService.translate('pagination.showing_range', {
      start: startItem,
      end: endItem,
      total: this.totalItems
    });
  }

  // ============================================
  // 上傳檔案顯示相關方法 (RadioButton uploadedFiles)
  // ============================================

  /**
   * 判斷檔案是否為圖片類型
   */
  isImageFile(fileName: string): boolean {
    if (!fileName) return false;

    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  }

  /**
   * 開啟圖片預覽模態框
   */
  openImageModal(fileUrl: string, fileName: string): void {
    console.log(this.i18nService.translate('showlist.console.open_image_preview'), fileName, fileUrl);

    // 可以在這裡實作圖片預覽功能
    // 例如使用 Bootstrap Modal 或其他圖片預覽庫
    window.open(fileUrl, '_blank');
  }

  /**
   * 下載檔案
   */
  downloadFile(fileUrl: string, fileName: string): void {
    console.log(this.i18nService.translate('showlist.console.download_file'), fileName, fileUrl);

    try {
      // 建立隱藏的下載連結
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName;
      link.style.display = 'none';

      // 添加到 DOM，點擊下載，然後移除
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(this.i18nService.translate('showlist.console.file_download_triggered'), fileName);
    } catch (error) {
      console.error(this.i18nService.translate('showlist.console.file_download_failed'), error);
      alert(this.i18nService.translate('showlist.file_download_failed'));
    }
  }


  //TODO:整個寄信的功能 不應該放在showlist, 應該要放在原本的元件中

  /**
   * 取得承攬商聯絡人信箱
   */
  private async getContractorContactEmail(companyKey: string): Promise<any> {
    try {
      console.log('📞 ' + this.i18nService.translate('showlist.console.call_contact_info_api') + ':', companyKey);

      const url = `${environment.apiUrl}/api/contractor/contact-info`;
      const params = {
        companyid: environment.companyid,
        id: companyKey
      };

      const response = await this.http.get<any>(url, { params }).toPromise();

      console.log('📞 ' + this.i18nService.translate('showlist.console.contact_info_api_response') + ':', response);

      if (response && response.success && response.data) {
        return {
          type: 'contractor_contact',
          user_id: response.data.user_id,
          email: response.data.email,
          fullName: response.data.fullName,
          source: '承攬商聯絡人'
        };
      } else {
        console.warn('⚠️ ' + this.i18nService.translate('showlist.console.contact_info_api_abnormal') + ':', response);
        return null;
      }
    } catch (error) {
      console.error('❌ ' + this.i18nService.translate('showlist.console.call_contact_info_api_failed') + ':', error);
      return null;
    }
  }

  /**
   * 取得工作人員詳細資料
   */
  private async getPersonnelDetails(workerNames: string[]): Promise<any> {
    try {
      console.log('👥 ' + this.i18nService.translate('showlist.console.call_personnel_details_api') + ':', workerNames);

      if (!workerNames || workerNames.length === 0) {
        console.log('⚠️ ' + this.i18nService.translate('showlist.console.no_worker_names_skip'));
        return [];
      }

      const url = `${environment.apiUrl}/api/contractor/personnel-details`;
      const body = {
        companyid: environment.companyid,
        names: workerNames
      };

      const response = await this.http.post<any>(url, body).toPromise();

      console.log('👥 ' + this.i18nService.translate('showlist.console.personnel_details_api_response') + ':', response);

      if (response && response.success && response.data) {
        return response.data.map((person: any) => ({
          type: 'worker_detail',
          personalData: person,
          source: '工作人員資料'
        }));
      } else {
        console.warn('⚠️ ' + this.i18nService.translate('showlist.console.personnel_details_api_abnormal') + ':', response);
        return [];
      }
    } catch (error) {
      console.error('❌ ' + this.i18nService.translate('showlist.console.call_personnel_details_api_failed') + ':', error);
      return [];
    }
  }

  /**
   * 整合所有信箱資料 (分類為 TO 和 CC)
   */
  private consolidateEmailData(contractorEmail: any, personnelDetails: any[]): any {
    const emailData = {
      toRecipients: [] as any[],   // 主要收件人 (承攬商公司聯絡人)
      ccRecipients: [] as any[]    // 副本收件人 (工作人員)
    };

    console.log('📧 ===== ' + this.i18nService.translate('showlist.console.consolidate_email_data') + ' =====');

    // 處理承攬商聯絡人信箱 (TO)
    if (contractorEmail && contractorEmail.email) {
      emailData.toRecipients.push({
        ...contractorEmail,
        recipientType: 'primary',
        mailType: 'TO'
      });
      console.log('✅ ' + this.i18nService.translate('showlist.console.add_primary_recipient') + ':', contractorEmail.email);
    } else {
      console.log('⚠️ ' + this.i18nService.translate('showlist.console.no_contractor_contact_email'));
    }

    // 處理工作人員資料 (CC)
    if (personnelDetails && personnelDetails.length > 0) {
      personnelDetails.forEach((detail, index) => {
        console.log(`👤 ${this.i18nService.translate('showlist.console.process_worker_data')} ${index + 1} ${this.i18nService.translate('showlist.console.data')}:`, detail);

        // 檢查工作人員信箱 (從 text_input_4.value 取得)
        const personalData = detail.personalData;
        if (personalData) {
          let workerEmail = '';
          let workerName = '';
          let workerPhone = '';

          // 取得信箱 (從 text_input_4.value)
          if (personalData.text_input_4 && personalData.text_input_4.value) {
            workerEmail = personalData.text_input_4.value;
          }

          // 取得姓名 (從 text_input_1.value)
          if (personalData.text_input_1 && personalData.text_input_1.value) {
            workerName = personalData.text_input_1.value;
          }

          // 取得電話 (從 text_input_2.value)
          if (personalData.text_input_2 && personalData.text_input_2.value) {
            workerPhone = personalData.text_input_2.value;
          }

          if (workerEmail) {
            emailData.ccRecipients.push({
              type: 'worker_email',
              user_id: personalData.id || `worker_${index}`,
              email: workerEmail,
              fullName: workerName || '未知姓名',
              phone: workerPhone || '',
              source: `工作人員 - ${workerName || '未知'}`,
              recipientType: 'worker',
              mailType: 'CC'
            });
            console.log('✅ ' + this.i18nService.translate('showlist.console.add_cc_recipient') + ':', workerEmail, this.i18nService.translate('showlist.console.name') + ':', workerName);
          } else {
            console.log('⚠️ ' + this.i18nService.translate('showlist.console.no_worker_email'));

            // 列出所有可用欄位，看看信箱可能在哪裡
            console.log('🔍 ' + this.i18nService.translate('showlist.console.available_fields') + ':', Object.keys(personalData));
            // 檢查常見的信箱欄位
            ['text_input_4', 'email', 'mail', 'contact'].forEach(field => {
              if (personalData[field]) {
                console.log(`📧 ${this.i18nService.translate('showlist.console.found_field')} ${field}:`, personalData[field]);
              }
            });
          }
        }
      });
    } else {
      console.log('⚠️ ' + this.i18nService.translate('showlist.console.no_worker_details_cc'));
    }

    console.log('📧 ===== ' + this.i18nService.translate('showlist.console.email_classification_result') + ' =====');
    console.log('📩 ' + this.i18nService.translate('showlist.console.main_recipients_to', {count: emailData.toRecipients.length}));
    emailData.toRecipients.forEach((email, index) => {
      console.log(`  TO ${index + 1}. ${email.source}: ${email.email}`);
    });

    console.log('📄 ' + this.i18nService.translate('showlist.console.cc_recipients_count', {count: emailData.ccRecipients.length}));
    emailData.ccRecipients.forEach((email, index) => {
      console.log(`  CC ${index + 1}. ${email.source}: ${email.email}`);
    });

    return emailData;
  }

  /**
   * 發送通知信件 (每個工作人員收到獨立信件，都 CC 給承攬商負責人)
   */
  private async sendNotificationEmails(constructionInfo: any, emailData: any): Promise<void> {
    console.log('📤 ===== ' + this.i18nService.translate('showlist.console.prepare_send_notification_emails') + ' =====');
    console.log('📋 ' + this.i18nService.translate('showlist.console.construction_info'), constructionInfo);
    console.log('📧 ' + this.i18nService.translate('showlist.console.recipient_data'), emailData);

    const { toRecipients, ccRecipients } = emailData;

    // 取得承攬商負責人信箱作為 CC
    const contractorEmails = toRecipients.map((r: any) => r.email).filter((e: any) => e);
    const workerEmails = ccRecipients.filter((r: any) => r.email); // 保留完整的工作人員資料物件

    // 根據設定決定是直接發送還是顯示確認彈窗
    if (this.enableEmailConfirmation) {
      alert(this.i18nService.translate('showlist.alert.confirm_popup'));
      // 顯示確認彈窗
      await this.showEmailConfirmation(constructionInfo, contractorEmails, workerEmails);
      return; // 提前返回，因為實際發送會在確認後進行
    }
    else{
      alert(this.i18nService.translate('showlist.alert.send_email_directly'));
    }

    // 直接發送郵件（原本的邏輯）

    console.log('📧 ===== ' + this.i18nService.translate('showlist.console.email_send_strategy') + ' =====');
    console.log('🏢 ' + this.i18nService.translate('showlist.console.contractor_manager_emails_cc'), contractorEmails);
    console.log('👷 ' + this.i18nService.translate('showlist.console.worker_count'), workerEmails.length);

    // 檢查是否有工作人員信箱
    if (!workerEmails || workerEmails.length === 0) {
      console.log('⚠️ ' + this.i18nService.translate('showlist.console.no_worker_email_send_contractor'));

      if (contractorEmails.length > 0) {
        // 只有承攬商信箱時，發送一封給承攬商的通知
        this.sendSingleEmailToContractor(constructionInfo, contractorEmails);
      } else {
        console.log('❌ ' + this.i18nService.translate('showlist.console.no_worker_no_contractor_email'));
        alert(this.i18nService.translate('showlist.alert.no_recipients_found'));
      }
      return;
    }

    // 為每個工作人員發送獨立的個人化信件 (包含 QR Code)
    const emailPromises: any[] = [];

    console.log('📤 ===== ' + this.i18nService.translate('showlist.console.start_send_personalized_emails_qr') + ' =====');

    // 序列處理每個工作人員，因為需要 async 生成 QR Code
    for (let index = 0; index < workerEmails.length; index++) {
      const worker = workerEmails[index];
      console.log('📧 ' + this.i18nService.translate('showlist.console.prepare_send_to_worker', {index: index + 1, name: worker.fullName, email: worker.email}));

      try {
        // 生成包含 QR Code 的個人化郵件內容
        const htmlContent = await this.generatePersonalizedEmailHTML(constructionInfo, worker);

        // 為每個工作人員建立個人化的郵件資料
        const personalizedEmailPayload = {
          to: [worker.email],              // 工作人員作為主要收件人
          cc: contractorEmails,            // 承攬商負責人作為副本收件人
          subject: `個人施工通知 - ${worker.fullName} - ${constructionInfo.contractorCompany.companyName}`,
          htmlContent: htmlContent,        // 包含 QR Code 的 HTML 內容
          attachments: [],
          metadata: {
            formId: constructionInfo.formId,
            companyName: constructionInfo.contractorCompany.companyName,
            workerName: worker.fullName,
            workerEmail: worker.email,
            workPeriod: constructionInfo.workPeriod,
            emailType: 'personalized_worker_notification'
          }
        };

        emailPromises.push(personalizedEmailPayload);

        console.log('✅ ' + this.i18nService.translate('showlist.console.personalized_email_qr_ready', {index: index + 1}), {
          to: personalizedEmailPayload.to,
          cc: personalizedEmailPayload.cc,
          subject: personalizedEmailPayload.subject,
          hasQRCode: htmlContent.includes('進場掃描 QR Code')
        });
      } catch (error) {
        console.error('❌ ' + this.i18nService.translate('showlist.console.worker_email_generation_failed', {name: worker.fullName}), error);
        // 即使某個工作人員的郵件生成失敗，也繼續處理其他人
      }
    }

    // 顯示發送摘要
    const totalEmails = emailPromises.length;
    const totalRecipients = workerEmails.length + (contractorEmails.length * totalEmails); // 每封信都會 CC 給承攬商

    let message = `🏗️ 個人化施工通知已準備發送！\n\n`;
    message += `📋 施工基本資訊:\n`;
    message += `  - 承攬商公司: ${constructionInfo.contractorCompany.companyName}\n`;
    message += `  - 施工期間: ${constructionInfo.workPeriod.startDate} 到 ${constructionInfo.workPeriod.endDate}\n\n`;

    message += `📧 個人化郵件策略 (含 QR Code):\n`;
    message += `  - 將發送 ${totalEmails} 封獨立的個人化信件\n`;
    message += `  - 每封信的主要收件人: 個別工作人員\n`;
    message += `  - 每封信都會 CC 給承攬商負責人\n`;
    message += `  - 每封信都包含該工作人員專屬的進場 QR Code\n\n`;

    message += `🔳 QR Code 功能:\n`;
    message += `  - 每個工作人員都有唯一的進場 QR Code\n`;
    message += `  - 進場時掃描 QR Code 確認入場資格\n`;
    message += `  - 暫時使用 www.google.com 測試連結\n\n`;

    message += `👷 工作人員收件清單:\n`;
    workerEmails.forEach((worker: any, index: number) => {
      message += `  ${index + 1}. ${worker.fullName}: ${worker.email} 🔳\n`;
    });

    if (contractorEmails.length > 0) {
      message += `\n🏢 承攬商負責人 (每封信的 CC):\n`;
      contractorEmails.forEach((email: string, index: number) => {
        message += `  ${index + 1}. ${email}\n`;
      });
    }

    message += `\n📊 郵件發送統計:\n`;
    message += `  - 總信件數: ${totalEmails} 封\n`;
    message += `  - QR Code 生成數: ${totalEmails} 個\n`;
    message += `  - 預估總收件次數: ${totalRecipients} 次\n`;

    alert(message);

    console.log('📤 ===== ' + this.i18nService.translate('showlist.console.all_personalized_email_data_structure') + ' =====');
    emailPromises.forEach((emailPayload, index) => {
      console.log('📧 ' + this.i18nService.translate('showlist.console.personalized_email_index', {index: index + 1}), emailPayload);
    });

    // 觸發施工通知發送事件給父元件
    this.constructionNotificationSent.emit({
      success: true,
      emailsSent: totalEmails,
      totalRecipients: totalRecipients,
      emailType: 'personalized_notifications',
      personalizedEmails: emailPromises,
      contractorEmails: contractorEmails,
      workerEmails: workerEmails,
      constructionInfo: constructionInfo
    });

    console.log('📤 ' + this.i18nService.translate('showlist.console.personalized_construction_notification_complete'));
  }

  /**
   * 當只有承攬商信箱時，發送給承攬商的通知
   */
  private sendSingleEmailToContractor(constructionInfo: any, contractorEmails: string[]): void {
    console.log('📧 ===== ' + this.i18nService.translate('showlist.console.send_contractor_overall_notification') + ' =====');

    const contractorEmailPayload = {
      to: contractorEmails,
      cc: [],
      subject: `施工通知 - ${constructionInfo.contractorCompany.companyName}`,
      htmlContent: this.generateContractorEmailHTML(constructionInfo),
      attachments: [],
      metadata: {
        formId: constructionInfo.formId,
        companyName: constructionInfo.contractorCompany.companyName,
        workersCount: constructionInfo.workers.length,
        workPeriod: constructionInfo.workPeriod,
        emailType: 'contractor_only_notification'
      }
    };

    console.log('📤 ' + this.i18nService.translate('showlist.console.contractor_notification_email'), contractorEmailPayload);

    let message = `🏗️ 施工通知 (僅承攬商)\n\n`;
    message += `📋 因無工作人員信箱，改為發送整體通知給承攬商負責人\n\n`;
    message += `🏢 收件人: ${contractorEmails.join(', ')}\n`;
    message += `📧 信件主旨: ${contractorEmailPayload.subject}`;

    alert(message);

    // 觸發事件
    this.constructionNotificationSent.emit({
      success: true,
      emailsSent: 1,
      totalRecipients: contractorEmails.length,
      emailType: 'contractor_only',
      contractorEmail: contractorEmailPayload,
      constructionInfo: constructionInfo
    });
  }

  /**
   * 為工作人員生成唯一的 QR Code
   */
  private async generateWorkerQRCode(worker: any, constructionInfo: any): Promise<string> {
    try {
      // 生成工作人員唯一識別碼
      const workerId = worker.user_id || worker.email || worker.fullName;
      const timestamp = new Date().getTime();

      // 暫時使用 google.com + 唯一參數
      const qrUrl = `https://www.google.com?worker=${encodeURIComponent(workerId)}&form=${constructionInfo.formId}&company=${encodeURIComponent(constructionInfo.contractorCompany.companyName)}&ts=${timestamp}`;

      console.log('🔳 ' + this.i18nService.translate('showlist.console.generate_worker_qr_code', {name: worker.fullName}), qrUrl);

      // 生成 QR Code 圖片 (Base64 格式)
      const qrCodeDataURL = await QRCode.toDataURL(qrUrl, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      return qrCodeDataURL;
    } catch (error) {
      console.error(this.i18nService.translate('showlist.console.qr_code_generation_failed'), error);
      return ''; // 如果失敗，返回空字串
    }
  }

  /**
   * 生成個人化郵件 HTML 內容
   */
  private async generatePersonalizedEmailHTML(constructionInfo: any, worker: any): Promise<string> {
    // 生成專屬 QR Code
    const qrCodeDataURL = await this.generateWorkerQRCode(worker, constructionInfo);

    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #f5820d;">🏗️ 個人施工通知</h2>

        <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
          <h3 style="color: #484949; margin-top: 0;">👤 工作人員資訊</h3>
          <p><strong>姓名:</strong> ${worker.fullName}</p>
          <p><strong>電話:</strong> ${worker.phone}</p>
          <p><strong>信箱:</strong> ${worker.email}</p>
        </div>

        <div style="background-color: #ffecc5; padding: 15px; border-radius: 5px; margin: 15px 0;">
          <h3 style="color: #484949; margin-top: 0;">🏢 施工基本資訊</h3>
          <p><strong>承攬商公司:</strong> ${constructionInfo.contractorCompany.companyName}</p>
          <p><strong>施工期間:</strong> ${constructionInfo.workPeriod.startDate} 到 ${constructionInfo.workPeriod.endDate}</p>
          <p><strong>工作夥伴總數:</strong> ${constructionInfo.workers.length} 人</p>
          <p><strong>工單編號:</strong> ${constructionInfo.workOrderId}</p>
        </div>

        ${qrCodeDataURL ? `
        <div style="background-color: #e8f5e8; padding: 20px; border-radius: 5px; margin: 20px 0; text-align: center;">
          <h3 style="color: #2e9116; margin-top: 0;">📱 進場掃描 QR Code</h3>
          <p style="margin-bottom: 15px; color: #484949;">
            <strong>請保存此 QR Code，進場時掃描以確認入場資格</strong>
          </p>
          <div style="display: inline-block; padding: 10px; background-color: white; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            <img src="${qrCodeDataURL}" alt="進場專用 QR Code" style="display: block; width: 200px; height: 200px;" />
          </div>
          <p style="margin-top: 10px; font-size: 12px; color: #666;">
            🔳 個人專屬 QR Code - 請勿分享給他人
          </p>
        </div>
        ` : ''}

        <div style="border-left: 4px solid #2e9116; padding-left: 15px; margin: 20px 0;">
          <h3 style="color: #2e9116; margin-top: 0;">⚠️ 重要提醒</h3>
          <ul>
            <li>施工申請已完成最終核准，請按時進場施工</li>
            <li>進場時請出示上方 QR Code 供掃描確認</li>
            <li>請務必遵守現場安全規定</li>
            <li>如有任何問題，請聯繫現場負責人</li>
            <li>本通知已同步發送給承攬商負責人</li>
          </ul>
        </div>

        <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #888; font-size: 12px;">
          <p>此為系統自動發送的通知，請勿直接回覆此信件</p>
          <p>系統網址: ${constructionInfo.systemUrl}</p>
        </div>
      </div>
    `;
  }

  /**
   * 生成承攬商整體通知 HTML 內容
   */
  private generateContractorEmailHTML(constructionInfo: any): string {
    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #f5820d;">🏗️ 施工開始通知</h2>

        <div style="background-color: #ffecc5; padding: 15px; border-radius: 5px; margin: 15px 0;">
          <h3 style="color: #484949; margin-top: 0;">🏢 施工基本資訊</h3>
          <p><strong>承攬商公司:</strong> ${constructionInfo.contractorCompany.companyName}</p>
          <p><strong>施工期間:</strong> ${constructionInfo.workPeriod.startDate} 到 ${constructionInfo.workPeriod.endDate}</p>
          <p><strong>工作人員數:</strong> ${constructionInfo.workers.length} 人</p>
          <p><strong>工單編號:</strong> ${constructionInfo.workOrderId}</p>
        </div>

        <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
          <h3 style="color: #484949; margin-top: 0;">👷 工作人員清單</h3>
          ${constructionInfo.workers.map((worker: any, index: number) => `
            <p style="margin: 5px 0;">
              <strong>${index + 1}. ${worker.name}</strong>
              ${worker.phone ? `(${worker.phone})` : ''}
              ${worker.isSafetyPersonnel ? ' <span style="color: #2e9116;">[工安人員]</span>' : ''}
            </p>
          `).join('')}
        </div>

        <div style="border-left: 4px solid #2e9116; padding-left: 15px; margin: 20px 0;">
          <h3 style="color: #2e9116; margin-top: 0;">📋 通知說明</h3>
          <p>施工申請已完成最終核准，請確保：</p>
          <ul>
            <li>所有工作人員按時進場</li>
            <li>遵守現場安全規定</li>
            <li>完成必要的安全教育</li>
            <li>配戴適當的防護設備</li>
          </ul>
        </div>

        <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #888; font-size: 12px;">
          <p>此為系統自動發送的通知，請勿直接回覆此信件</p>
          <p>系統網址: ${constructionInfo.systemUrl}</p>
        </div>
      </div>
    `;
  }

  // ===== 檔案瀏覽器相關方法 =====

  /**
   * 在新分頁開啟檔案，使用檔案瀏覽器
   */
  private openFileInNewTab(url: string): void {
    const fileExtension = this.getFileExtension(url);
    if (fileExtension === 'pdf' || fileExtension === 'jpg' || fileExtension === 'jpeg' || fileExtension === 'png' || fileExtension === 'gif') {
      this.createViewerWindow(url);
    } else {
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (!newWindow) {
        window.location.href = url;
      }
    }
  }

  /**
   * 取得檔案副檔名
   */
  private getFileExtension(url: string): string {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const lastDotIndex = pathname.lastIndexOf('.');
      return lastDotIndex !== -1 ? pathname.substring(lastDotIndex + 1).toLowerCase() : '';
    } catch (error) {
      console.error(this.i18nService.translate('showlist.console.cannot_parse_file_extension'), error);
      return '';
    }
  }

  /**
   * 建立檔案瀏覽器視窗
   */
  private createViewerWindow(url: string): void {
    const fileExtension = this.getFileExtension(url);
    let windowFeatures = 'width=1000,height=700,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no';

    const viewerWindow = window.open('', '_blank', windowFeatures);
    if (!viewerWindow) {
      console.warn(this.i18nService.translate('showlist.console.cannot_open_browser_window'));
      window.location.href = url;
      return;
    }

    // 根據檔案類型建立不同的檢視器
    if (fileExtension === 'pdf') {
      viewerWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>PDF 檢視器</title>
          <style>
            body, html { margin: 0; padding: 0; height: 100%; overflow: hidden; }
            iframe { width: 100%; height: 100%; border: none; }
          </style>
        </head>
        <body>
          <iframe src="${url}" type="application/pdf"></iframe>
        </body>
        </html>
      `);
    } else if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension)) {
      viewerWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>圖片檢視器</title>
          <style>
            body, html {
              margin: 0;
              padding: 20px;
              background-color: #f5f5f5;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
            }
            img {
              max-width: 100%;
              max-height: 100%;
              object-fit: contain;
              box-shadow: 0 4px 8px rgba(0,0,0,0.1);
            }
          </style>
        </head>
        <body>
          <img src="${url}" alt="圖片檢視" />
        </body>
        </html>
      `);
    }

    viewerWindow.document.close();
  }

  /**
   * 顯示寄信確認彈窗
   */
  private async showEmailConfirmation(constructionInfo: any, contractorEmails: string[], workerEmails: any[]): Promise<void> {
    console.log('📧 ===== ' + this.i18nService.translate('showlist.console.prepare_show_email_confirmation') + ' =====');

    try {
      // 生成郵件預覽資料
      this.emailPreviewData = await this.generateEmailPreviewData(constructionInfo, contractorEmails, workerEmails);
      this.pendingConstructionInfo = constructionInfo;

      // 顯示確認彈窗
      this.showEmailConfirmationModal = true;

      console.log('✅ ' + this.i18nService.translate('showlist.console.email_confirmation_shown'), {
        emailCount: this.emailPreviewData.length,
        constructionInfo: constructionInfo
      });

    } catch (error) {
      console.error('❌ ' + this.i18nService.translate('showlist.console.show_email_confirmation_error'), error);
      alert(this.i18nService.translate('showlist.alert.prepare_email_error') + ': ' + (error as Error).message);
    }
  }

  /**
   * 生成郵件預覽資料
   */
  private async generateEmailPreviewData(constructionInfo: any, contractorEmails: string[], workerEmails: any[]): Promise<EmailPreviewData[]> {
    const emailPreviewData: EmailPreviewData[] = [];

    console.log('🔧 ' + this.i18nService.translate('showlist.console.start_generate_email_preview'));

    // 檢查是否有工作人員信箱
    if (!workerEmails || workerEmails.length === 0) {
      console.log('⚠️ ' + this.i18nService.translate('showlist.console.no_worker_email_generate_contractor'));

      if (contractorEmails.length > 0) {
        // 只有承攬商信箱時，生成一封給承攬商的通知
        const contractorEmailData: EmailPreviewData = {
          id: `contractor-email-${Date.now()}`,
          to: contractorEmails,
          cc: [],
          subject: `施工通知 - ${constructionInfo.contractorCompany.companyName}`,
          htmlContent: this.generateContractorEmailHTML(constructionInfo),
          selected: true,
          metadata: {
            formId: constructionInfo.formId,
            companyName: constructionInfo.contractorCompany.companyName,
            workersCount: constructionInfo.workers.length,
            workPeriod: constructionInfo.workPeriod,
            emailType: 'contractor_notification'
          }
        };

        emailPreviewData.push(contractorEmailData);
      }
    } else {
      // 為每個工作人員生成個人化郵件
      console.log('👷 ' + this.i18nService.translate('showlist.console.generate_personalized_emails'));

      for (let index = 0; index < workerEmails.length; index++) {
        const worker = workerEmails[index];
        console.log('📧 ' + this.i18nService.translate('showlist.console.generate_worker_email_index', { index: index + 1, name: worker.fullName }));

        try {
          // 生成包含 QR Code 的個人化郵件內容
          const htmlContent = await this.generatePersonalizedEmailHTML(constructionInfo, worker);

          const personalizedEmailData: EmailPreviewData = {
            id: `worker-email-${Date.now()}-${index}`,
            to: [worker.email],
            cc: contractorEmails, // 承攬商負責人作為副本收件人
            subject: `個人施工通知 - ${worker.fullName} - ${constructionInfo.contractorCompany.companyName}`,
            htmlContent: htmlContent,
            selected: true, // 預設選取
            metadata: {
              formId: constructionInfo.formId,
              companyName: constructionInfo.contractorCompany.companyName,
              workerName: worker.fullName,
              workerEmail: worker.email,
              workPeriod: constructionInfo.workPeriod,
              emailType: 'personalized_worker_notification'
            },
            workerInfo: {
              name: worker.fullName,
              email: worker.email,
              phone: worker.phone || ''
            }
          };

          emailPreviewData.push(personalizedEmailData);

        } catch (error) {
          console.error('❌ ' + this.i18nService.translate('showlist.console.worker_email_generation_error', { workerName: worker.fullName }) + ':', error);
          // 即使某個工作人員的郵件生成失敗，也繼續處理其他人
        }
      }
    }

    console.log('✅ ' + this.i18nService.translate('showlist.console.email_preview_data_complete'), {
      totalEmails: emailPreviewData.length,
      workerEmails: emailPreviewData.filter(e => e.metadata?.emailType === 'personalized_worker_notification').length,
      contractorEmails: emailPreviewData.filter(e => e.metadata?.emailType === 'contractor_notification').length
    });

    return emailPreviewData;
  }

  /**
   * 處理寄信確認結果
   */
  onEmailConfirmationResult(result: EmailConfirmationResult): void {
    console.log('📧 ' + this.i18nService.translate('showlist.console.received_email_confirmation'), result);

    if (result.confirmed && result.action === 'send') {
      // 確認發送，執行實際發送邏輯
      this.executeEmailSending(result.emails);
    } else {
      // 取消發送
      console.log('📧 ' + this.i18nService.translate('showlist.console.user_cancelled_email_send'));

      // 觸發取消事件給父元件
      this.constructionNotificationSent.emit({
        success: false,
        cancelled: true,
        message: '使用者取消發送施工通知'
      });
    }

    // 重設狀態
    this.resetEmailConfirmationState();
  }

  /**
   * 執行實際的郵件發送
   */
  private async executeEmailSending(confirmedEmails: EmailPreviewData[]): Promise<void> {
    console.log('📤 ===== ' + this.i18nService.translate('showlist.console.start_actual_email_send') + ' =====');
    console.log('📧 ' + this.i18nService.translate('showlist.console.confirmed_email_quantity') + ':', confirmedEmails.length);

    try {
      let totalRecipients = 0;
      let successCount = 0;

      // 逐一發送每封郵件
      for (const email of confirmedEmails) {
        console.log('📧 ' + this.i18nService.translate('showlist.console.send_email_subject') + ':', email.subject);

        // 收集收件人
        const toRecipients: string[] = email.to.filter(addr => addr && addr.trim() !== '');
        const ccRecipients: string[] = email.cc.filter(addr => addr && addr.trim() !== '');

        totalRecipients += toRecipients.length + ccRecipients.length;

        // 準備郵件 payload
        const emailPayload = {
          to: toRecipients.length === 1 ? toRecipients[0] : toRecipients,
          cc: ccRecipients.length > 0 ? ccRecipients : undefined,
          subject: email.subject,
          body: email.htmlContent,
          isHtml: true,
          service: environment.emailService || 'JSAI'
        };

        console.log('📧 ' + this.i18nService.translate('showlist.console.send_email_recipients_allocation') + ':', {
          TO收件人: toRecipients,
          CC收件人: ccRecipients,
          主旨: email.subject
        });

        // 呼叫實際的郵件 API
        const response = await firstValueFrom(
          this.http.post(`${environment.apiUrl}/api/email/sendEmail`, emailPayload)
        );

        console.log('✅ ' + this.i18nService.translate('showlist.console.email_send_success') + ':', response);
        successCount++;
      }

      console.log('✅ ' + this.i18nService.translate('showlist.console.all_emails_complete_success', { successCount: successCount, totalCount: confirmedEmails.length }));

      // 顯示成功訊息
      alert('📧 ' + this.i18nService.translate('showlist.alert.construction_notification_success', { emailCount: confirmedEmails.length, recipientCount: totalRecipients }));

      // 觸發成功事件給父元件
      this.constructionNotificationSent.emit({
        success: true,
        emailsSent: confirmedEmails.length,
        totalRecipients: totalRecipients,
        emailType: 'confirmed_notifications',
        confirmedEmails: confirmedEmails,
        constructionInfo: this.pendingConstructionInfo
      });

    } catch (error) {
      console.error('❌ 郵件發送失敗:', error);
      alert(this.i18nService.translate('showlist.alert.email_send_failed') + ': ' + (error as Error).message);

      // 觸發失敗事件給父元件
      this.constructionNotificationSent.emit({
        success: false,
        error: (error as Error).message,
        emailsSent: 0
      });
    }
  }

  /**
   * 重設寄信確認狀態
   */
  private resetEmailConfirmationState(): void {
    this.showEmailConfirmationModal = false;
    this.emailPreviewData = [];
    this.pendingConstructionInfo = null;
  }

  /**
   * 處理寄信確認彈窗關閉
   */
  onEmailConfirmationModalClosed(): void {
    console.log('📧 ' + this.i18nService.translate('showlist.console.email_confirmation_dialog_closed'));
    this.resetEmailConfirmationState();
  }

  // ========================================
  // 🆕 待辦事項提示功能相關方法
  // ========================================

  /**
   * 取得指定項目的待辦事項數量
   */
  getTodoCount(item: any): number {
    if (!this.getTodoInfoCallback) return 0;
    const todoInfo = this.getTodoInfoCallback(item);
    return todoInfo.count;
  }

  /**
   * 取得待辦事項 tooltip
   */
  getTodoTooltip(item: any): string {
    if (!this.getTodoInfoCallback) return '';
    const todoInfo = this.getTodoInfoCallback(item);

    if (todoInfo.count === 0) {
      return '所有必要項目都已完成';
    }

    return `有 ${todoInfo.count} 個待辦事項，點擊查看詳情`;
  }

  /**
   * 處理待辦徽章點擊事件
   */
  onTodoBadgeClick(item: any, event: Event): void {
    event.stopPropagation(); // 防止觸發其他點擊事件

    if (!this.getTodoInfoCallback) return;

    this.currentTodoInfo = this.getTodoInfoCallback(item);
    this.showTodoListModal = true;
  }

  /**
   * 關閉待辦清單彈窗
   */
  closeTodoListModal(): void {
    this.showTodoListModal = false;
    this.currentTodoInfo = undefined;
  }

  /**
   * 顯示表單資訊彈窗
   */
  showFormInfo(): void {
    this.showFormInfoModal = true;
  }

  /**
   * 關閉表單資訊彈窗
   */
  closeFormInfo(): void {
    this.showFormInfoModal = false;
  }

  /**
   * 檢查是否有待辦事項
   * 用於決定簽核流程是否可點擊
   */
  hasTodoItems(item: any): boolean {
    if (!this.getTodoInfoCallback) return false;
    const todoInfo = this.getTodoInfoCallback(item);
    return todoInfo.count > 0;
  }


}


